import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Icon } from '@iconify/react';
import type {
  CampaignMetadata,
  EmailProviderData,
  Interaction,
  ISRContactFields,
  ISRLeadSubstatus,
  ISRNote,
  ISRNotesDocument,
  ISRSalesState,
  ISRSalesStatus,
  ISRUpdateDiff,
  ISRUpdateSnapshot,
  MappedLeadData,
  OutreachStep,
} from '../../types';
import { EmailIntelBadge, SequenceChannelIntel } from '../atoms/ChannelIntel';
import { CountryPhoneSelect } from '../atoms/CountryPhoneSelect';
import { GeoSelect, type GeoOption } from '../atoms/GeoSelect';
import { geoService, type GeoCountry } from '../../services/geo.service';
import { cnaeService, cnaeLabelFor, type CnaeCode } from '../../services/cnae.service';
import { useUserPreferences } from '../../hooks/useUserPreferences';
import { leadDialingCode, hasCountryCode, splitPhonePrefix, validatePhoneForField } from '../../utils/phoneCountryCodes';
import { ISRBriefingTab, ISRGuidanceTab, CampaignContextTab, buildBriefingPublic } from '../Tabs/ISRBriefingTab';
import { ISRWorkspaceDrawerShell } from './ISRWorkspaceDrawerShell';
// Direct leaf-module import (NOT the UniBox barrel): UniBox.tsx imports
// ISRWorkspaceDrawer back from this file, so going through the barrel creates
// a circular dependency that breaks Vite Fast Refresh — every edit then
// full-reloads the page and wipes the in-memory query cache.
import { useStageReveal } from '../UniBox/useViewTransition';
import { SkeletonLine, SkeletonSection } from '../atoms/Skeleton';
import { getICPConfig, formatRevenue } from '../../utils/helpers';
import { n8nService } from '../core/services/n8n.service';
import { showErrorToast } from '../../utils/toast';
import { STATUS_MAP, getNormalizedStatusKey } from '../../utils/statusMapping';
import { useLogoTileTone } from '../../utils/useLogoTileTone';
import { fetchWeather, geocodePlace, getWeatherIcon, WeatherResult } from '../../src/services/weather';
import { fetchLocalNews, readLocalNews, isLocalNewsEnabled, LocalNewsItem } from '../../src/services/localNews';
import { hasOutreachMessageBody, messageHtmlToPlainText } from '../../utils/messageFormatting';
import {
  buildCompanyOpportunityGroups,
  buildCompanyTextGroups,
  formatMoneyValue,
  formatTimeline,
  mergeCompanySummaries,
  toDisplayItems,
  uniqueItems,
} from '../Tabs/companyInsights';
import { CampaignOpportunitiesNotice } from '../Tabs/CampaignOpportunitiesNotice';
import { useTeamPreferences } from '../../hooks/useTeamPreferences';
import { useAuth } from '../../contexts/AuthContext';
import { queryKeys } from '../../services/query/queryKeys';
import { useQueryClient } from '@tanstack/react-query';
import SupportRequestModal from '../modals/SupportRequestModal';
import { ReplacementLeadBadge } from './ReplacementLeadBadge';
import { timingLog, newTraceId, getExecCount } from '../../utils/timingLog';

type WorkspaceTab = 'briefing' | 'guidance' | 'pains' | 'sequence' | 'campaign' | 'psych' | 'network' | 'signals' | 'opportunities';

export interface ISRWorkspaceActor {
  id?: string | null;
  email?: string | null;
  name?: string | null;
  avatar?: string | null;
}

export interface ISRWorkspaceProps {
  lead: MappedLeadData;
  campaign?: CampaignMetadata;
  editable?: boolean;
  /** Sales stepper editability, independent of full-snapshot editing — the
   *  status/substatus save needs only campaign + lead + user identity. */
  salesEditable?: boolean;
  saving?: boolean;
  saveError?: string | null;
  currentUser: ISRWorkspaceActor;
  onSaveSnapshot: (snapshot: ISRUpdateSnapshot, metadata?: { changed_fields?: string[]; previous_values?: Record<string, string | null>; updated?: ISRUpdateDiff }) => void | Promise<void>;
  /**
   * Sales persistence for hosts that use the drawer WITHOUT the full mutation
   * controller (e.g. Sales Intelligence/UniBox). The second argument is the
   * complete notes document with the new status activity already appended.
   *
   * The updated object distinguishes category and level of each change:
   * - personLvl2: applied directly to the lead's lvl2 record
   * - company:     SUGGESTION only — shared lvl1 records require backend approval
   * - status/substatus: applied directly via update_campaign
   * - notes:       applied directly (always the full versioned document)
   */
  onSaveSalesState?: (sales: ISRSalesState, notes: ISRNotesDocument, updated?: ISRUpdateDiff) => void | Promise<void>;
  onClose: () => void;
  onOpenFullLead?: () => void;
  onDirtyChange?: (dirty: boolean) => void;
  initialTab?: WorkspaceTab;
  expanded?: boolean;
  showCloseButton?: boolean;
  className?: string;
  /** Navigate between leads without closing the drawer. */
  onPreviousLead?: () => void;
  onNextLead?: () => void;
  hasPreviousLead?: boolean;
  hasNextLead?: boolean;
  leadPosition?: number;
  totalLeads?: number;
  /** Opens the Report Generator locked to this lead (white "+Report" beside Re-enrich). */
  onGenerateReport?: (leadId: string, campaignId: string) => void;
  /** True while the full lead record hydrates — awaiting sections shimmer. */
  hydrating?: boolean;
}

export interface ISRWorkspaceDrawerProps
  extends Omit<ISRWorkspaceProps, 'onClose' | 'onDirtyChange' | 'expanded' | 'showCloseButton'> {
  open: boolean;
  onClose: () => void;
  confirmDiscard?: () => boolean;
  /** Navigate between leads without closing the drawer. */
  onPreviousLead?: () => void;
  onNextLead?: () => void;
  hasPreviousLead?: boolean;
  hasNextLead?: boolean;
  leadPosition?: number;
  totalLeads?: number;
}

type PersonDraft = ISRUpdateSnapshot['person'];
type CompanyDraft = ISRUpdateSnapshot['company'];
type ContactDraft = ISRContactFields & { person: PersonDraft; company: CompanyDraft };

interface WorkspaceState {
  contact: ContactDraft;
  sales: ISRSalesState;
  notes: ISRNotesDocument;
}

interface ContactFieldDefinition {
  scope: 'person' | 'company';
  key: string;
  label: string;
  icon: string;
  type: 'email' | 'tel' | 'url' | 'number' | 'text';
  placeholder: string;
  autoComplete?: string;
  /** Edit-only field — never rendered in read mode. */
  editOnly?: boolean;
  /** Custom format validator: returns the error message or null when valid. */
  validate?: (raw: string) => string | null;
  /** Edit mode renders a searchable GeoSelect instead of a free-text input —
   *  the value must come from the backing dataset (Appwrite collection). */
  select?: 'cnae' | 'country' | 'state' | 'city';
}

/**
 * Two tab groups: lead-specific intel first, then the generic reference
 * tabs separated by a divider — Campaign context (same for every lead in
 * the campaign) and Local News (about the lead's city, not the lead) are
 * not person-specific, so they live visually apart at the far right.
 */
const TAB_GROUPS: Array<{
  label: string;
  tabs: Array<{ key: WorkspaceTab; label: string; icon: string }>;
}> = [
  {
    label: 'Lead intelligence',
    tabs: [
      { key: 'briefing', label: 'Briefing', icon: 'bx:radar' },
      { key: 'guidance', label: 'Guidance', icon: 'bx:compass' },
            { key: 'opportunities', label: 'Opportunities', icon: 'bx:trending-up' },
      { key: 'pains', label: 'Pains', icon: 'bx:shield-quarter' },
      { key: 'psych', label: 'Psychology', icon: 'bx:brain' },
      { key: 'network', label: 'Team', icon: 'bx:group' },
      { key: 'signals', label: 'Signals', icon: 'bx:pulse' },
    ],
  },
  {
    label: 'Context',
    tabs: [
      { key: 'sequence', label: 'Outreach Sequences', icon: 'bx:send' },
    ],
  },
];

const CONTACT_FIELDS: ContactFieldDefinition[] = [
  // Person full name — mirrors Company's CNAE row for card symmetry. Seeds
  // from lvl1_person.name (transformer precedence) and writes back to the
  // lvl1/lvl2 `name` attribute via person.full_name.
  { scope: 'person', key: 'full_name', label: 'Name', icon: 'bx:user', type: 'text', placeholder: 'Ana García', autoComplete: 'name' },
  // Role/position — edit-only (the drawer header already shows it in read
  // mode). Written to the lead's person lvl2 `role_position` attribute.
  { scope: 'person', key: 'role_position', label: 'Role', icon: 'bx:briefcase', type: 'text', placeholder: 'Head of Procurement', editOnly: true },
  { scope: 'person', key: 'email_id', label: 'Email', icon: 'bx:envelope', type: 'email', placeholder: 'name@company.com', autoComplete: 'email' },
  { scope: 'person', key: 'phone_id', label: 'Phone', icon: 'bx:phone', type: 'tel', placeholder: '+1 555 0100', autoComplete: 'tel' },
  { scope: 'person', key: 'mobile_id', label: 'Mobile', icon: 'bx:mobile-alt', type: 'tel', placeholder: '+1 555 0101', autoComplete: 'tel' },
  { scope: 'person', key: 'whatsapp_id', label: 'WhatsApp', icon: 'bx:bxl-whatsapp', type: 'tel', placeholder: '+1 555 0101', autoComplete: 'tel' },
  { scope: 'person', key: 'linkedin_id', label: 'LinkedIn', icon: 'bx:bxl-linkedin-square', type: 'url', placeholder: 'https://linkedin.com/in/name' },
  { scope: 'person', key: 'twitter_x_id', label: 'X', icon: 'bx:bxl-twitter', type: 'url', placeholder: 'https://x.com/handle' },
  { scope: 'person', key: 'instagram_id', label: 'Instagram', icon: 'bx:bxl-instagram', type: 'url', placeholder: 'https://instagram.com/handle' },
  { scope: 'person', key: 'facebook_id', label: 'Facebook', icon: 'bx:bxl-facebook-circle', type: 'url', placeholder: 'https://facebook.com/name' },
  { scope: 'company', key: 'website_id', label: 'Website', icon: 'bx:globe', type: 'url', placeholder: 'https://company.com', autoComplete: 'url' },
  { scope: 'company', key: 'email_id', label: 'Email', icon: 'bx:buildings', type: 'email', placeholder: 'hello@company.com', autoComplete: 'email' },
  { scope: 'company', key: 'phone_id', label: 'Company phone', icon: 'bx:phone-call', type: 'tel', placeholder: '+1 555 0102', autoComplete: 'tel' },
  { scope: 'company', key: 'mobile_id', label: 'Company mobile', icon: 'bx:mobile', type: 'tel', placeholder: '+1 555 0103', autoComplete: 'tel' },
  { scope: 'company', key: 'whatsapp_id', label: 'Company WhatsApp', icon: 'bx:bxl-whatsapp', type: 'tel', placeholder: '+1 555 0103', autoComplete: 'tel' },
  // n8n already carries both exact counts and ranges such as "51-200".
  // Keep this a text input so the workspace never destroys that source value.
  { scope: 'company', key: 'nr_employees', label: 'Employees', icon: 'bx:group', type: 'text', placeholder: '250 or 51-200' },
  { scope: 'company', key: 'linkedin_id', label: 'LinkedIn', icon: 'bx:bxl-linkedin-square', type: 'url', placeholder: 'https://linkedin.com/company/name' },
  { scope: 'company', key: 'twitter_x_id', label: 'X', icon: 'bx:bxl-twitter', type: 'url', placeholder: 'https://x.com/handle' },
  { scope: 'company', key: 'instagram_id', label: 'Instagram', icon: 'bx:bxl-instagram', type: 'url', placeholder: 'https://instagram.com/handle' },
  { scope: 'company', key: 'facebook_id', label: 'Facebook', icon: 'bx:bxl-facebook-circle', type: 'url', placeholder: 'https://facebook.com/name' },
  { scope: 'company', key: 'revenue_range', label: 'Revenue', icon: 'bx:wallet', type: 'text', placeholder: '1M-10M or 2.5M' },
  // CNAE id — edit-only suggestion field (the read-mode header already shows
  // the full CNAE detail), chosen from the Appwrite cnae_codes collection.
  // The dropdown's value is the dotted code — byte-identical to the wire
  // format n8n stores, no conversion either way.
  {
    scope: 'company',
    key: 'cnae_code',
    label: 'CNAE',
    icon: 'bx:category',
    type: 'text',
    placeholder: 'Search CNAE…',
    editOnly: true,
    select: 'cnae',
  },
  // Location — rendered ONLY inside each card's collapsible "Location"
  // section (see FieldGroup), never in the main field grid. lvl1 schema keys
  // (person AND company both carry the same location block). Country/State/
  // City are PICKED from the geo collections, never typed.
  { scope: 'person', key: 'country', label: 'Country', icon: 'bx:planet', type: 'text', placeholder: 'Select country…', select: 'country' },
  { scope: 'person', key: 'state', label: 'State', icon: 'bx:map', type: 'text', placeholder: 'Select state…', select: 'state' },
  { scope: 'person', key: 'city', label: 'City', icon: 'bx:building-house', type: 'text', placeholder: 'Select city…', select: 'city' },
  { scope: 'person', key: 'full_address', label: 'Address', icon: 'bx:map-pin', type: 'text', placeholder: 'Calle Serrano 41', autoComplete: 'street-address' },
  { scope: 'company', key: 'country', label: 'Country', icon: 'bx:planet', type: 'text', placeholder: 'Select country…', select: 'country' },
  { scope: 'company', key: 'state', label: 'State', icon: 'bx:map', type: 'text', placeholder: 'Select state…', select: 'state' },
  { scope: 'company', key: 'city', label: 'City', icon: 'bx:building-house', type: 'text', placeholder: 'Select city…', select: 'city' },
  { scope: 'company', key: 'full_address', label: 'Address', icon: 'bx:map-pin', type: 'text', placeholder: 'Calle Serrano 41', autoComplete: 'street-address' },
];

/**
 * Social/web presence per scope. Rendered as clickable brand-icon links under
 * the contact fields. Read-only: the n8n `update_isr_lead` contract does not
 * accept social keys, so these come from the lead's enriched profile.
 */
const SOCIAL_LINKS: Record<'person' | 'company', Array<{ key: string; label: string; icon: string; accent: string }>> = {
  person: [
    { key: 'leadLinkedin', label: 'LinkedIn', icon: 'bx:bxl-linkedin-square', accent: 'text-sky-300' },
    { key: 'leadTwitter', label: 'X', icon: 'bx:bxl-twitter', accent: 'text-white/80' },
    { key: 'leadInstagram', label: 'Instagram', icon: 'bx:bxl-instagram', accent: 'text-pink-300' },
    { key: 'leadFacebook', label: 'Facebook', icon: 'bx:bxl-facebook-circle', accent: 'text-blue-300' },
    { key: 'leadTiktok', label: 'TikTok', icon: 'bx:bxl-tiktok', accent: 'text-white/80' },
    { key: 'leadYoutube', label: 'YouTube', icon: 'bx:bxl-youtube', accent: 'text-red-300' },
  ],
  company: [
    { key: 'companyLinkedin', label: 'LinkedIn', icon: 'bx:bxl-linkedin-square', accent: 'text-sky-300' },
    { key: 'companyTwitter', label: 'X', icon: 'bx:bxl-twitter', accent: 'text-white/80' },
    { key: 'companyInstagram', label: 'Instagram', icon: 'bx:bxl-instagram', accent: 'text-pink-300' },
    { key: 'companyFacebook', label: 'Facebook', icon: 'bx:bxl-facebook-circle', accent: 'text-blue-300' },
    { key: 'companyTiktok', label: 'TikTok', icon: 'bx:bxl-tiktok', accent: 'text-white/80' },
    { key: 'companyYoutube', label: 'YouTube', icon: 'bx:bxl-youtube', accent: 'text-red-300' },
  ],
};

/** Absolute URL or '' — social values arrive as bare handles or full URLs. */
const socialHref = (value: string | null | undefined): string => {
  const raw = valueOf(value).trim();
  if (!raw) return '';
  if (/^https?:\/\//i.test(raw)) return raw;
  return raw.includes('.') || raw.includes('/') ? `https://${raw}` : '';
};

/**
 * Sales stepper vocabulary — mirrors LEAD_SUBSTATUS_BY_STATUS in
 * utils/statusMapping.ts. `transitional` entries (label ends with "…") are
 * SYSTEM-OWNED processing states: rendered for information but never
 * selectable by the user.
 */
const SALES_OPTIONS: Array<{
  status: ISRSalesStatus;
  label: string;
  icon: string;
  substatuses: Array<{ value: string; label: string; transitional?: boolean }>;
}> = [
  {
    // ENRICHED is the entry stage of the sales flow — the lead has completed
    // enrichment and is ready for outreach. Exactly ONE selectable substatus:
    // "Ready to Outreach" (the default and only option).
    status: 'enriched' as ISRSalesStatus,
    label: 'Enriched',
    icon: 'bx:radar',
    substatuses: [
      { value: 'ready_to_outreach', label: 'Ready to Outreach' },
    ],
  },
  {
    status: 'outreaching' as ISRSalesStatus,
    label: 'Outreaching',
    icon: 'bx:paper-plane',
    substatuses: [
      { value: 'not_started', label: 'Not started' },
      { value: 'icebreaker', label: 'Icebreaker' },
      { value: 'follow_up', label: 'Follow-up' },
      { value: 'last_message', label: 'Last message' },
    ],
  },
  {
    // OUTREACHED is now a top-level STATUS with exactly one substatus.
    status: 'outreached' as ISRSalesStatus,
    label: 'Outreached',
    icon: 'bx:paper-plane',
    substatuses: [
      { value: 'awaiting', label: 'Awaiting' },
    ],
  },
  {
    status: 'engaged' as ISRSalesStatus,
    label: 'Engaged',
    icon: 'bx:conversation',
    substatuses: [
      { value: 'reply_received', label: 'Reply received' },
      { value: 'in_conversation', label: 'In conversation' },
      { value: 'meeting_requested', label: 'Meeting requested' },
      { value: 'meeting_scheduled', label: 'Meeting scheduled' },
      { value: 'discovery_completed', label: 'Discovery completed' },
      { value: 'qualified', label: 'Qualified' },
      { value: 'objection', label: 'Objection raised' },
    ],
  },
  {
    status: 'proposal' as ISRSalesStatus,
    label: 'Proposal',
    icon: 'bx:file-blank',
    substatuses: [
      { value: 'proposal_prepared', label: 'Proposal prepared' },
      { value: 'review_scheduled', label: 'Review scheduled' },
      { value: 'objections_resolved', label: 'Objections resolved' },
      { value: 'decision_process_identified', label: 'Decision process identified' },
      { value: 'next_action_scheduled', label: 'Next action scheduled' },
    ],
  },
  {
    status: 'converted' as ISRSalesStatus,
    label: 'Converted',
    icon: 'bx:trophy',
    substatuses: [
      { value: 'closed_won', label: 'Closed-won' },
      { value: 'onboarding', label: 'Onboarding' },
      { value: 'active', label: 'Active' },
      { value: 'upsell', label: 'Upsell opportunity' },
      { value: 'support', label: 'Support needed' },
      { value: 'at_risk', label: 'At risk' },
    ],
  },
  {
    status: 'discarded' as ISRSalesStatus,
    label: 'Discarded',
    icon: 'bx:user-x',
    substatuses: [
      { value: 'not_interested', label: 'Not interested' },
      { value: 'wrong_person', label: 'Wrong person' },
      { value: 'out_of_icp', label: 'Out of ICP' },
      { value: 'bad_timing', label: 'Bad timing' },
      { value: 'no_response', label: 'No response' },
      { value: 'bounced', label: 'Bounced' },
    ],
  },
];

const ALLOWED_SALES_STATUSES = new Set(SALES_OPTIONS.map((option) => option.status as string));

const salesStatusLabel = (sales: ISRSalesState): string =>
  SALES_OPTIONS.find((option) => option.status === sales.lead_status)?.label || 'No sales status';

const salesSubstatusLabel = (sales: ISRSalesState): string | null => {
  const status = SALES_OPTIONS.find((option) => option.status === sales.lead_status);
  return status?.substatuses.find((option) => option.value === sales.lead_substatus)?.label || null;
};

const salesActivityCopy = (previous: ISRSalesState, next: ISRSalesState) => {
  if (previous.lead_status === next.lead_status && previous.lead_substatus === next.lead_substatus) return null;
  const statusChanged = previous.lead_status !== next.lead_status;
  const nextOption = SALES_OPTIONS.find((option) => option.status === next.lead_status);
  const previousSubstatus = salesSubstatusLabel(previous);
  const nextSubstatus = salesSubstatusLabel(next);
  const previousStatus = salesStatusLabel(previous);
  const nextStatus = salesStatusLabel(next);
  return {
    title: nextStatus,
    body: statusChanged
      ? `${previousStatus} → ${nextStatus}${nextSubstatus ? ` | ${nextSubstatus}` : ''}`
      : `${nextStatus} | ${previousSubstatus || 'No substatus'} → ${nextSubstatus || 'No substatus'}`,
    icon: nextOption?.icon || 'bx:transfer-alt',
  };
};

const findSalesOptionByLabel = (value: string | null | undefined) => {
  const normalized = String(value || '')
    .replace(/^moved to\s+/i, '')
    .replace(/\s+updated$/i, '')
    .trim()
    .toLowerCase();
  return SALES_OPTIONS.find((option) => option.label.toLowerCase() === normalized);
};

/** Present older verbose status notes with the same compact copy as new ones. */
const statusActivityPresentation = (note: ISRNote) => {
  let title = note.title || 'Status changed';
  let body = note.body;
  let statusOption = findSalesOptionByLabel(title);

  const statusChangeMatch = body.match(/^Status changed from (.+?) to (.+?)\.?$/i);
  if (statusChangeMatch) {
    const [previousStatus] = statusChangeMatch[1].split(/\s+·\s+/);
    const [nextStatus, nextSubstatus] = statusChangeMatch[2].replace(/\.$/, '').split(/\s+·\s+/);
    statusOption = findSalesOptionByLabel(nextStatus) || statusOption;
    title = statusOption?.label || nextStatus;
    body = `${previousStatus} → ${title}${nextSubstatus ? ` | ${nextSubstatus}` : ''}`;
  } else {
    const substatusChangeMatch = body.match(/^(.+?) moved from (.+?) to (.+?)\.?$/i);
    if (substatusChangeMatch) {
      statusOption = findSalesOptionByLabel(substatusChangeMatch[1]) || statusOption;
      title = statusOption?.label || substatusChangeMatch[1];
      body = `${title} | ${substatusChangeMatch[2]} → ${substatusChangeMatch[3].replace(/\.$/, '')}`;
    }
  }

  if (statusOption) title = statusOption.label;
  return { title, body, status: statusOption?.status || null };
};

const valueOf = (value: unknown): string => {
  // n8n's empty sentinel is 'unknown' in either case (firstPresentText below
  // filters the lowercase variant too) — normalize both to '' so dropdowns
  // never seed/show the sentinel as a value.
  if (value === null || value === undefined) return '';
  const text = String(value);
  const lower = text.trim().toLowerCase();
  if (lower === 'unknown' || lower === 'n/a') return '';
  return text;
};

/**
 * First REAL code from n8n's comma-joined CNAE field ("unknown, 69.20" →
 * "69.20"). Enrichment emits the sentinel for individual segments, so EACH
 * segment is filtered like valueOf does; all-sentinel input resolves to ''.
 */
const firstCnaeCode = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  return String(value)
    .split(',')
    .map((segment) => segment.trim())
    .find((segment) => {
      const lower = segment.toLowerCase();
      return segment !== '' && lower !== 'unknown' && lower !== 'n/a';
    }) || '';
};

const nestedId = (value: unknown, fallback: string | number): string => {
  if (value && typeof value === 'object') {
    const raw = value as Record<string, unknown>;
    return valueOf(raw.id || raw.$id || raw.ID || fallback);
  }
  return valueOf(fallback);
};

const deriveContact = (lead: MappedLeadData): ContactDraft => {
  const raw = (lead.rawOriginalData || {}) as Record<string, any>;
  const lvl2 = (lead.personLvl2 || raw.lvl2 || raw._rawN8NLvl2 || {}) as Record<string, any>;
  const person = (lead.personLvl1 || raw.lvl1_person || {}) as Record<string, any>;
  const company = (lead.companyLvl1 || raw.lvl1_company || {}) as Record<string, any>;

  // Resolve the lvl1 IDs — the ISR contract requires person.id and company.id
  // to be the lvl1 record IDs, NOT the lvl2 lead_id. lvl1_company is an
  // object with its own `id`; lvl1_person likewise. The lvl2 block also carries
  // company_lvl1_id / person_id as cross-references, but those are the lvl1
  // values in n8n's schema. Prefer the direct lvl1 object first.
  const personLvl1Id = nestedId(person, lvl2.person_id || lvl2.company_lvl1_id || lead.id);
  const companyLvl1Id = nestedId(company, lvl2.company_lvl1_id || lvl2.company_id || lead.id);

  return {
    person: {
      id: personLvl1Id,
      // Name — lvl1 wins (transformer precedence: lvl1_person.name || lvl2.name).
      full_name: valueOf(lead.leadName || person.name || lvl2.name) || null,
      // Role/position — lead-specific lvl2 attribute (lvl2 wins; lvl1 is the
      // enriched canonical value).
      role_position: valueOf(lvl2.role_position || person.role_position || lead.leadRole) || null,
      email_id: valueOf(lead.email),
      phone_id: valueOf(lead.phone),
      mobile_id: valueOf(lead.mobile),
      whatsapp_id: valueOf(lead.leadWhatsapp || lead.mobile),
      // Social — canonical lvl1 channel keys, seeded from the mapped lead.
      linkedin_id: valueOf(lead.leadLinkedin || person.linkedin_id || lvl2.linkedin_id),
      twitter_x_id: valueOf(lead.leadTwitter || person.twitter_x_id || lvl2.twitter_x_id),
      instagram_id: valueOf(lead.leadInstagram || person.instagram_id || lvl2.instagram_id),
      facebook_id: valueOf(lead.leadFacebook || person.facebook_id || lvl2.facebook_id),
      // Person location — the LEAD's own lvl2 row first (lead-specific, most
      // current), falling back to the canonical lvl1_person record. Same
      // precedence the transformers use for contact channels.
      country: valueOf(lvl2.country || person.country) || null,
      state: valueOf(lvl2.state || person.state) || null,
      city: valueOf(lvl2.city || person.city) || null,
      full_address: valueOf(lvl2.full_address || person.full_address) || null,
    },
    company: {
      id: companyLvl1Id,
      email_id: valueOf(lead.companyEmail),
      phone_id: valueOf(lead.companyPhone),
      mobile_id: valueOf(company.mobile_id || lvl2.company_mobile_id),
      whatsapp_id: valueOf(lead.companyWhatsapp),
      website_id: valueOf(lead.companyWebsite),
      nr_employees: valueOf(lead.nr_employees || lead.company_nr_employees || lead.employee_count || lead.companyEmployees),
      linkedin_id: valueOf(lead.companyLinkedin || company.linkedin_id),
      twitter_x_id: valueOf(lead.companyTwitter || company.twitter_x_id),
      instagram_id: valueOf(lead.companyInstagram || company.instagram_id),
      facebook_id: valueOf(lead.companyFacebook || company.facebook_id),
      revenue_range: valueOf(lead.revenue && lead.revenue !== 'Unknown' ? lead.revenue : lead.revenueRange),
      // CNAE id — n8n packs multiple codes into one comma-separated field
      // ("unknown,69.20"); the editor manages ONE code, so seed the first
      // REAL code (sentinel segments are skipped).
      cnae_code: firstCnaeCode(lead.cnae_code || company.cnae_code || lvl2.cnae_code) || null,
      // HQ location — lvl1_company values first, then the mapped lead fields.
      country: valueOf(company.country || lead.companyCountry) || null,
      state: valueOf(company.state || lead.companyState) || null,
      city: valueOf(company.city || lead.companyCity) || null,
      full_address: valueOf(company.full_address || lead.fullAddress || lead.companyAddress) || null,
    },
  } as ContactDraft;
};

const normalizeNotes = (lead: MappedLeadData): ISRNotesDocument => {
  const candidate = lead.isrNotes as ISRNotesDocument | ISRNote[] | undefined;
  if (Array.isArray(candidate)) return { schema_version: 1, notes: candidate } as ISRNotesDocument;
  if (candidate && Array.isArray(candidate.notes)) {
    return { schema_version: 1, notes: candidate.notes } as ISRNotesDocument;
  }
  return { schema_version: 1, notes: [] } as ISRNotesDocument;
};

const deriveSales = (lead: MappedLeadData): ISRSalesState => {
  // Normalize through the status mapper so transitional values that arrive AS
  // the status ("MESSAGE READY", "WRITING MESSAGE", "READY TO OUTREACH") all
  // resolve to their umbrella stage ('enriched') instead of falling out of
  // the stepper vocabulary.
  const rawStatus = getNormalizedStatusKey(lead.status);
  const allowedStatus = ALLOWED_SALES_STATUSES.has(rawStatus)
    ? rawStatus as ISRSalesStatus
    : null;
  const storedSubstatus = valueOf(lead.salesSubstatus);
  const validSubstatus = allowedStatus && storedSubstatus
    ? SALES_OPTIONS.find((item) => item.status === allowedStatus)?.substatuses.some((s) => s.value === storedSubstatus)
      ? storedSubstatus as ISRLeadSubstatus
      // Legacy "outreached" substatus on an OUTREACHING lead → the lead moved
      // to the OUTREACHED stage.
      : storedSubstatus === 'outreached'
        ? 'awaiting' as ISRLeadSubstatus
        : null
    : null;
  return {
    lead_status: storedSubstatus === 'outreached' && !allowedStatus
      ? 'outreached' as ISRSalesStatus
      : allowedStatus,
    // Default the sub-status to the stage's first option (e.g. outreaching →
    // "not_started") when the lead carries none — the stepper always shows
    // a concrete detail selected.
    lead_substatus: allowedStatus
      ? validSubstatus || SALES_OPTIONS.find((item) => item.status === allowedStatus)?.substatuses[0]?.value as ISRLeadSubstatus || null
      : null,
  } as ISRSalesState;
};

const deriveState = (lead: MappedLeadData): WorkspaceState => ({
  contact: deriveContact(lead),
  sales: deriveSales(lead),
  notes: normalizeNotes(lead),
});

const snapshotFromState = (state: WorkspaceState): ISRUpdateSnapshot => ({
  person: state.contact.person,
  company: state.contact.company,
  lead_status: state.sales.lead_status,
  lead_substatus: state.sales.lead_substatus,
  notes: state.notes,
}) as ISRUpdateSnapshot;

const serializeSnapshot = (snapshot: ISRUpdateSnapshot) => JSON.stringify(snapshot);

// N8N person fields can arrive as non-strings despite the type — guard so a
// bad value renders the fallback tile instead of crashing the tab.
const getInitials = (name: unknown) => (typeof name === 'string' ? name : '')
  .split(/\s+/)
  .filter(Boolean)
  .slice(0, 2)
  .map((part) => part[0])
  .join('')
  .toUpperCase() || '?';

/** First non-empty, cleaned text value — used for the lead overview blurb. */
const firstPresentText = (...values: Array<unknown>): string => {
  for (const value of values) {
    if (value === null || value === undefined) continue;
    // N8N fields can arrive as numbers or objects despite the types —
    // coerce everything to a cleaned string or skip it.
    if (typeof value !== 'string' && typeof value !== 'number') continue;
    const cleaned = String(value).replace(/\r\n/g, '\n').replace(/\s+/g, ' ').trim();
    if (cleaned && cleaned.toLowerCase() !== 'unknown') return cleaned;
  }
  return '';
};

/** "14:32" in the lead's timezone, or '' when the timezone is unknown. */
const getLocalTimeString = (timezone?: string): string => {
  if (!timezone) return '';
  try {
    return new Intl.DateTimeFormat([], { timeZone: timezone, hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date());
  } catch {
    return '';
  }
};

/** Day part in the lead's timezone — "Morning" / "Afternoon" / "Evening" / "Night". */
const getLocalDayPart = (timezone?: string): string => {
  if (!timezone) return '';
  try {
    const hour = parseInt(new Intl.DateTimeFormat([], { timeZone: timezone, hour: 'numeric', hour12: false }).format(new Date()), 10);
    if (hour >= 5 && hour < 12) return 'Morning';
    if (hour >= 12 && hour < 17) return 'Afternoon';
    if (hour >= 17 && hour < 21) return 'Evening';
    return 'Night';
  } catch {
    return '';
  }
};

/**
 * Representative IANA timezone per ISO country code. N8N rarely populates a
 * lead's `timezone` (null in practice) and coordinates are often null too,
 * while country/city ARE reliable — so local time resolves from the country
 * when the explicit timezone is missing. Multi-timezone countries map to
 * their primary business hub; that's the right call for "is it a good hour
 * to call" — being one hour off in the US interior beats showing nothing.
 */
const COUNTRY_TIMEZONES: Record<string, string> = {
  ES: 'Europe/Madrid', PT: 'Europe/Lisbon', FR: 'Europe/Paris', DE: 'Europe/Berlin',
  IT: 'Europe/Rome', GB: 'Europe/London', IE: 'Europe/Dublin', NL: 'Europe/Amsterdam',
  BE: 'Europe/Brussels', AT: 'Europe/Vienna', CH: 'Europe/Zurich', PL: 'Europe/Warsaw',
  SE: 'Europe/Stockholm', NO: 'Europe/Oslo', DK: 'Europe/Copenhagen', FI: 'Europe/Helsinki',
  CZ: 'Europe/Prague', GR: 'Europe/Athens', RO: 'Europe/Bucharest', HU: 'Europe/Budapest',
  US: 'America/New_York', CA: 'America/Toronto', MX: 'America/Mexico_City', BR: 'America/Sao_Paulo',
  AR: 'America/Argentina/Buenos_Aires', CL: 'America/Santiago', CO: 'America/Bogota',
  PE: 'America/Lima', UY: 'America/Montevideo', VE: 'America/Caracas',
  UK: 'Europe/London',
};

// Country dialing, flag + lead-country resolution live in
// utils/phoneCountryCodes.ts (single source of truth, shared with the
// CountryPhoneSelect atom).

/**
 * Best-effort country code from any country-ish string the lead carries
 * ("ES", "Spain", "España"). Uses the browser's locale data — no network.
 */
const countryToTimezone = (country?: string | null): string => {
  const raw = (country || '').trim();
  if (!raw) return '';

  // Direct ISO code ("ES") or name ("Spain" → "ES" via display names).
  let code = raw.toUpperCase();
  if (code.length !== 2 || !/^[A-Z]{2}$/.test(code)) {
    const displayNames = new Intl.DisplayNames(['en'], { type: 'region' });
    try {
      code = displayNames.of(raw.toUpperCase()) ? code : '';
    } catch { code = ''; }
    // Reverse lookup: find the ISO code whose English name matches.
    if (!code) {
      for (const candidate of Object.keys(COUNTRY_TIMEZONES)) {
        try {
          if (displayNames.of(candidate)?.toLowerCase() === raw.toLowerCase()) {
            code = candidate;
            break;
          }
        } catch { /* skip */ }
      }
    }
  }

  return COUNTRY_TIMEZONES[code] || '';
};

/** True when Intl accepts the value as an IANA timezone. N8N sometimes
 *  stores garbage ("Unknown", offsets like "+02:00") that silently breaks
 *  every Intl call — validate before trusting an explicit value. */
const isValidTimezone = (value?: string | null): value is string => {
  if (!value || !/[a-z]/i.test(value)) return false;
  try {
    new Intl.DateTimeFormat([], { timeZone: value });
    return true;
  } catch {
    return false;
  }
};

/** Resolve the lead's display timezone: valid explicit → country. */
const resolveTimezone = (lead: MappedLeadData): string => {
  // 1. Explicit person/company timezone — only when Intl can actually use it.
  const candidates = [
    lead.personTimezone,
    lead.companyTimezone,
    (lead.rawProfileIntel as Record<string, any> | undefined)?.temporal_intelligence?.timezone,
    (lead.rawOriginalData as Record<string, any> | undefined)?.lvl2?.timezone,
  ];
  for (const candidate of candidates) {
    if (isValidTimezone(candidate)) return candidate;
  }

  // 2. Country from the raw N8N rows — the most reliably populated field.
  const raw = (lead.rawOriginalData || {}) as Record<string, any>;
  const lvl1Person = (lead.personLvl1 || raw.lvl1_person || {}) as Record<string, any>;
  const lvl1Company = (lead.companyLvl1 || raw.lvl1_company || {}) as Record<string, any>;
  const country = firstPresentText(
    lead.companyCountry,
    lvl1Company.country,
    lvl1Person.country,
    lvl1Person.country_code,
    lvl1Company.country_code,
  );
  return countryToTimezone(country);
};

const WEATHER_ICON_TONE: Record<string, string> = {
  'solar:sun-bold': 'text-amber-300',
  'solar:cloud-sun-bold': 'text-amber-200/90',
  'solar:clouds-bold': 'text-white/70',
  'solar:fog-bold': 'text-white/60',
  'solar:cloud-rain-bold': 'text-sky-300',
  'solar:cloud-snowfall-bold': 'text-cyan-100',
  'solar:cloud-storm-bold': 'text-violet-300',
};

/**
 * Avatar with graceful fallback: renders the image when present and loads;
 * otherwise a gradient initials tile (matching the list/card placeholder
 * style). Handles broken image URLs — onError swaps back to the initials.
 */
const AvatarWithFallback: React.FC<{
  src?: string | null;
  name: string;
  sizeClass: string;
  textClass: string;
  roundedClass?: string;
  alt: string;
  /** Optional href — makes the avatar a clickable link (e.g. LinkedIn). */
  href?: string;
}> = ({ src, name, sizeClass, textClass, roundedClass = 'rounded-3xl', alt, href }) => {
  const [failed, setFailed] = useState(false);
  const showImage = Boolean(src) && !failed;

  const inner = showImage ? (
    <img
      src={src || ''}
      alt={alt}
      className="h-full w-full object-cover grayscale contrast-110 brightness-110 transition-all duration-500 ease-out group-hover/avatar:grayscale-0 group-hover/avatar:contrast-100 group-hover/avatar:brightness-100 group-hover/avatar:scale-105"
      onError={() => setFailed(true)}
    />
  ) : (
    <span className={`flex h-full w-full items-center justify-center font-semibold text-white/85 transition-transform duration-500 group-hover/avatar:scale-105 ${textClass}`}>
      {getInitials(name)}
    </span>
  );

  const containerClass = `group/avatar relative flex ${sizeClass} ${roundedClass} overflow-hidden bg-[linear-gradient(145deg,rgba(82,96,255,0.22),rgba(21,251,255,0.10))] shadow-2xl cursor-pointer`;

  if (href) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={containerClass} title={`View ${name}'s profile`}>
        {inner}
      </a>
    );
  }

  return (
    <span className={containerClass}>
      {inner}
    </span>
  );
};

/** Company logo badge — tile background adapts to the logo's brightness: a
 *  light logo gets a dark tile, a dark logo gets a light tile, so both are
 *  always legible. Initials on a brand gradient when no logo. */
const CompanyLogoBadge: React.FC<{
  src?: string | null;
  name: string;
  className?: string;
}> = ({ src, name, className = 'h-11 w-11' }) => {
  const [failed, setFailed] = useState(false);
  const showImage = Boolean(src) && !failed;
  const tone = useLogoTileTone(showImage ? src : null);
  const tileClass = showImage
    ? (tone === 'dark' ? 'bg-[#0c0d11] ring-1 ring-inset ring-white/10' : 'bg-[#eef0f3]/80 ring-1 ring-inset ring-black/10')
    : 'bg-[linear-gradient(145deg,rgba(82,96,255,0.30),rgba(21,251,255,0.16))]';

  return (
    <span className={`flex ${className} items-center justify-center overflow-hidden rounded-xl border-[3px] border-[#0a0b0e] shadow-xl ${tileClass} `}>
      {showImage ? (
        <img
          src={src || ''}
          alt=""
          className="h-full w-full object-contain p-1"
          onError={() => setFailed(true)}
        />
      ) : (
        <span className="text-[13px] font-bold text-white/90">{getInitials(name)}</span>
      )}
    </span>
  );
};

const normalizePhone = (value: string | null | undefined) => {
  const digits = valueOf(value).replace(/\D/g, '').replace(/^00/, '');
  return digits.length >= 7 ? digits : '';
};

const normalizeUrl = (value: string | null | undefined) => {
  const raw = valueOf(value).trim();
  if (!raw) return '';
  if (/^(https?:|mailto:|tel:|sms:)/i.test(raw)) return raw;
  return raw.includes('.') ? `https://${raw}` : '';
};

/** Canonical profile URL builders for the social platforms. */
const SOCIAL_PROFILE_BUILDERS: Record<string, (handle: string) => string> = {
  linkedin_id: (h) => `https://linkedin.com/in/${h}`,
  twitter_x_id: (h) => `https://x.com/${h}`,
  instagram_id: (h) => `https://instagram.com/${h}`,
  facebook_id: (h) => `https://facebook.com/${h}`,
};

/**
 * Normalize a social field value: lowercase, strip a leading '@', and expand a
 * bare handle into the platform's canonical full URL. Uses the canonical URL
 * for the platform when available (linkedin/twitter/instagram/facebook).
 */
const normalizeSocialValue = (key: string, value: string): string => {
  const raw = value.trim().toLowerCase().replace(/^@/, '');
  if (!raw) return value;
  const builder = SOCIAL_PROFILE_BUILDERS[key];
  if (builder) {
    // Already a full URL pointing at the platform → normalize the path.
    if (/^https?:\/\//.test(raw)) return raw.replace(/\/+$/, '');
    return builder(raw.replace(/^https?:\/\/[^/]+\//, ''));
  }
  // Generic: ensure scheme + lowercase.
  if (/^https?:\/\//i.test(raw)) return raw.replace(/\/+$/, '');
  return raw.includes('.') ? `https://${raw}` : raw;
};

const channelMeta: Record<string, { label: string; icon: string; accent: string }> = {
  email: { label: 'Email', icon: 'bx:envelope', accent: 'text-indigo-200' },
  linkedin: { label: 'LinkedIn', icon: 'bx:bxl-linkedin-square', accent: 'text-sky-300' },
  whatsapp: { label: 'WhatsApp', icon: 'bx:bxl-whatsapp', accent: 'text-emerald-300' },
  phone: { label: 'Phone', icon: 'bx:phone-call', accent: 'text-amber-200' },
  sms: { label: 'SMS', icon: 'bx:message-rounded-dots', accent: 'text-yellow-200' },
  twitter_x: { label: 'X', icon: 'bx:bxl-twitter', accent: 'text-white/80' },
  instagram: { label: 'Instagram', icon: 'bx:bxl-instagram', accent: 'text-pink-300' },
  messenger: { label: 'Messenger', icon: 'bx:bxl-messenger', accent: 'text-blue-300' },
  tiktok: { label: 'TikTok', icon: 'bx:bxl-tiktok', accent: 'text-white/80' },
  line: { label: 'LINE', icon: 'bx:message-square-dots', accent: 'text-lime-300' },
};

const channelAccentColor: Record<string, string> = {
  email: '#5260FF',
  linkedin: '#0A66C2',
  whatsapp: '#25D366',
  phone: '#F59E0B',
  sms: '#EAB308',
  twitter_x: '#AEB7C8',
  instagram: '#E1306C',
  messenger: '#168AFF',
  tiktok: '#D7DEE9',
  line: '#06C755',
};

const copyToClipboard = async (value: string) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
};

const buildChannelUrl = (channel: string, step: OutreachStep, lead: MappedLeadData): string => {
  const subject = step.subject ? messageHtmlToPlainText(step.subject) : '';
  const body = step.body ? messageHtmlToPlainText(step.body) : '';
  const personNumber = normalizePhone(lead.leadWhatsapp || lead.mobile || lead.phone);
  switch (channel) {
    case 'email':
      return lead.email
        ? `mailto:${encodeURIComponent(lead.email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
        : '';
    case 'whatsapp':
      return personNumber ? `https://wa.me/${personNumber}?text=${encodeURIComponent(body)}` : '';
    case 'phone':
      return personNumber ? `tel:+${personNumber}` : '';
    case 'sms':
      return personNumber ? `sms:+${personNumber}?&body=${encodeURIComponent(body)}` : '';
    case 'linkedin':
      return normalizeUrl(lead.leadLinkedin);
    case 'twitter_x':
      return normalizeUrl(lead.leadTwitter);
    case 'instagram':
      return normalizeUrl(lead.leadInstagram);
    case 'messenger':
      return normalizeUrl(lead.leadMessenger);
    case 'tiktok':
      return normalizeUrl(lead.leadTiktok);
    case 'line':
      return normalizeUrl(lead.leadLine);
    default:
      return '';
  }
};

const validateContact = (
  contact: ContactDraft,
  /** Values the user has actually changed — only these may produce blocking
   *  errors. Seeded values from n8n pass through untouched so a legacy or
   *  differently-formatted stored value never disables the Edit button before
   *  the user has touched anything. */
  changedKeys?: Set<string>,
): { errors: Record<string, string>; warnings: Record<string, string> } => {
  const errors: Record<string, string> = {};
  const warnings: Record<string, string> = {};
  const values = contact as unknown as Record<'person' | 'company', Record<string, unknown>>;
  CONTACT_FIELDS.forEach((field) => {
    const raw = valueOf(values[field.scope][field.key]).trim();
    if (!raw) return;
    const path = `${field.scope}.${field.key}`;
    // Custom format rules (e.g. CNAE) apply ONLY to values the user typed —
    // a seeded legacy value that fails the pattern must not block editing.
    const touched = changedKeys?.has(path) ?? false;
    if (field.validate && touched) {
      const message = field.validate(raw);
      if (message) {
        errors[path] = message;
        return;
      }
    }
    // ALL format rules follow the same touched-only contract: n8n stores
    // enrichment output that can violate any of them (observed: company
    // website_id carrying a raw timestamp "2026-10-28 13:19:43"). Validating
    // seeded values too would permanently disable the Edit button for those
    // leads — exactly what this function's doc comment forbids.
    if (!touched) return;
    if (field.type === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(raw)) {
      errors[path] = 'Enter a valid email address.';
    } else if (field.type === 'url' && !/^https?:\/\//i.test(normalizeUrl(raw))) {
      errors[path] = 'Enter a valid website.';
    } else if (field.type === 'tel') {
      // Per-country validation: length, line type (mobile/landline), and
      // field-specific rules (whatsapp/mobile must be mobile, phone can be both).
      const withCode = hasCountryCode(raw);
      if (withCode) {
        const { blocker, message } = validatePhoneForField(raw, field.key as 'phone_id' | 'mobile_id' | 'whatsapp_id');
        if (blocker && message) {
          errors[path] = message;
        } else if (message) {
          // Non-blocking warning (e.g. "could not verify mobile").
          warnings[path] = message;
        }
      } else {
        // Number present but no +country code: a SOFT warning, not a blocker.
        // The dedicated country-code dropdown prefills this; the user can
        // still save and the number goes to n8n as-is for correction.
        warnings[path] = 'Add the country code (e.g. +34) before the number.';
      }
    } else if (field.type === 'number' && (!Number.isInteger(Number(raw)) || Number(raw) < 0)) {
      errors[path] = 'Enter a whole number of employees.';
    }
  });
  return { errors, warnings };
};

const FieldGroup: React.FC<{
  title: string;
  icon: string;
  scope: 'person' | 'company';
  lead: MappedLeadData;
  contact: ContactDraft;
  editing: boolean;
  editable: boolean;
  errors: Record<string, string>;
  /** Soft, non-blocking hints (e.g. a phone missing its country code). */
  warnings?: Record<string, string>;
  /** lvl1 record ID shown as a click-to-copy #tag in the header. */
  leadId?: string | number | null;
  onChange: (scope: 'person' | 'company', key: string, value: string) => void;
}> = ({ title, icon, scope, lead, contact, editing, editable, errors, warnings = {}, leadId, onChange }) => {
  const scopedFields = CONTACT_FIELDS.filter((field) => field.scope === scope);
  const values = contact[scope] as unknown as Record<string, unknown>;
  // Locale for dataset-backed labels (CNAE today). English is the platform
  // default; when the per-user language switcher lands this hook already
  // carries it — no call-site changes needed.
  const { data: userPreferences } = useUserPreferences();
  const cnaeLocale = (userPreferences?.language as string) || 'en';
  // HQ Location collapsible — company scope only. Auto-opens on edit so the
  // fields are visible while editing; the user can still collapse it.
  const [locationOpen, setLocationOpen] = useState(false);
  const locationWasOpenRef = useRef(false);
  useEffect(() => {
    if (editing && editable && !locationWasOpenRef.current) {
      locationWasOpenRef.current = true;
      setLocationOpen(true);
    }
    if (!editing) locationWasOpenRef.current = false;
  }, [editing, editable]);

  // ── Dataset-backed dropdown options (CNAE + cascading geo) ──────────────
  // All loaded lazily via react-query; GeoSelect filters client-side. The
  // hooks run unconditionally per React rules — the queries are staleTime'd
  // and shared across both FieldGroup instances by key.
  const { data: cnaeRows = [] as CnaeCode[] } = useQuery({
    queryKey: ['cnae-codes'],
    queryFn: () => cnaeService.listCnaeCodes(),
    staleTime: Infinity,
  });
  const { data: geoCountries = [] as GeoCountry[] } = useQuery({
    queryKey: ['geo-countries'],
    queryFn: () => geoService.listCountries(),
    staleTime: Infinity,
  });
  // Draft values drive the cascade — editing country re-derives state/city lists.
  const draftCountryName = valueOf(values.country);
  const draftStateName = valueOf(values.state);
  // Country/state matching must be forgiving: n8n stores whatever enrichment
  // produced ("Community of Madrid", "Madrid (province)", "ESPAÑA", …), while
  // the geo collections carry canonical names. Normalize to lowercase ASCII
  // with parentheses/accents stripped, and also accept ISO codes + the
  // country's native name ("España" → ES).
  const geoNameKey = (raw: string): string =>
    raw.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
      .replace(/\(.*?\)/g, '').replace(/[^a-z0-9]/g, '').trim();
  const countryIso = useMemo(() => {
    if (!draftCountryName) return '';
    const key = geoNameKey(draftCountryName);
    if (!key) return '';
    // Exact (normalized) first, then containment either way for values like
    // "Community of Madrid" vs canonical "Madrid".
    return geoCountries.find((c) =>
      geoNameKey(c.name) === key ||
      c.iso2?.toLowerCase() === key ||
      geoNameKey(c.native || '') === key,
    )?.iso2
      || geoCountries.find((c) => {
        const nameKey = geoNameKey(c.name);
        return nameKey && (key.includes(nameKey) || nameKey.includes(key));
      })?.iso2
      || '';
  }, [geoCountries, draftCountryName]);
  const { data: geoStates = [] } = useQuery({
    queryKey: ['geo-states', countryIso],
    queryFn: () => geoService.listStates(countryIso),
    enabled: Boolean(countryIso),
    staleTime: Infinity,
  });
  const stateCode = useMemo(() => {
    if (!draftStateName) return '';
    const key = geoNameKey(draftStateName);
    if (!key) return '';
    return (geoStates.find((s) =>
      geoNameKey(s.name) === key ||
      s.state_code?.toLowerCase() === key,
    )?.state_code)
      || (geoStates.find((s) => {
        const nameKey = geoNameKey(s.name);
        return nameKey && key.length >= 3 && (key.includes(nameKey) || nameKey.includes(key));
      })?.state_code)
      || '';
  }, [geoStates, draftStateName]);
  // City-driven fallback: when the stored state string can't be matched to a
  // geo_states row (native names, ISO forms, empty), resolve the state from
  // the lead's CITY instead so the city dropdown still opens. Cleared the
  // moment the user picks a real state from the dropdown.
  const draftCityName = valueOf(values.city);
  const [stateFromCity, setStateFromCity] = useState('');
  useEffect(() => {
    let cancelled = false;
    if (countryIso && draftStateName && !stateCode && draftCityName) {
      geoService.stateForCity(countryIso, draftCityName).then((resolved) => {
        if (!cancelled) setStateFromCity(resolved || '');
      }).catch(() => undefined);
    } else if (stateCode) {
      setStateFromCity('');
    }
    return () => { cancelled = true; };
  }, [countryIso, draftStateName, stateCode, draftCityName]);
  const cityStateCode = stateCode || stateFromCity;
  const { data: geoCities = [] } = useQuery({
    queryKey: ['geo-cities', countryIso, cityStateCode],
    queryFn: () => geoService.listCities(cityStateCode, countryIso),
    enabled: Boolean(countryIso && cityStateCode),
    staleTime: Infinity,
  });
  // Row-symmetric layout — the SAME data type sits on the SAME row in the
  // Person and Company cards: 1) Email (+ Employees/Revenue for company),
  // 1b) Name (person) / CNAE (company) mirrored row, 2) Phones,
  // 3) Website, then socials as icon rows.
  const emailField = scopedFields.find((field) => field.key === 'email_id')!;
  const fullNameField = scope === 'person' ? scopedFields.find((field) => field.key === 'full_name') : undefined;
  const rolePositionField = scope === 'person' ? scopedFields.find((field) => field.key === 'role_position') : undefined;
  const phoneFields = scopedFields.filter((field) => field.type === 'tel');
  const websiteField = scope === 'company' ? scopedFields.find((field) => field.key === 'website_id') : undefined;
  const employeesField = scope === 'company' ? scopedFields.find((field) => field.key === 'nr_employees') : undefined;
  const revenueField = scope === 'company' ? scopedFields.find((field) => field.key === 'revenue_range') : undefined;
  // CNAE id — edit-only (the read-mode header already shows the CNAE detail).
  const cnaeField = scope === 'company' ? scopedFields.find((field) => field.key === 'cnae_code') : undefined;
  // Location fields — rendered ONLY inside the collapsible below Row 3
  // (both scopes: person lvl1 and company lvl1 carry the same location block).
  const locationFields = scopedFields.filter((field) =>
    ['country', 'state', 'city', 'full_address'].includes(field.key),
  );

  /** Options for dataset-backed selects. A seeded value that isn't in the
   *  dataset becomes a synthetic option so it stays visible and never gets
   *  silently wiped — it only changes when the user picks something else. */
  const selectOptionsFor = (field: ContactFieldDefinition): GeoOption[] => {
    switch (field.select) {
      case 'cnae': {
        // One language ONLY (user's locale, English default) — no second
        // language shown anywhere in the row.
        const options: GeoOption[] = cnaeRows.map((row) => ({
          value: row.code,
          label: `${row.code} · ${cnaeLabelFor(row, cnaeLocale)}`,
        }));
        // Safety net: the draft value should always be a single dotted code
        // (seeded from the first REAL code of n8n's comma-joined field), but
        // if a legacy/multi value slips through, pin it to its first code so
        // the trigger never renders a comma blob.
        const current = firstCnaeCode(values[field.key]);
        if (current && !options.some((opt) => opt.value === current)) {
          options.unshift({ value: current, label: `${current} · (current value)` });
        }
        return options;
      }
      case 'country': {
        const options: GeoOption[] = geoCountries.map((c) => ({
          value: c.name,
          label: c.name,
          emoji: c.emoji || undefined,
        }));
        const current = valueOf(values[field.key]).trim();
        if (current && !options.some((opt) => opt.value === current)) {
          options.unshift({ value: current, label: current });
        }
        return options;
      }
      case 'state': {
        const options: GeoOption[] = geoStates.map((s) => ({
          value: s.name,
          label: s.name,
          hint: s.state_code || undefined,
        }));
        const current = valueOf(values[field.key]).trim();
        if (current && !options.some((opt) => opt.value === current)) {
          options.unshift({ value: current, label: current });
        }
        return options;
      }
      case 'city': {
        const options: GeoOption[] = geoCities.map((c) => ({
          value: c.name,
          label: c.name,
        }));
        const current = valueOf(values[field.key]).trim();
        if (current && !options.some((opt) => opt.value === current)) {
          options.unshift({ value: current, label: current });
        }
        return options;
      }
      default:
        return [];
    }
  };

  /** Searchable dataset-backed editor for select fields (CNAE / Country /
   *  State / City) — replaces the free-text input so users can only PICK a
   *  known value. Underline styling matches the inline edit fields. */
  const renderSelectEditor = (field: ContactFieldDefinition, path: string) => {
    const disabled = field.select === 'state' ? !countryIso
      : field.select === 'city' ? !(countryIso && cityStateCode)
      : false;
    // CNAE manages ONE code — a legacy multi-code value shows/keeps its
    // first REAL code (sentinel segments skipped).
    const currentValue = field.select === 'cnae'
      ? firstCnaeCode(values[field.key])
      : valueOf(values[field.key]).trim();
    return (
      <GeoSelect
        id={path}
        label={field.label}
        value={currentValue}
        onChange={(next) => onChange(scope, field.key, next)}
        options={selectOptionsFor(field)}
        placeholder={field.placeholder}
        disabled={disabled}
        borderless
        compact
        triggerClassName="!h-6 !border-0 !border-b !border-b-white/20 !bg-transparent !text-[12px] !shadow-none !rounded-none focus:!border-b-cyan-300/70 focus:!ring-0 !px-0 !text-left"
        formatTrigger={(option) => option?.label || ''}
      />
    );
  };
  // Social fields live in the editable draft — shown even when empty so the
  // ISR can ADD a missing profile while editing.
  const socialFields = scopedFields.filter(
    (field) => ['linkedin_id', 'twitter_x_id', 'instagram_id', 'facebook_id'].includes(field.key),
  );

  /** Country-code aware editor for phone/mobile/whatsapp fields. Shows the
   *  detected dialing prefix and lets the user switch it, while the rest of
   *  the number is edited freely. Prefills the prefix from the lead's country
   *  when the field has none. */
  const renderTelEditor = (
    field: ContactFieldDefinition,
    rawValue: string,
    fieldError: string | undefined,
    doChange: (s: 'person' | 'company', k: string, v: string) => void,
  ) => {
    const telPath = `${scope}.${field.key}`;
    // Detect the country prefix from the RAW value at render time.
    const { prefix, rest } = splitPhonePrefix(rawValue);
    const detected = leadDialingCode(lead) || '+34';
    // When the number already carries a +country code, use it; otherwise fall
    // back to the lead's country-derived prefix so the dropdown always shows a
    // valid preselected value (never blank).
    const effectivePrefix = prefix || detected;

    const applyPrefix = (nextPrefix: string) => {
      // Re-read the current value directly from the store to avoid stale
      // closure. This ensures we never lose digits when the prefix changes.
      const currentValue = valueOf(values[field.key]);
      const currentRest = splitPhonePrefix(currentValue).rest || '';
      const nextValue = currentRest ? `${nextPrefix} ${currentRest}` : nextPrefix;
      doChange(scope, field.key, nextValue);
      // Mirror WhatsApp to mobile when they share the same number.
      if (field.key === 'whatsapp_id' && scope === 'person') {
        const mobileKey = 'mobile_id';
        const mobileRaw = valueOf(values[mobileKey]);
        const mobileRest = splitPhonePrefix(mobileRaw).rest;
        if (!mobileRest) doChange(scope, mobileKey, nextValue);
      }
    };

    // The input shows ONLY the local number part (no prefix) — the prefix is
    // shown in the CountryPhoneSelect chip. Together they form the full value
    // `+33 14997600` that gets stored. This avoids the confusing "double +33"
    // visual where the user sees the prefix in both the chip and the input.
    return (
      <div className="flex min-w-0 flex-1 items-center gap-1">
        <div className="w-[92px] shrink-0">
          <CountryPhoneSelect
            id={telPath}
            value={effectivePrefix}
            onChange={applyPrefix}
            disabled={false}
            borderless
            compact
            placeholder="+…"
            triggerClassName="!h-6 !border-0 !border-b !border-b-white/20 !bg-transparent !text-[12px] !shadow-none !rounded-none focus:!border-b-cyan-300/70 focus:!ring-0 !px-0"
          />
        </div>
        <input
          name={telPath}
          type="tel"
          inputMode="tel"
          autoComplete={field.autoComplete}
          value={rest}
          onChange={(event) => {
            // User typed only the local number — rebuild the full value with
            // the current prefix from the dropdown.
            const digits = event.target.value.replace(/[^\d\s\-()]/g, '');
            const nextValue = digits ? `${effectivePrefix} ${digits}` : effectivePrefix;
            doChange(scope, field.key, nextValue);
            // Mobile → WhatsApp auto-seed: same number in practice — but ONLY
            // while whatsapp is empty, so an explicitly different value is
            // never overwritten (one-way mirror).
            if (field.key === 'mobile_id') {
              const whatsappRaw = valueOf(values.whatsapp_id);
              const whatsappRest = splitPhonePrefix(whatsappRaw).rest;
              if (!whatsappRest) doChange(scope, 'whatsapp_id', nextValue);
            }
          }}
          placeholder={rawValue ? ' ' : 'Number'}
          aria-invalid={Boolean(fieldError)}
          aria-describedby={fieldError ? `${telPath}-error` : undefined}
          aria-label={`${field.label} number`}
          className="min-w-0 flex-1 border-b border-white/20 bg-transparent text-[12px] text-white outline-none placeholder:text-white/20 focus:border-cyan-300/70"
        />
      </div>
    );
  };

  const renderField = (field: ContactFieldDefinition, className = '') => {
    const path = `${scope}.${field.key}`;
    const value = valueOf(values[field.key]);
    const error = errors[path];
    return (
      <div key={path} className={`min-w-0 pb-1.5 ${className}`}>
        {editing && editable ? (
          <div className="flex items-center gap-2">
            <Icon icon={field.icon} className="h-4 w-4 shrink-0 text-white/40" aria-hidden="true" />
            {field.select ? renderSelectEditor(field, path) : field.type === 'tel' ? renderTelEditor(field, value, error, onChange) : (
              <input
                name={path}
                type={field.type}
                inputMode={field.type === 'number' ? 'numeric' : undefined}
                autoComplete={field.autoComplete}
                min={field.type === 'number' ? 0 : undefined}
                step={field.type === 'number' ? 1 : undefined}
                value={value}
                onChange={(event) => onChange(scope, field.key, event.target.value)}
                placeholder={field.placeholder}
                aria-invalid={Boolean(error)}
                aria-describedby={error ? `${path}-error` : undefined}
                aria-label={field.label}
                className="min-w-0 flex-1 border-b border-white/20 bg-transparent text-[12px] text-white outline-none placeholder:text-white/20 focus:border-cyan-300/70"
              />
            )}
          </div>
        ) : (
          <div className="flex min-w-0 items-center gap-2" title={`${field.label}: ${value || '—'}`}>
            <Icon icon={field.icon} className="h-4 w-4 shrink-0 text-white/35" aria-hidden="true" />
            {field.type === 'url' && value ? (
              <a
                href={/^https?:\/\//i.test(value) ? value : `https://${value}`}
                target="_blank"
                rel="noopener noreferrer"
                className="min-w-0 truncate text-[12px] text-cyan-200/85 underline decoration-cyan-200/30 underline-offset-2 transition-colors hover:text-cyan-100 hover:decoration-cyan-200/70"
              >
                {value}
              </a>
            ) : field.type === 'email' && value ? (
              <span className="inline-flex items-center gap-1.5 min-w-0">
                <a
                  href={`mailto:${value}`}
                  className="min-w-0 truncate text-[12px] text-white/85 underline decoration-white/20 underline-offset-2 transition-colors hover:text-white hover:decoration-white/50"
                >
                  {value}
                </a>
                {scope === 'person' && lead.emailProviderData && (
                  <EmailIntelBadge email={value} data={lead.emailProviderData} />
                )}
              </span>
            ) : (
              <span className={`min-w-0 truncate text-[12px] ${value ? 'text-white' : 'text-white/25'}`}>
                {field.key === 'revenue_range' && value && value !== 'Unknown'
                  ? formatRevenue(value)
                  : value || '—'}
              </span>
            )}
          </div>
        )}
        {error && <dd id={`${path}-error`} className="mt-0.5 pl-6 text-[10px] text-rose-300">{error}</dd>}
        {!error && warnings[path] && (
          <dd id={`${path}-warn`} className="mt-0.5 pl-6 text-[10px] text-amber-300/90">{warnings[path]}</dd>
        )}
      </div>
    );
  };

  /** Social field — clickable brand icon with the URL on hover. Empty fields
   *  are hidden entirely (visible only as inputs while editing, so a missing
   *  profile can be added). */
  const renderSocialField = (field: ContactFieldDefinition) => {
    const path = `${scope}.${field.key}`;
    const value = valueOf(values[field.key]);
    const error = errors[path];

    // Not editing and empty → show nothing.
    if (!(editing && editable) && !value) return null;

    if (editing && editable) {
      return (
        <div key={path} className="min-w-0 flex items-center gap-2.5 border-b border-white/[0.06] py-1.5">
          <Icon icon={field.icon} className="h-4 w-4 shrink-0 text-white/45" aria-hidden="true" />
          <input
            name={path}
            type="url"
            value={value}
            onChange={(event) => onChange(scope, field.key, event.target.value)}
            placeholder={field.placeholder}
            aria-invalid={Boolean(error)}
            aria-label={field.label}
            className="min-w-0 flex-1 border-b border-white/20 bg-transparent text-[12px] text-white outline-none placeholder:text-white/20 focus:border-cyan-300/70"
          />
        </div>
      );
    }

    const href = /^https?:\/\//i.test(value) ? value : `https://${value}`;
    return (
      <a
        key={path}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`${field.label} — opens in a new tab`}
        title={value}
        className="flex h-8 w-8 items-center justify-center rounded-lg border border-white/10 bg-white/[0.04] transition-colors hover:border-white/25 hover:bg-white/[0.08]"
      >
        <Icon icon={field.icon} className="h-4 w-4" aria-hidden="true" />
      </a>
    );
  };

  return (
    <section className="min-w-0">
      <h3 className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-white/50">
        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-white/[0.055] text-white/55">
          <Icon icon={icon} className="h-3.5 w-3.5" aria-hidden="true" />
        </span>
        {title}
        {leadId && (
          <button type="button" onClick={async () => { const id = String(leadId); try { await navigator.clipboard.writeText(id); } catch {} }} title={`Copy ${title} ID: ${leadId}`} className="cursor-pointer font-mono text-[9px] text-white/25 transition-colors hover:text-white/50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-cyan-300/40">#{leadId}</button>
        )}
        {scope === 'company' && editing && editable && (
          <span
            className="group/approval relative inline-flex items-center gap-1 rounded-md bg-amber-400/10 px-1.5 py-0.5 text-[9px] font-semibold normal-case tracking-normal text-amber-300"
          >
            <Icon icon="bx:time-five" className="h-3 w-3 shrink-0" aria-hidden="true" />
            Update information requires Brainiacs review &amp; approval
            <span className="invisible group-hover/approval:visible absolute bottom-full left-1/2 z-50 mb-2 w-64 -translate-x-1/2 rounded-lg border border-white/10 bg-[#14161f] p-2.5 text-[11px] font-normal normal-case leading-5 text-white/75 shadow-xl">
              Company records are shared across all users. Edits here are suggestions that require review and approval from Brainiacs before being updated.
            </span>
          </span>
        )}
      </h3>

      <dl className="mt-1.5 space-y-1.5">
        {/* Row 1 — Email. Company pairs Employees + Revenue beside it. */}
        <div className={scope === 'company'
          ? 'grid grid-cols-[minmax(0,1fr)_minmax(64px,0.42fr)_minmax(82px,0.58fr)] items-center gap-x-2'
          : 'flex items-center gap-3'}>
          {renderField(emailField, 'min-w-0 flex-1')}
          {employeesField && renderField(employeesField)}
          {revenueField && renderField(revenueField)}
        </div>

        {/* Row 1b — MIRRORED edit-only row: Person = full name + role inputs,
            Company = CNAE dropdown. Rendered ONLY while editing — in read mode
            neither card has it, so both drop the row equally and stay aligned. */}
        {editing && editable && fullNameField ? (
          <div className="grid grid-cols-2 gap-x-2">
            {renderField(fullNameField)}
            {rolePositionField && renderField(rolePositionField)}
          </div>
        ) : editing && editable && cnaeField ? (
          <div className="min-h-8">{renderField(cnaeField)}</div>
        ) : null}

        {/* Row 2 — Phone / Mobile / WhatsApp. Read-only: compact 3-across
            grid. Editing: phone full-width, then Mobile + WhatsApp SHARE a
            2-col row (normally the same number — kept side by side so the
            mirror behaviour is visible while typing). */}
        {editing && editable ? (
          (() => {
            const phoneField = phoneFields.find((field) => field.key === 'phone_id');
            const mobileField = phoneFields.find((field) => field.key === 'mobile_id');
            const whatsappField = phoneFields.find((field) => field.key === 'whatsapp_id');
            const otherFields = phoneFields.filter(
              (field) => !['phone_id', 'mobile_id', 'whatsapp_id'].includes(field.key),
            );
            return (
              <div className="flex flex-col gap-1">
                {phoneField && renderField(phoneField)}
                {(mobileField || whatsappField) && (
                  <div className="grid grid-cols-2 gap-x-2">
                    {mobileField && renderField(mobileField)}
                    {whatsappField && renderField(whatsappField)}
                  </div>
                )}
                {otherFields.map((field) => renderField(field))}
              </div>
            );
          })()
        ) : (
          <div className="grid grid-cols-3 gap-x-2">
            {phoneFields.map((field) => renderField(field))}
          </div>
        )}

        {/* Row 3 — fixed-height slot (min-h-8, matching the social icon
            buttons) on BOTH cards so the Location blocks below always land on
            the same baseline, even when Company has no social icons to show
            next to its Website. Company = Website + inline social icons;
            Person = social icons; editing Person = invisible spacer. */}
        {websiteField ? (
          <div className="flex min-h-8 items-center gap-3">
            <div className="min-w-0 flex-1">{renderField(websiteField)}</div>
            {!editing && socialFields.length > 0 && (
              <div className="flex shrink-0 items-center gap-1">
                {socialFields.map((field) => renderSocialField(field))}
              </div>
            )}
          </div>
        ) : !editing ? (
          <div className="flex min-h-8 flex-wrap items-center gap-1">
            {socialFields.map((field) => renderSocialField(field))}
          </div>
        ) : (
          <div aria-hidden="true" className="min-h-8" />
        )}
      </dl>

      {/* Social inputs while editing — stacked full-width on BOTH cards
          (same slot, same count → Location blocks stay aligned). */}
      {editing && editable && socialFields.length > 0 && (
        <div className="mt-2 space-y-1">
          {socialFields.map((field) => renderSocialField(field))}
        </div>
      )}

      {/* Location — collapsible (person AND company cards), UNDER the social
          icons. Collapsed shows a one-line summary; expanded shows Country /
          State / City / Address fields. Auto-opens on edit; the summary line
          stays visible while editing so a collapsed state never hides what's
          being saved. */}
      {locationFields.length > 0 && (() => {
        // Canonical address line: Address line, CP City, State, Country —
        // matches the format used across the system.
        const addressLine = valueOf(values.full_address);
        const cityName = valueOf(values.city);
        const stateName = valueOf(values.state);
        const countryName = valueOf(values.country);
        const geoParts = [cityName, stateName, countryName].filter(Boolean);
        const fullLine = [addressLine, ...geoParts].filter(Boolean).join(', ');
        const expanded = locationOpen || (editing && editable);
        return (
          <div className="mt-2 rounded-lg border border-white/[0.06] bg-white/[0.02]">
            <button
              type="button"
              onClick={() => setLocationOpen((open) => !open)}
              aria-expanded={expanded}
              className="flex w-full items-center gap-1.5 px-2 py-1.5 text-left transition-colors hover:bg-white/[0.03] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-cyan-300/40"
            >
              <Icon icon="bx:map" className="h-3.5 w-3.5 shrink-0 text-white/40" aria-hidden="true" />
              <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-white/50">Location</span>
              <span className="min-w-0 flex-1 truncate text-right text-[11px] text-white/55" title={fullLine}>{fullLine || '—'}</span>
              <Icon
                icon="bx:chevron-down"
                className={`h-3.5 w-3.5 shrink-0 text-white/40 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
                aria-hidden="true"
              />
            </button>
            {expanded && (
              <div className="space-y-1 border-t border-white/[0.06] px-2 py-1.5">
                {locationFields.map((field) => renderField(field))}
              </div>
            )}
          </div>
        );
      })()}
    </section>
  );
};

/**
 * Compact sales progression control — one click (or drag) moves the lead.
 * Only the current stage uses its STATUS_MAP accent; every other stage stays
 * visually inactive so completion is never confused with selection.
 */
const SalesStateControl: React.FC<{
  leadStatus: string;
  state: ISRSalesState;
  disabled: boolean;
  onChange: (next: ISRSalesState) => void;
  onPreviousLead?: () => void;
  onNextLead?: () => void;
  hasPreviousLead?: boolean;
  hasNextLead?: boolean;
  leadPosition?: number;
  totalLeads?: number;
  /** When set, a Close button appears in the status band's right column. */
  onClose?: () => void;
  /** True when the team's mod_sales flag is active — gates the Start
   *  Outreach button (active vs. locked-request). */
  hasSalesModule?: boolean;
  /** Fires the N8N auto-outreach workflow (active button click). */
  onStartOutreach?: () => void;
  /** Opens the Sales Intelligence activation modal (locked button click). */
  onRequestOutreach?: () => void;
}> = ({ leadStatus, state, disabled, onChange, onPreviousLead, onNextLead, hasPreviousLead, hasNextLead, leadPosition, totalLeads, onClose, hasSalesModule = false, onStartOutreach, onRequestOutreach }) => {
  const activeOption = SALES_OPTIONS.find((item) => item.status === state.lead_status);
  const statusBandRef = useRef<HTMLDivElement>(null);
  const statusTrackRef = useRef<HTMLDivElement>(null);
  const activeStatusRef = useRef<HTMLButtonElement>(null);
  const [triangleLeft, setTriangleLeft] = useState<number | null>(null);

  useLayoutEffect(() => {
    const band = statusBandRef.current;
    const track = statusTrackRef.current;
    const button = activeStatusRef.current;
    if (!activeOption || !band || !track || !button) {
      setTriangleLeft(null);
      return undefined;
    }

    // Keep later lifecycle stages visible, then locate the arrow from the
    // selected button's actual rendered center (not the center of the band).
    const desiredScroll = Math.max(
      0,
      button.offsetLeft - ((track.clientWidth - button.offsetWidth) / 2),
    );
    track.scrollLeft = desiredScroll;

    const updateTriangle = () => {
      const bandRect = band.getBoundingClientRect();
      const buttonRect = button.getBoundingClientRect();
      setTriangleLeft(buttonRect.left - bandRect.left + (buttonRect.width / 2));
    };

    updateTriangle();
    const frame = window.requestAnimationFrame(updateTriangle);
    const resizeObserver = typeof ResizeObserver === 'undefined'
      ? null
      : new ResizeObserver(updateTriangle);
    resizeObserver?.observe(band);
    resizeObserver?.observe(track);
    resizeObserver?.observe(button);
    track.addEventListener('scroll', updateTriangle, { passive: true });
    window.addEventListener('resize', updateTriangle);

    return () => {
      window.cancelAnimationFrame(frame);
      resizeObserver?.disconnect();
      track.removeEventListener('scroll', updateTriangle);
      window.removeEventListener('resize', updateTriangle);
    };
  }, [activeOption?.status]);

  // Non-sales lead (new/enriching): show its Lead Intelligence status pill.
  // No "Start outreach" button here — ENRICHED owns that action now in the
  // sales status band below. Nav buttons still render for lead navigation.
  if (!activeOption) {
    const statusKey = getNormalizedStatusKey(leadStatus);
    const statusConfig = STATUS_MAP[statusKey];
    return (
      <div className="relative w-full">
        {/* Row 1 — status pill, same height as the sales status band so the
            layout is stable across lead states. */}
        <div className="w-full" style={{ backgroundColor: statusConfig.color }}>
          <div className="grid min-w-0 grid-cols-[1fr_auto] items-center">
            <div className="flex items-center gap-3 px-4 py-2 sm:px-6">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-black/15 text-[#080a0e]">
                <Icon icon="bx:radar" className="h-4 w-4" aria-hidden="true" />
              </span>
              <span className="min-w-0">
                <span className="block text-[9px] font-bold uppercase tracking-[0.16em] text-[#080a0e]/55">Current status</span>
                <span className="block truncate text-[11px] font-black uppercase tracking-[0.08em] text-[#080a0e]">{statusConfig.label || leadStatus}</span>
              </span>
            </div>
            {onClose && (
              <div className="flex h-full items-center pl-3 pr-5" style={{ backgroundColor: statusConfig.color }}>
                <button
                  type="button"
                  onClick={onClose}
                  aria-label="Close SDR workspace"
                  className="inline-flex h-9 items-center gap-1.5 rounded-md px-3 text-[12px] font-semibold text-white/80 transition-colors hover:bg-white/[0.1] hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
                >
                  <Icon icon="bx:x" className="h-5 w-5" aria-hidden="true" />
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
        {/* Row 2 — empty substatus-like band, keeps the nav buttons in the
            exact same grid position as when a sales status is active. */}
        <div className="grid min-w-0 grid-cols-[1fr_auto] items-center">
          <div className="px-3 py-1.5 sm:px-5" />
          {(onPreviousLead || onNextLead) && (
            <div className="flex h-full items-center bg-[#101319] pl-2 pr-3" role="group" aria-label="Lead navigation">
              <button
                type="button"
                onClick={onPreviousLead}
                disabled={!hasPreviousLead}
                aria-label="Previous lead"
                className="flex h-12 w-12 items-center justify-center rounded-md bg-transparent text-white/40 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/45 disabled:cursor-not-allowed disabled:opacity-20 disabled:hover:text-white/40"
              >
                <Icon icon="bx:chevron-left" className="h-8 w-8" aria-hidden="true" />
              </button>
              {typeof leadPosition === 'number' && totalLeads > 0 && (
                <span className="min-w-[2.75rem] text-center text-[11px] font-semibold tabular-nums text-white/45" aria-live="polite">
                  {leadPosition}<span className="mx-0.5 text-white/25">of</span>{totalLeads}
                </span>
              )}
              <button
                type="button"
                onClick={onNextLead}
                disabled={!hasNextLead}
                aria-label="Next lead"
                className="flex h-12 w-12 items-center justify-center rounded-md bg-transparent text-white/40 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/45 disabled:cursor-not-allowed disabled:opacity-20 disabled:hover:text-white/40"
              >
                <Icon icon="bx:chevron-right" className="h-8 w-8" aria-hidden="true" />
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  const stages = SALES_OPTIONS.filter((item) => item.status !== 'discarded');
  const isDiscarded = activeOption.status === 'discarded';
  const activeConfig = STATUS_MAP[activeOption.status];
  // Use the exact STATUS_MAP accent shown in the Leads list. Mixing it into
  // the dark workspace background made the same lifecycle stage look darker.
  const statusBandColor = activeConfig.color;
  const substatusBandColor = `color-mix(in srgb, ${activeConfig.color} 22%, #0a0b0e)`;

  return (
    <div className="w-full">
      {/* The selected status owns the entire upper band. The unboxed, edge-to-
          edge accent makes the hierarchy unmistakable without adding bulk. */}
      <div className="relative grid min-w-0 grid-cols-[1fr_auto] items-center">
          <div ref={statusBandRef} className="px-3 py-1.5 sm:px-5" style={{ backgroundColor: statusBandColor }}>
            {/* Only the current status is visually active. Previous and future
                statuses share the same neutral treatment but remain reversible. */}
            <div
              ref={statusTrackRef}
              role="radiogroup"
              aria-label="Sales stage"
              className="custom-scrollbar flex min-w-0 items-center gap-0.5 overflow-x-auto"
              onDragOver={(event) => { if (!disabled) event.preventDefault(); }}
            >
              {stages.map((stage, index) => {
                const isActive = stage.status === activeOption.status;
                return (
                  <React.Fragment key={stage.status}>
                    {index > 0 && (
                      <span aria-hidden="true" className="h-px w-1.5 shrink-0 bg-white/[0.08]" />
                    )}
                    <button
                      ref={isActive ? activeStatusRef : undefined}
                      type="button"
                      role="radio"
                      aria-checked={isActive}
                      draggable={!disabled}
                  onDragStart={(event) => event.dataTransfer.setData('text/plain', stage.status)}
                  onDrop={(event) => {
                    event.preventDefault();
                    if (disabled) return;
                    const droppedOn = stage.status;
                    onChange({
                      lead_status: droppedOn as ISRSalesStatus,
                      lead_substatus: SALES_OPTIONS.find((item) => item.status === droppedOn)?.substatuses[0]?.value || null,
                    } as ISRSalesState);
                  }}
                  disabled={disabled}
                  onClick={() => {
                    if (stage.status === activeOption.status) return;
                    onChange({
                      lead_status: stage.status as ISRSalesStatus,
                      lead_substatus: stage.substatuses[0]?.value || null,
                    } as ISRSalesState);
                  }}
                  title={`Move to ${stage.label}`}
                  className={`inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md px-2.5 text-[10px] font-bold uppercase tracking-[0.06em] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50 disabled:cursor-not-allowed disabled:opacity-50 ${
                    isActive
                      ? 'bg-white text-[#080a0e] shadow-[0_4px_12px_rgba(0,0,0,0.22)]'
                      : 'bg-transparent text-[#080a0e]/80 hover:bg-black/10 hover:text-[#080a0e]'
                  }`}
                >
                  <Icon icon={stage.icon} className="h-3 w-3 shrink-0" aria-hidden="true" />
                  {stage.label}
                </button>
              </React.Fragment>
            );
          })}
          <span aria-hidden="true" className="mx-0.5 h-4 w-px shrink-0 bg-white/[0.08]" />
          <button
            ref={isDiscarded ? activeStatusRef : undefined}
            type="button"
            aria-pressed={isDiscarded}
            disabled={disabled || isDiscarded}
            onClick={() => onChange({ lead_status: 'discarded' as ISRSalesStatus, lead_substatus: 'not_interested' } as ISRSalesState)}
            title="Discard this lead"
            className={`inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md px-2.5 text-[10px] font-bold uppercase tracking-[0.06em] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50 disabled:cursor-not-allowed ${
              isDiscarded
                ? 'bg-white text-[#080a0e] shadow-[0_4px_12px_rgba(0,0,0,0.22)]'
                : 'bg-transparent text-[#080a0e]/70 hover:bg-black/10 hover:text-[#080a0e]'
            }`}
          >
            <Icon icon="bx:archive-in" className="h-3 w-3" aria-hidden="true" />
            Discard
          </button>
          {/* AI outreach CTA — visible only on ENRICHED (substatus is always
              "Ready to Outreach", the single default). Active when the team
              carries mod_sales (triggers the N8N auto-outreach workflow);
              locked otherwise (opens the Sales Intelligence activation request
              modal). The locked variant mutates nothing, so it stays clickable
              even when sales editing is disabled. */}
          {activeOption.status === 'enriched' && state.lead_substatus === 'ready_to_outreach' && (
            <button
              type="button"
              disabled={hasSalesModule && disabled}
              onClick={hasSalesModule ? onStartOutreach : onRequestOutreach}
              title={hasSalesModule
                ? 'Start the auto-outreach workflow for this lead'
                : 'Contract Sales Intelligence to unlock AI outreach.'}
              className={hasSalesModule
                ? 'ml-auto inline-flex h-8 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg bg-[#080a0e] px-3 text-[11px] font-bold text-white transition-colors hover:bg-black/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50 disabled:opacity-45'
                : 'ml-auto inline-flex h-8 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg border border-[#5260FF]/45 bg-[linear-gradient(135deg,#050609,#11141c)] px-3 text-[11px] font-bold text-indigo-200 shadow-[0_5px_16px_rgba(5,6,10,0.38),inset_0_1px_0_rgba(255,255,255,0.08)] transition-all hover:border-[#6f7aff]/70 hover:shadow-[0_7px_22px_rgba(82,96,255,0.28),inset_0_1px_0_rgba(255,255,255,0.1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7c86ff]/60'}
            >
              <Icon icon={hasSalesModule ? 'bx:paper-plane' : 'bx:lock'} className="h-3.5 w-3.5" aria-hidden="true" />
              {hasSalesModule ? (
                'ACTIVATE AI OUTREACH'
              ) : (
                <span
                  className="isr-outreach-cta-shimmer bg-clip-text text-transparent"
                  style={{
                    backgroundImage: 'linear-gradient(110deg, #c7ccff 0%, #c7ccff 38%, #ffffff 50%, #c7ccff 62%, #c7ccff 100%)',
                    backgroundPosition: '100% 50%',
                    backgroundSize: '240% 100%',
                  }}
                >
                  UNLOCK OUTREACH
                </span>
              )}
            </button>
          )}
        </div>
        </div>
        {onClose && (
          <div className="flex h-full items-center pl-3 pr-5" style={{ backgroundColor: statusBandColor }}>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close SDR workspace"
              className="inline-flex h-9 items-center gap-1.5 rounded-md px-3 text-[12px] font-semibold text-white/80 transition-colors hover:bg-white/[0.1] hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
            >
              <Icon icon="bx:x" className="h-5 w-5" aria-hidden="true" />
              Close
            </button>
          </div>
        )}
        {triangleLeft !== null && (
          <span
            aria-hidden="true"
            className="absolute bottom-[-8px] z-10 h-0 w-0 -translate-x-1/2 border-x-[9px] border-t-[9px] border-x-transparent"
            style={{ left: triangleLeft, borderTopColor: statusBandColor }}
          />
        )}
      </div>

      {/* The lighter second band is visibly downstream from Status. The notch
          above replaces the redundant "Substatus" label. */}
      <div className="grid min-w-0 grid-cols-[1fr_auto] items-center" style={{ backgroundColor: substatusBandColor }}>
          <div className="flex min-w-0 items-center px-3 py-1.5 sm:px-5">
            <div role="radiogroup" aria-label={`${activeOption.label} substatus`} className="custom-scrollbar flex min-w-0 items-center gap-1 overflow-x-auto">
              {activeOption.substatuses.map((item) => {
                const selected = state.lead_substatus === item.value;
                if (item.transitional) {
                  return (
                    <span
                      key={item.value}
                      title={`${item.label} — system processing state, not selectable`}
                      className="inline-flex h-7 shrink-0 cursor-default items-center gap-1 rounded-md border border-dashed px-2 text-[10px] font-medium"
                      style={{ borderColor: selected ? `${activeConfig.color}80` : 'rgba(255,255,255,0.12)', color: selected ? '#FFFFFF' : 'rgba(255,255,255,0.6)', backgroundColor: selected ? `${activeConfig.color}32` : 'transparent' }}
                    >
                      {selected && <Icon icon="bx:loader-circle" className="h-2.5 w-2.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />}
                      {item.label}
                    </span>
                  );
                }
                return (
                  <button
                    key={item.value}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    disabled={disabled}
                    onClick={() => onChange({ ...state, lead_substatus: item.value } as ISRSalesState)}
                    title={item.label}
                    className="inline-flex h-7 shrink-0 items-center gap-1 rounded-md border px-2.5 text-[11px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/45 disabled:cursor-not-allowed disabled:opacity-50"
                    style={selected ? {
                      borderColor: `${activeConfig.color}80`,
                      backgroundColor: `${activeConfig.color}32`,
                      color: '#FFFFFF',
                    } : {
                      borderColor: 'transparent',
                      backgroundColor: 'transparent',
                      color: 'rgba(255,255,255,0.55)',
                    }}
                  >
                    {selected && <Icon icon="bx:check" className="h-2.5 w-2.5" style={{ color: activeConfig.color }} aria-hidden="true" />}
                    {item.label}
                  </button>
                );
              })}
            </div>
          </div>
          {/* Lead navigation — right column with the Activity column background.
              Fills the grid cell fully so the dark colour reaches the right edge. */}
          {(onPreviousLead || onNextLead) && (
            <div className="flex h-full items-center bg-[#101319] pl-2 pr-3" role="group" aria-label="Lead navigation">
              <button
                type="button"
                onClick={onPreviousLead}
                disabled={!hasPreviousLead}
                aria-label="Previous lead"
                className="flex h-12 w-12 items-center justify-center rounded-md bg-transparent text-white/40 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/45 disabled:cursor-not-allowed disabled:opacity-20 disabled:hover:text-white/40"
              >
                <Icon icon="bx:chevron-left" className="h-8 w-8" aria-hidden="true" />
              </button>
              {typeof leadPosition === 'number' && totalLeads > 0 && (
                <span className="min-w-[2.75rem] text-center text-[11px] font-semibold tabular-nums text-white/45" aria-live="polite">
                  {leadPosition}<span className="mx-0.5 text-white/25">of</span>{totalLeads}
                </span>
              )}
              <button
                type="button"
                onClick={onNextLead}
                disabled={!hasNextLead}
                aria-label="Next lead"
                className="flex h-12 w-12 items-center justify-center rounded-md bg-transparent text-white/40 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/45 disabled:cursor-not-allowed disabled:opacity-20 disabled:hover:text-white/40"
              >
                <Icon icon="bx:chevron-right" className="h-8 w-8" aria-hidden="true" />
              </button>
            </div>
          )}
      </div>
    </div>
  );
};

const SequencePanel: React.FC<{ lead: MappedLeadData }> = ({ lead }) => {
  const sequences = useMemo(
    () => ((lead.outreachSequences || []) as Array<OutreachStep & { channel: string }>).filter(hasOutreachMessageBody),
    [lead.outreachSequences],
  );
  const channels = useMemo(() => Array.from(new Set(sequences.map((step) => step.channel))), [sequences]);
  const nextUnsent = useMemo(() => sequences
    .filter((step) => !step.sentAt)
    .sort((a, b) => a.step - b.step)[0] || sequences[0], [sequences]);
  const [activeChannel, setActiveChannel] = useState(nextUnsent?.channel || channels[0] || '');
  const [activeStepByChannel, setActiveStepByChannel] = useState<Record<string, number>>({});
  const [copied, setCopied] = useState('');

  useEffect(() => {
    setActiveChannel(nextUnsent?.channel || channels[0] || '');
    setActiveStepByChannel({});
  }, [lead.id, nextUnsent?.channel, channels]);

  const selected = sequences
    .filter((step) => step.channel === activeChannel)
    .sort((a, b) => a.step - b.step);
  const requestedStep = activeStepByChannel[activeChannel];
  const activeStep = selected.some((step) => step.step === requestedStep)
    ? requestedStep
    : selected.find((step) => step === nextUnsent)?.step
      ?? selected.find((step) => !step.sentAt)?.step
      ?? selected[0]?.step;
  const activeChannelAccent = channelAccentColor[activeChannel] || '#5260FF';

  const handleCopy = async (key: string, value: string) => {
    if (!value) return;
    try {
      await copyToClipboard(value);
      setCopied(key);
      window.setTimeout(() => setCopied((current) => current === key ? '' : current), 1800);
    } catch {
      setCopied('Copy failed');
    }
  };

  if (!sequences.length) {
    return (
      <div className="flex min-h-[360px] flex-col items-center justify-center rounded-2xl border border-dashed border-white/10 p-8 text-center">
        <Icon icon="bx:message-square-x" className="h-9 w-9 text-white/22" aria-hidden="true" />
        <h3 className="mt-4 text-[13px] font-medium text-white/70">No outreach sequences</h3>
        <p className="mt-1 max-w-sm text-[12px] leading-5 text-white/40">Generate outreach messages before starting this lead's communication workflow.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex min-w-0 items-center gap-3 border-b border-white/[0.07] pb-2">
        <div role="tablist" aria-label="Sequence channels" className="flex min-w-0 flex-1 gap-1 overflow-x-auto [scrollbar-width:none]">
          {channels.map((channel) => {
            const meta = channelMeta[channel] || { label: channel.replace(/_/g, ' '), icon: 'bx:message-rounded', accent: 'text-white/65' };
            const pending = sequences.filter((step) => step.channel === channel && !step.sentAt).length;
            const active = channel === activeChannel;
            return (
              <button
                key={channel}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setActiveChannel(channel)}
                className={`inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg px-2.5 text-[12px] capitalize transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/40 ${active ? 'bg-white/[0.08] text-white' : 'text-white/45 hover:bg-white/[0.04] hover:text-white/80'}`}
              >
                <Icon icon={meta.icon} className={`h-4 w-4 ${active ? '' : 'opacity-60'}`} aria-hidden="true" />
                {meta.label}
                {pending > 0 && <span className="rounded-full bg-white/10 px-1.5 py-0.5 text-[10px] text-white/60">{pending}</span>}
              </button>
            );
          })}
        </div>
        <span className="shrink-0 text-[12px] text-white/40">
          {sequences.filter((step) => step.sentAt).length}/{sequences.length} sent
        </span>
      </div>

      {/* Channel verification — shows only the ACTIVE channel's verification
          with the key insight inline (no hover needed to see what matters). */}
      <SequenceChannelIntel lead={lead} activeChannel={activeChannel} />

      <div className="grid min-w-0 gap-3 md:grid-cols-[7.5rem_minmax(0,1fr)] md:items-start">
        <div
          role="tablist"
          aria-label={`${activeChannel || 'Message'} sequence steps`}
          className="custom-scrollbar flex min-w-0 gap-1.5 overflow-x-auto pb-1 md:flex-col md:overflow-visible md:border-r md:border-white/[0.07] md:pb-0 md:pr-3"
        >
          {selected.map((step) => {
            const selectedStep = step.step === activeStep;
            const isNext = step === nextUnsent;
            const tabId = `isr-sequence-step-${step.channel}-${step.step}`;
            return (
              <button
                key={tabId}
                id={tabId}
                type="button"
                role="tab"
                aria-selected={selectedStep}
                aria-controls={`${tabId}-panel`}
                onClick={() => setActiveStepByChannel((current) => ({ ...current, [activeChannel]: step.step }))}
                className={`flex h-10 min-w-[7rem] shrink-0 items-center justify-between gap-2 rounded-lg border px-2.5 text-left text-[11px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/45 md:w-full md:min-w-0 ${selectedStep ? 'text-white' : 'border-transparent bg-white/[0.025] text-white/45 hover:bg-white/[0.06] hover:text-white/80'}`}
                style={selectedStep ? {
                  borderColor: `${activeChannelAccent}70`,
                  backgroundColor: `${activeChannelAccent}24`,
                  boxShadow: `inset 3px 0 0 ${activeChannelAccent}`,
                } : undefined}
              >
                <span>Step {step.step}</span>
                {step.sentAt ? (
                  <span className="shrink-0 text-emerald-300/75" title="Sent">
                    <Icon icon="bx:check-circle" className="h-3.5 w-3.5" aria-hidden="true" />
                    <span className="sr-only">Sent</span>
                  </span>
                ) : isNext ? (
                  <span className="rounded bg-cyan-300/12 px-1 py-0.5 text-[8px] font-bold uppercase tracking-[0.08em] text-cyan-200">Next</span>
                ) : (
                  <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-white/20" aria-hidden="true" />
                )}
              </button>
            );
          })}
        </div>

        <div className="min-w-0">
        {selected.filter((step) => step.step === activeStep).map((step) => {
          const key = `${step.channel}-${step.step}`;
          const isNext = step === nextUnsent;
          const meta = channelMeta[step.channel] || { label: step.channel, icon: 'bx:message-rounded', accent: 'text-white/65' };
          const subjectText = step.subject ? messageHtmlToPlainText(step.subject) : '';
          const bodyText = messageHtmlToPlainText(step.body);
          const allText = subjectText ? `Subject: ${subjectText}\n\n${bodyText}` : bodyText;
          const openUrl = buildChannelUrl(step.channel, step, lead);
          const isWebUrl = /^https?:/i.test(openUrl);
          const tabId = `isr-sequence-step-${step.channel}-${step.step}`;
          return (
            <article key={key} id={`${tabId}-panel`} role="tabpanel" aria-labelledby={tabId} className={`overflow-hidden rounded-2xl border bg-[#0d0f13] ${isNext ? 'border-cyan-300/30' : 'border-white/[0.07]'}`}>
              <header className="flex flex-wrap items-center justify-between gap-2 border-b border-white/[0.06] px-4 py-3">
                <div className="flex items-center gap-2.5">
                  <span className={`flex h-8 w-8 items-center justify-center rounded-lg border border-white/[0.07] bg-white/[0.035] ${meta.accent}`}>
                    <Icon icon={meta.icon} className="h-4 w-4" aria-hidden="true" />
                  </span>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-[13px] font-medium text-white">Step {step.step}</h3>
                      {isNext && <span className="rounded-md bg-cyan-400/12 px-1.5 py-0.5 text-[10px] font-medium text-cyan-200">Next</span>}
                    </div>
                    <p className="mt-0.5 text-[11px] text-white/40">
                      {step.sentAt ? `Sent ${new Date(step.sentAt).toLocaleString()}` : 'Ready to send'}
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                  {step.subject && (
                    <button type="button" onClick={() => handleCopy(`${key}-subject`, subjectText)} className="h-8 rounded-lg border border-white/15 px-2.5 text-[12px] text-white/60 hover:border-white/35 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/40">
                      {copied === `${key}-subject` ? 'Copied' : 'Copy subject'}
                    </button>
                  )}
                  <button type="button" onClick={() => handleCopy(`${key}-body`, bodyText)} className="h-8 rounded-lg border border-white/15 px-2.5 text-[12px] text-white/60 hover:border-white/35 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/40">
                    {copied === `${key}-body` ? 'Copied' : step.channel === 'phone' ? 'Copy script' : 'Copy message'}
                  </button>
                  {step.subject && (
                    <button type="button" onClick={() => handleCopy(`${key}-all`, allText)} className="h-8 rounded-lg border border-white/15 px-2.5 text-[12px] text-white/60 hover:border-white/35 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/40">
                      {copied === `${key}-all` ? 'Copied' : 'Copy all'}
                    </button>
                  )}
                  {openUrl ? (
                    <a
                      href={openUrl}
                      target={isWebUrl ? '_blank' : undefined}
                      rel={isWebUrl ? 'noreferrer' : undefined}
                      className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-[#5260FF] px-3 text-[12px] font-medium text-white transition-colors hover:bg-[#6270FF] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8a93ff]"
                    >
                      <Icon icon="bx:link-external" className="h-3.5 w-3.5" aria-hidden="true" />
                      {step.channel === 'phone' ? 'Call' : `Open ${meta.label}`}
                    </a>
                  ) : (
                    <span title={`Add ${meta.label} contact information to open this channel`} className="inline-flex h-8 cursor-not-allowed items-center rounded-lg border border-white/[0.06] px-2.5 text-[12px] text-white/22">Add contact to open</span>
                  )}
                </div>
              </header>
              <div className="space-y-4 px-5 py-4">
                {step.subject && (
                  <div>
                    <p className="text-[11px] text-white/40">Subject</p>
                    <p className="mt-1 break-words text-[13px] leading-6 text-white/85">{subjectText}</p>
                  </div>
                )}
                <div>
                  <p className="text-[11px] text-white/40">{step.channel === 'phone' ? 'Call script' : 'Message'}</p>
                  <p className="mt-1 whitespace-pre-wrap break-words text-[13px] leading-6 text-white/70">{bodyText}</p>
                </div>
              </div>
            </article>
          );
        })}
        </div>
      </div>
      <p className="sr-only" aria-live="polite">{copied ? copied === 'Copy failed' ? copied : 'Copied to clipboard' : ''}</p>
    </div>
  );
};

type TimelineItem = {
  id: string;
  type: 'note' | 'status' | 'interaction';
  timestamp: string;
  title: string;
  body: string;
  icon: string;
  author?: string;
  /** HUMAN (ISR wrote a note), AI AGENT (outreach interaction), SYSTEM (status change). */
  actor: 'HUMAN' | 'AI AGENT' | 'SYSTEM';
  statusTone?: { color: string; background: string; border: string };
};

/** Pill badge config for each actor type — matches the ActivityTimeline atom. */
const ACTOR_BADGE: Record<TimelineItem['actor'], { icon: string; color: string; bg: string; border: string }> = {
  HUMAN: { icon: 'bx:user', color: 'text-cyan-200', bg: 'bg-cyan-400/10', border: 'border-cyan-300/20' },
  'AI AGENT': { icon: 'solar:magic-wand-3-bold-duotone', color: 'text-violet-200', bg: 'bg-violet-400/10', border: 'border-violet-300/20' },
  SYSTEM: { icon: 'bx:bot', color: 'text-white/50', bg: 'bg-white/[0.04]', border: 'border-white/10' },
};

const NOTE_ICON_OPTIONS = [
  { value: 'bx:notepad', label: 'Note' },
  { value: 'bx:phone-call', label: 'Call' },
  { value: 'bx:envelope', label: 'Email' },
  { value: 'bx:calendar', label: 'Meeting' },
  { value: 'bx:check-square', label: 'Task' },
  { value: 'bx:bulb', label: 'Insight' },
  { value: 'bx:magnet', label: 'Magnet' },
] as const;

/** Mirrors the note CTA and timeline rhythm so the activity rail never
 * collapses to an empty column while the full lead record is hydrating. */
const ActivityRailSkeleton: React.FC = () => (
  <div className="mt-4" role="status" aria-busy="true" aria-label="Loading activity">
    <SkeletonLine className="h-9 w-full rounded-lg" />
    <div className="mt-4 border-t border-white/[0.08] pt-4">
      {[0, 1, 2, 3].map((item) => (
        <div key={item} className="relative grid grid-cols-[26px_minmax(0,1fr)] gap-2.5 pb-4 last:pb-0">
          <span aria-hidden="true" className="absolute bottom-0 left-[12px] top-7 w-px bg-white/[0.06]" />
          <SkeletonLine className="relative z-10 h-6 w-6 rounded-md" />
          <div className="min-w-0 pt-0.5">
            <div className="flex items-center justify-between gap-2">
              <SkeletonLine className={`h-2.5 ${item % 2 === 0 ? 'w-20' : 'w-24'}`} />
              <SkeletonLine className="h-2 w-12" />
            </div>
            <SkeletonLine className="mt-2 h-2.5 w-full" />
            <SkeletonLine className={`mt-1.5 h-2.5 ${item === 1 ? 'w-2/3' : 'w-5/6'}`} />
          </div>
        </div>
      ))}
    </div>
    <span className="sr-only">Activity is loading.</span>
  </div>
);

/**
 * Persistent right rail: what already happened with this lead — every note
 * and interaction, newest first — plus the note capture box. ISR notes can
 * be edited or deleted in place; system interactions stay read-only.
 */
const ActivityRail: React.FC<{
  lead: MappedLeadData;
  notes: ISRNotesDocument;
  noteDraft: string;
  setNoteDraft: (value: string) => void;
  onAddNote: (icon: string) => boolean | void | Promise<boolean | void>;
  onUpdateNote: (noteId: string, body: string) => void;
  onDeleteNote: (noteId: string) => void;
  disabled: boolean;
  hydrating?: boolean;
  parseError?: string | null;
}> = ({ lead, notes, noteDraft, setNoteDraft, onAddNote, onUpdateNote, onDeleteNote, disabled, hydrating = false, parseError }) => {
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [composerOpen, setComposerOpen] = useState(false);
  const [noteIcon, setNoteIcon] = useState<string>(NOTE_ICON_OPTIONS[0].value);

  const timeline = useMemo<TimelineItem[]>(() => {
    const noteItems = notes.notes.map((note) => {
      const isStatusActivity = note.activity_type === 'status_change';
      const presentation = isStatusActivity ? statusActivityPresentation(note) : null;
      const statusConfig = presentation?.status ? STATUS_MAP[presentation.status] : null;
      return {
        id: note.id,
        type: isStatusActivity ? 'status' as const : 'note' as const,
        timestamp: note.created_at,
        title: presentation?.title || note.title || 'SDR note',
        body: presentation?.body || note.body,
        author: note.created_by_name || note.created_by || 'SDR',
        // Status changes are SYSTEM; manual notes are HUMAN.
        actor: isStatusActivity ? 'SYSTEM' as const : 'HUMAN' as const,
        icon: note.icon || 'bx:notepad',
        ...(statusConfig ? {
          statusTone: {
            color: statusConfig.color,
            background: statusConfig.bgColor,
            border: statusConfig.borderColor,
          },
        } : {}),
      };
    });
    const interactionItems = (lead.interactions || []).map((interaction: Interaction) => ({
      id: interaction.id,
      type: 'interaction' as const,
      timestamp: interaction.timestamp,
      title: interaction.meta?.subject || interaction.type.replace(/_/g, ' '),
      body: interaction.content,
      author: interaction.participantName,
      actor: 'AI AGENT' as const,
      icon: interaction.type === 'call' ? 'bx:phone-call' : interaction.type === 'email' ? 'bx:envelope' : 'bx:message-rounded-dots',
    }));
    return [...noteItems, ...interactionItems].sort((a, b) => {
      const aTime = Number.isNaN(Date.parse(a.timestamp)) ? 0 : Date.parse(a.timestamp);
      const bTime = Number.isNaN(Date.parse(b.timestamp)) ? 0 : Date.parse(b.timestamp);
      return bTime - aTime;
    });
  }, [lead.interactions, notes.notes]);

  const startEdit = (id: string, body: string) => {
    setEditingNoteId(id);
    setEditDraft(body);
    setConfirmDeleteId(null);
  };

  const commitEdit = () => {
    if (!editingNoteId) return;
    const trimmed = editDraft.trim();
    if (trimmed && trimmed !== notes.notes.find((n) => n.id === editingNoteId)?.body) {
      onUpdateNote(editingNoteId, trimmed);
    }
    setEditingNoteId(null);
    setEditDraft('');
  };

  // Stage 1 (sub-tabs): summary data is derived ONLY from the already-loaded
  // notes document — no lvl2/transformer wiring, so the load path is untouched.
  const [activityTab, setActivityTab] = useState<'activity' | 'summary'>('activity');
  const summaryNotes = useMemo(
    () => notes.notes.filter((n) => n.activity_type === 'interaction_summary'),
    [notes.notes],
  );
  const latestSummary = summaryNotes[summaryNotes.length - 1]?.body || '';

  return (
    <div className="px-3 pb-3 pt-3.5">

      {hydrating ? (
        <>
          <div className="flex items-baseline justify-between gap-2">
            <h2 className="text-[10px] font-bold uppercase tracking-[0.14em] text-white/50">Activity</h2>
            <SkeletonLine className="h-2.5 w-5" />
          </div>
          <ActivityRailSkeleton />
        </>
      ) : (
        <>
      {parseError && (
        <div role="alert" className="mt-3 rounded-lg border border-rose-400/20 bg-rose-500/10 p-2.5 text-[11px] leading-4 text-rose-100">
          Saving blocked — notes need recovery. {parseError}
        </div>
      )}

      {/* Stage 1: Add Note and activity timeline with sub-tabs */}
      {/* Add note stays compact until the ISR asks to write. */}
      <div className="border-b border-white/[0.08] pb-4">
        {!composerOpen ? (
          <button
            type="button"
            onClick={() => setComposerOpen(true)}
            disabled={disabled || Boolean(parseError)}
            aria-expanded={composerOpen}
            className="inline-flex h-9 w-full items-center justify-center gap-1.5 rounded-lg bg-white px-3 text-[12px] font-semibold text-[#0a0b0e] transition-colors hover:bg-white/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/60 disabled:cursor-not-allowed disabled:bg-white/10 disabled:text-white/35"
          >
            <Icon icon="bx:plus" className="h-4 w-4" aria-hidden="true" />
            Add Note
          </button>
        ) : (
          <div className="rounded-xl border border-white/10 bg-black/20 p-2.5">
            <label className="block">
              <span className="sr-only">Note text</span>
              <textarea
                value={noteDraft}
                onChange={(event) => setNoteDraft(event.target.value)}
                disabled={disabled || Boolean(parseError)}
                rows={3}
                autoFocus
                placeholder="What did you learn?"
                className="w-full resize-y rounded-lg border border-white/10 bg-black/25 px-2.5 py-2 text-[12px] leading-[18px] text-white outline-none placeholder:text-white/22 focus-visible:border-cyan-300/50 disabled:cursor-not-allowed disabled:opacity-45"
              />
            </label>
            <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
              <label className="relative flex min-w-0 flex-1 items-center gap-1.5 rounded-lg border border-white/10 bg-white/[0.04] px-2 text-white/60">
                <Icon icon={noteIcon} className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                <span className="sr-only">Activity icon</span>
                <select
                  value={noteIcon}
                  onChange={(event) => setNoteIcon(event.target.value)}
                  disabled={disabled}
                  className="h-8 min-w-0 flex-1 appearance-none bg-transparent pr-4 text-[11px] text-white/70 outline-none"
                >
                  {NOTE_ICON_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                </select>
                <Icon icon="bx:chevron-down" className="pointer-events-none absolute right-1.5 h-3.5 w-3.5" aria-hidden="true" />
              </label>
              <span className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => { setComposerOpen(false); setNoteDraft(''); setNoteIcon(NOTE_ICON_OPTIONS[0].value); }}
                  className="h-8 rounded-lg px-2 text-[11px] text-white/45 hover:bg-white/[0.05] hover:text-white/80"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    const added = await onAddNote(noteIcon);
                    if (added !== false) {
                      setComposerOpen(false);
                      setNoteIcon(NOTE_ICON_OPTIONS[0].value);
                    }
                  }}
                  disabled={disabled || Boolean(parseError) || !noteDraft.trim()}
                  className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-[#5260FF] px-2.5 text-[11px] font-medium text-white transition-colors hover:bg-[#6270FF] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#929aff] disabled:cursor-not-allowed disabled:bg-white/[0.06] disabled:text-white/30"
                >
                  <Icon icon="bx:plus" className="h-3.5 w-3.5" aria-hidden="true" />
                  Add
                </button>
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Sub-tab switcher — Activity | Summary (Stage 1: UI only, no data sync) */}
      <div className="mt-4 flex items-center gap-1 rounded-lg bg-white/[0.04] p-0.5" role="tablist" aria-label="Activity view">
        <button
          type="button"
          role="tab"
          aria-selected={activityTab === 'activity'}
          onClick={() => setActivityTab('activity')}
          className={`flex h-7 flex-1 items-center justify-center gap-1 rounded-md text-[11px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/40 ${
            activityTab === 'activity' ? 'bg-white/[0.08] text-white' : 'text-white/45 hover:text-white/80'
          }`}
        >
          <Icon icon="bx:time-five" className="h-3.5 w-3.5" aria-hidden="true" />
          Activity
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activityTab === 'summary'}
          onClick={() => setActivityTab('summary')}
          className={`flex h-7 flex-1 items-center justify-center gap-1 rounded-md text-[11px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/40 ${
            activityTab === 'summary' ? 'bg-white/[0.08] text-white' : 'text-white/45 hover:text-white/80'
          }`}
        >
          <Icon icon="bx:conversation" className="h-3.5 w-3.5" aria-hidden="true" />
          Final Summary
        </button>
      </div>

      {activityTab === 'summary' ? (
        <div className="mt-3">
          {latestSummary ? (
            <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-3.5">
              <div className="mb-2 flex items-center gap-1.5">
                <span className="flex h-5 w-5 items-center justify-center rounded-md bg-[#5260FF]/15 text-[#8a93ff]">
                  <Icon icon="bx:conversation" className="h-3 w-3" aria-hidden="true" />
                </span>
                <span className="text-[11px] font-semibold text-white/80">Interaction Summary</span>
              </div>
              <p className="whitespace-pre-wrap break-words text-[12px] leading-5 text-white/70">{latestSummary}</p>
            </div>
          ) : (
            <p className="rounded-lg border border-dashed border-white/10 px-3 py-6 text-center text-[11px] text-white/35">
              No summary yet. Write your reflection after interacting with this lead.
            </p>
          )}
        </div>
      ) : (
        <>
      {/* Activity header (Activity tab) */}
      <div className="mt-3 flex items-baseline justify-between gap-2">
        <h2 className="text-[10px] font-bold uppercase tracking-[0.14em] text-white/50">Activity</h2>
          <span className="text-[11px] text-white/35">{timeline.length}</span>
      </div>

      {/* Timeline — notes + interactions, newest first. */}
      <div className="mt-3 space-y-0">
        {timeline.map((item) => (
          <article key={`${item.type}-${item.id}`} className="group/note relative grid grid-cols-[26px_minmax(0,1fr)] gap-2.5 pb-4 last:pb-0">
            <span aria-hidden="true" className="absolute bottom-0 left-[12px] top-7 w-px bg-white/[0.08]" />
            <span
              className={`relative z-10 flex h-6 w-6 items-center justify-center rounded-md border ${item.type === 'status' && item.statusTone ? '' : item.type === 'status' ? 'border-violet-300/20 bg-violet-400/10 text-violet-200' : item.type === 'note' ? 'border-cyan-300/20 bg-cyan-400/10 text-cyan-200' : 'border-white/10 bg-[#11151d] text-white/45'}`}
              style={item.type === 'status' && item.statusTone ? {
                color: item.statusTone.color,
                backgroundColor: item.statusTone.background,
                borderColor: item.statusTone.border,
              } : undefined}
            >
              <Icon icon={item.icon} className="h-3.5 w-3.5" aria-hidden="true" />
            </span>
            <div className="min-w-0">
              <div className="flex flex-wrap items-baseline justify-between gap-x-2 gap-y-0.5">
                <h3 className="text-[12px] font-semibold text-white/80">
                  {item.title}
                </h3>
                <time className="text-[10px] tabular-nums text-white/35" dateTime={item.timestamp}>
                  {item.timestamp ? new Date(item.timestamp).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                </time>
              </div>
              {/* Actor pill — matches the ActivityTimeline atom. */}
              <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
                {item.author && <span className="text-[10px] text-white/35">{item.author}</span>}
                <span className={`inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[8px] font-black uppercase tracking-[0.1em] ${ACTOR_BADGE[item.actor].bg} ${ACTOR_BADGE[item.actor].border} ${ACTOR_BADGE[item.actor].color}`}>
                  <Icon icon={ACTOR_BADGE[item.actor].icon} className="h-2.5 w-2.5" aria-hidden="true" />
                  {item.actor}
                </span>
              </div>

              {item.type === 'note' && editingNoteId === item.id ? (
                <div className="mt-1.5">
                  <textarea
                    value={editDraft}
                    onChange={(event) => setEditDraft(event.target.value)}
                    rows={Math.min(8, Math.max(2, editDraft.split('\n').length + 1))}
                    disabled={disabled}
                    aria-label="Edit note"
                    className="w-full resize-y rounded-lg border border-cyan-300/35 bg-black/30 px-2.5 py-2 text-[12px] leading-5 text-white outline-none focus-visible:border-cyan-300/60"
                  />
                  <div className="mt-1.5 flex items-center justify-end gap-1.5">
                    <button
                      type="button"
                      onClick={() => { setEditingNoteId(null); setEditDraft(''); }}
                      className="rounded-md px-2 py-1 text-[11px] text-white/45 hover:bg-white/[0.05] hover:text-white/80"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={commitEdit}
                      disabled={disabled || !editDraft.trim()}
                      className="rounded-md bg-[#5260FF] px-2.5 py-1 text-[11px] font-medium text-white hover:bg-[#6270FF] disabled:opacity-40"
                    >
                      Save note
                    </button>
                  </div>
                </div>
              ) : item.type === 'note' && confirmDeleteId === item.id ? (
                <div className="mt-1.5 flex items-center justify-between gap-2 rounded-lg border border-rose-400/20 bg-rose-500/10 px-2.5 py-1.5">
                  <span className="text-[11px] text-rose-100/80">Delete this note?</span>
                  <span className="flex gap-1.5">
                    <button type="button" onClick={() => setConfirmDeleteId(null)} className="rounded-md px-2 py-0.5 text-[11px] text-white/50 hover:text-white">No</button>
                    <button type="button" onClick={() => { onDeleteNote(item.id); setConfirmDeleteId(null); }} className="rounded-md bg-rose-500/80 px-2 py-0.5 text-[11px] font-medium text-white hover:bg-rose-500">Delete</button>
                  </span>
                </div>
              ) : (
                <>
                  <p className="mt-1 whitespace-pre-wrap break-words text-[12px] leading-5 text-white/65">{item.body}</p>
                  {item.type === 'note' && !disabled && !parseError && (
                    <span className="mt-1 flex items-center gap-2 opacity-0 transition-opacity group-hover/note:opacity-100 focus-within:opacity-100">
                      <button
                        type="button"
                        onClick={() => startEdit(item.id, item.body)}
                        className="inline-flex items-center gap-1 text-[10px] text-white/35 hover:text-cyan-200"
                        aria-label="Edit note"
                      >
                        <Icon icon="bx:edit-alt" className="h-3 w-3" aria-hidden="true" />
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmDeleteId(item.id)}
                        className="inline-flex items-center gap-1 text-[10px] text-white/35 hover:text-rose-300"
                        aria-label="Delete note"
                      >
                        <Icon icon="bx:trash" className="h-3 w-3" aria-hidden="true" />
                        Delete
                      </button>
                    </span>
                  )}
                </>
              )}
            </div>
          </article>
        ))}
        {!timeline.length && (
          <p className="rounded-lg border border-dashed border-white/10 px-4 py-6 text-center text-[12px] text-white/35">
            No notes or activity yet — this is the first contact.
          </p>
        )}
      </div>
        </>
      )}
      </>
      )}
    </div>
  );
};

/**
 * Wrapper that builds the briefing view model once and renders only the
 * campaign section of it — keeps CampaignContextTab in ISRBriefingTab.tsx
 * (single source of truth for campaign context resolution).
 */
const CampaignContextTabForLead: React.FC<{ lead: MappedLeadData; campaign?: CampaignMetadata }> = ({ lead, campaign }) => {
  const briefing = useMemo(() => buildBriefingPublic(lead, campaign), [lead, campaign]);
  return <CampaignContextTab briefing={briefing} />;
};

/** Flat key/value row group — the shared building block of the flat tabs. */
const FlatRows: React.FC<{ rows: Array<[string, React.ReactNode]> }> = ({ rows }) => {
  const present = rows.filter(([label, value]) => value !== null && value !== undefined && value !== '');
  if (!present.length) {
    return <p className="rounded-xl border border-dashed border-white/10 px-5 py-8 text-center text-[13px] text-white/40">No data available yet.</p>;
  }
  return (
    <dl className="divide-y divide-white/[0.06]">
      {present.map(([label, value]) => (
        <div key={label} className="grid grid-cols-[150px_minmax(0,1fr)] gap-4 py-3">
          <dt className="text-[12px] uppercase tracking-[0.1em] text-white/40">{label}</dt>
          <dd className="min-w-0 break-words text-[13px] leading-6 text-white/80">{value}</dd>
        </div>
      ))}
    </dl>
  );
};

/** Framework meaning dictionaries — power the hover tooltips. */
const MBTI_LETTER_MEANINGS: Record<string, { term: string; description: string }> = {
  I: { term: 'Introversion', description: 'Prefers reflection, written context, and quieter focus.' },
  E: { term: 'Extraversion', description: 'Prefers discussion, quick interaction, and external energy.' },
  S: { term: 'Sensing', description: 'Trusts facts, practical details, and what is already proven.' },
  N: { term: 'Intuition', description: 'Looks for patterns, possibilities, and future implications.' },
  T: { term: 'Thinking', description: 'Prioritizes logic, evidence, and objective tradeoffs.' },
  F: { term: 'Feeling', description: 'Prioritizes values, people impact, and relationship harmony.' },
  J: { term: 'Judging', description: 'Likes structure, decisions, timelines, and clear next steps.' },
  P: { term: 'Perceiving', description: 'Likes flexibility, options, exploration, and room to adapt.' },
};

const DISC_LETTER_MEANINGS: Record<string, string> = {
  D: 'Dominance — direct, results-driven, wants control and fast decisions.',
  I: 'Influence — outgoing, persuasive, motivated by recognition and connection.',
  S: 'Steadiness — patient, cooperative, values stability and sincere trust.',
  C: 'Conscientiousness — analytical, precise, wants accuracy and proof.',
};

const ENNEAGRAM_TYPE_MEANINGS: Record<string, { name: string; description: string }> = {
  '1': { name: 'Reformer', description: 'Principled, purposeful, self-controlled — perfectionistic.' },
  '2': { name: 'Helper', description: 'Generous, demonstrative, people-pleasing — wants to be needed.' },
  '3': { name: 'Achiever', description: 'Adaptable, excelling, driven — wants success and validation.' },
  '4': { name: 'Individualist', description: 'Expressive, dramatic, self-absorbed — wants to be unique.' },
  '5': { name: 'Investigator', description: 'Perceptive, innovative, secretive — wants competence.' },
  '6': { name: 'Loyalist', description: 'Engaging, responsible, anxious — wants security and support.' },
  '7': { name: 'Enthusiast', description: 'Spontaneous, versatile, distractible — wants stimulation.' },
  '8': { name: 'Challenger', description: 'Self-confident, decisive, willful — wants control.' },
  '9': { name: 'Peacemaker', description: 'Receptive, reassuring, complacent — wants harmony.' },
};

const ENNEAGRAM_WING_MEANINGS: Record<string, string> = {
  w9: 'Leans calm and accommodating — softens the core type.',
  w1: 'Leans principled and precise — adds discipline.',
  w2: 'Leans warm and helpful — adds people-focus.',
  w3: 'Leans ambitious and image-aware — adds drive.',
  w4: 'Leans creative and introspective — adds depth.',
  w5: 'Leans analytical and private — adds detachment.',
  w6: 'Leans loyal and cautious — adds vigilance.',
  w7: 'Leans upbeat and spontaneous — adds energy.',
  w8: 'Leans assertive and bold — adds force.',
};

/** Parse "ISTJ" → ['I','S','T','J']; "1w9" → type '1', wing 'w9'. */
const parseMbtiLetters = (type?: string | null): string[] =>
  (type || '').toUpperCase().replace(/[^A-Z]/g, '').slice(0, 4).split('').filter(Boolean);

/**
 * Hover tooltip for framework values — plain title-attribute-free custom
 * bubble so the text is readable and styled. Shows on hover/focus.
 */
const InfoHover: React.FC<{ label: React.ReactNode; tooltip: React.ReactNode }> = ({ label, tooltip }) => (
  <span className="group/info relative inline-flex cursor-help items-center gap-1.5 underline decoration-white/20 decoration-dotted underline-offset-4" tabIndex={0}>
    {label}
    <Icon icon="bx:help-circle" className="h-3.5 w-3.5 text-white/30 transition-colors group-hover/info:text-cyan-300/80 group-focus/info:text-cyan-300/80" aria-hidden="true" />
    <span
      role="tooltip"
      className="pointer-events-none absolute bottom-full left-0 z-50 mb-2 hidden w-max max-w-[280px] rounded-lg border border-white/12 bg-[#11131a] px-3 py-2 text-left text-[12px] font-normal leading-5 text-white/80 shadow-xl group-hover/info:block group-focus/info:block"
    >
      {tooltip}
    </span>
  </span>
);

/** MBTI value with per-letter hover chips explaining each preference. */
const MbtiValue: React.FC<{ type: string }> = ({ type }) => {
  const letters = parseMbtiLetters(type);
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      {letters.map((letter) => {
        const meaning = MBTI_LETTER_MEANINGS[letter];
        return (
          <InfoHover
            key={letter}
            label={<span className="font-semibold tracking-wide text-white">{letter}</span>}
            tooltip={meaning ? <><strong className="text-white">{meaning.term}.</strong> {meaning.description}</> : letter}
          />
        );
      })}
    </span>
  );
};

/** DISC value — each letter of the pattern gets its own hover meaning. */
const DiscValue: React.FC<{ pattern: string }> = ({ pattern }) => {
  const letters = (pattern.toUpperCase().match(/[DISC]/g) || []).filter((letter, index, all) => all.indexOf(letter) === index);
  if (!letters.length) return <span className="capitalize">{pattern}</span>;
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      {letters.map((letter) => (
        <InfoHover
          key={letter}
          label={<span className="font-semibold text-white">{letter}</span>}
          tooltip={DISC_LETTER_MEANINGS[letter] || letter}
        />
      ))}
    </span>
  );
};

/** Enneagram value — "1w9" shows the core type and the wing meaning. */
const EnneagramValue: React.FC<{ type: string }> = ({ type }) => {
  const raw = (type || '').trim().toLowerCase();
  const coreMatch = raw.match(/^(\d)/);
  const wingMatch = raw.match(/(w\d)/);
  const core = coreMatch ? ENNEAGRAM_TYPE_MEANINGS[coreMatch[1]] : null;
  const wing = wingMatch ? ENNEAGRAM_WING_MEANINGS[wingMatch[1]] : null;

  return (
    <InfoHover
      label={<span className="font-semibold text-white">{type}</span>}
      tooltip={
        <>
          {core ? <><strong className="text-white">Type {coreMatch![1]} — {core.name}.</strong> {core.description}</> : `Enneagram ${type}`}
          {wing && <><br />{wing}</>}
        </>
      }
    />
  );
};

/**
 * Psychology tab — the full call-relevant psychological profile in flat
 * sections: generation & age range, buyer archetype, influence levers,
 * personality frameworks, cognitive biases, and communication norms.
 */
const ISRPsychologyTab: React.FC<{ lead: MappedLeadData }> = ({ lead }) => {
  const intel = (lead.rawProfileIntel || {}) as Record<string, any>;
  const psych = intel.psychological_profile || {};
  const buyer = psych.buyer_archetype || intel.buyer_archetype || {};
  const cultural = intel.cultural_intelligence || {};
  const disc = intel.disc_profile || psych.disc_profile || {};
  const generation = intel.generational_profile || {};
  const influence = intel.influence_receptivity || {};
  const learning = intel.learning_style || {};
  const biases = intel.cognitive_bias_profile || psych.cognitive_bias_profile || {};
  const genCharacteristics = generation.generational_characteristics || {};
  const genValues = (genCharacteristics.work_values || []) as string[];
  const bigFive = psych.big_five_profile || {};
  const behavioralPredictions = Object.entries(bigFive.behavioral_predictions || {}).flatMap(([key, value]) => {
    const displayValue = firstPresentText(value);
    return displayValue ? [{
      key,
      label: key.replace(/[_-]+/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase()),
      value: displayValue.replace(/[_-]+/g, ' '),
    }] : [];
  });
  const behavioralSummary = firstPresentText(
    psych.psychological_synthesis?.behavioral_prediction_summary,
    psych.behavioral_prediction_summary,
  );
  const hasBehavioralPredictions = Boolean(behavioralSummary || behavioralPredictions.length);

  const cialdini = [
    { label: 'Authority', value: influence?.authority },
    { label: 'Social proof', value: influence?.social_proof },
    { label: 'Reciprocity', value: influence?.reciprocity },
    { label: 'Liking', value: influence?.liking },
    { label: 'Scarcity', value: influence?.scarcity },
    { label: 'Commitment', value: influence?.commitment_consistency },
  ].filter((lever) => lever.value !== undefined && lever.value !== null);
  const objections = (biases.predicted_objections || []) as any[];
  const hasPsychologyRail = hasBehavioralPredictions || objections.length > 0;

  return (
    <div className="@container pb-8">
      <div className={hasPsychologyRail ? '@min-[680px]:grid @min-[680px]:grid-cols-[minmax(0,1.45fr)_minmax(15rem,1fr)] @min-[680px]:gap-6' : ''}>
      <div className="min-w-0 space-y-8">
      {/* Generation — age range and cohort behaviour. Vital context for tone,
          references, and channel expectations. */}
      {(generation.generation || generation.estimated_age_range) && (
        <section>
          <h2 className="text-[15px] font-medium text-white">Generation</h2>
          <p className="mt-0.5 text-[11px] text-white/35">Age range and cohort behaviour</p>
          <div className="mt-3">
            <FlatRows
              rows={[
                ['Generation', generation.generation ? (
                  <span className="font-semibold text-white">
                    {String(generation.generation).replace(/_/g, ' ')}
                    {generation.estimated_age_range ? <span className="ml-2 font-normal text-white/50">~{generation.estimated_age_range} years</span> : null}
                  </span>
                ) : null],
                ['Tech adoption', genCharacteristics.tech_adoption_style ? <span className="capitalize">{String(genCharacteristics.tech_adoption_style).replace(/_/g, ' ')}</span> : null],
                ['Work values', genValues.length ? <span>{genValues.join(' · ')}</span> : null],
                ['Outreach strategy', generation.generational_outreach_strategy ? <span>{String(generation.generational_outreach_strategy)}</span> : null],
              ]}
            />
          </div>
        </section>
      )}

      {/* Buyer DNA — what drives the purchase and what proof closes it. */}
      {(buyer.archetype || buyer.type || buyer.buying_motivation) && (
        <section>
          <h2 className="text-[15px] font-medium text-white">Buyer profile</h2>
          <p className="mt-0.5 text-[11px] text-white/35">What drives the purchase and what proof closes it</p>
          <div className="mt-3">
            <FlatRows
              rows={[
                ['Archetype', (buyer.archetype || buyer.type) ? (
                  <>
                    <span className="text-white capitalize">{String(buyer.archetype || buyer.type).replace(/_/g, ' ')}</span>
                    {buyer.business_focus ? <span className="text-white/50"> — {String(buyer.business_focus).replace(/_/g, ' ')}</span> : null}
                  </>
                ) : null],
                ['Buying motivation', buyer.buying_motivation ? <span className="capitalize">{String(buyer.buying_motivation).replace(/_/g, ' ')}</span> : null],
                ['Proof needed', buyer.proof_required ? <span className="capitalize">{String(buyer.proof_required).replace(/_/g, ' ')}</span> : null],
                ['Adoption curve', buyer.innovation_adoption_curve_position ? <span className="text-cyan-200/90">{String(buyer.innovation_adoption_curve_position)}</span> : null],
                ['Risk approach', buyer.risk_tolerance ? <span className="capitalize">{String(buyer.risk_tolerance).replace(/_/g, ' ')}</span> : null],
                ['Decision style', psych.decision_making_style || buyer.decision_style ? <span className="capitalize">{String(psych.decision_making_style || buyer.decision_style).replace(/_/g, ' ')}</span> : null],
              ]}
            />
          </div>
        </section>
      )}

      {/* Personality frameworks — condensed to their headline read. */}
      {(psych.mbti_type || disc.disc_pattern || disc.primary_style || psych.enneagram_type) && (
        <section>
          <h2 className="text-[15px] font-medium text-white">Personality</h2>
          <p className="mt-0.5 text-[11px] text-white/35">How they think, communicate, and react</p>
          <div className="mt-3">
            <FlatRows
              rows={[
                ['MBTI', psych.mbti_type ? <MbtiValue type={String(psych.mbti_type)} /> : null],
                ['DISC', (disc.disc_pattern || disc.primary_style) ? (
                  <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
                    {disc.disc_pattern && <DiscValue pattern={String(disc.disc_pattern)} />}
                    {disc.primary_style && (
                      <span className="capitalize text-white/50">· {String(disc.primary_style).replace(/_/g, ' ')}</span>
                    )}
                  </span>
                ) : null],
                ['Enneagram', psych.enneagram_type ? <EnneagramValue type={String(psych.enneagram_type)} /> : null],
                ['Conflict style', disc.conflict_style ? <span className="capitalize">{String(disc.conflict_style).replace(/_/g, ' ')}</span> : null],
                ['Under pressure', disc.stress_behavior || disc.risk_approach ? <span className="capitalize">{String(disc.stress_behavior || disc.risk_approach).replace(/_/g, ' ')}</span> : null],
              ]}
            />
          </div>
        </section>
      )}

      {/* Influence levers — what actually moves this person. */}
      {(cialdini.length > 0 || influence.primary_influence_lever) && (
        <section>
          <h2 className="text-[15px] font-medium text-white">Influence levers</h2>
          <p className="mt-0.5 text-[11px] text-white/35">What moves this person — ranked</p>
          <div className="mt-3">
            {influence.primary_influence_lever && (
              <p className="mb-3 text-[13px] leading-6 text-white/80">
                <span className="text-cyan-200/90">{String(influence.primary_influence_lever)}</span>
                {influence.influence_strategy ? <span className="text-white/55"> — {String(influence.influence_strategy)}</span> : null}
              </p>
            )}
            <dl className="divide-y divide-white/[0.06]">
              {cialdini
                .slice()
                .sort((a, b) => parseInt(String(b.value), 10) - parseInt(String(a.value), 10))
                .map((lever) => {
                  const val = parseInt(String(lever.value), 10) || 0;
                  return (
                    <div key={lever.label} className="grid grid-cols-[110px_minmax(0,1fr)_2.5rem] items-center gap-4 py-2">
                      <dt className="text-[12px] uppercase tracking-[0.1em] text-white/40">{lever.label}</dt>
                      <dd className="h-1.5 overflow-hidden rounded-full bg-white/[0.07]">
                        <span
                          className={`block h-full rounded-full ${val > 70 ? 'bg-[#5260FF]' : val > 40 ? 'bg-cyan-400/70' : 'bg-white/20'}`}
                          style={{ width: `${Math.min(100, Math.max(2, val))}%` }}
                        />
                      </dd>
                      <dd className="text-right text-[12px] tabular-nums text-white/60">{val}%</dd>
                    </div>
                  );
                })}
            </dl>
            {Array.isArray(influence.resistance_points) && influence.resistance_points.length > 0 && (
              <p className="mt-3 text-[12px] leading-5 text-white/45">
                <span className="text-rose-200/80">Resistance:</span> {influence.resistance_points.join(', ')}
              </p>
            )}
          </div>
        </section>
      )}

      {/* Learning style — how to present information so it lands. */}
      {(learning.primary_style || learning.presentation_strategy) && (
        <section>
          <h2 className="text-[15px] font-medium text-white">How they absorb information</h2>
          <p className="mt-0.5 text-[11px] text-white/35">Match the format or lose the message</p>
          <div className="mt-3">
            <FlatRows
              rows={[
                ['Learning style', learning.primary_style ? <span className="capitalize">{String(learning.primary_style).replace(/_/g, ' ')}</span> : null],
                ['Format preference', learning.content_consumption_preference?.format_preference ? <span className="capitalize">{String(learning.content_consumption_preference.format_preference).replace(/_/g, ' ')}</span> : null],
                ['Presentation', learning.presentation_strategy ? <span>{String(learning.presentation_strategy)}</span> : null],
              ]}
            />
          </div>
        </section>
      )}

      {/* Communication norms — tone guardrails for the live conversation. */}
      {(cultural.formality_level || cultural.small_talk_appetite || intel.communication_preferences?.formality_level) && (
        <section>
          <h2 className="text-[15px] font-medium text-white">Communication norms</h2>
          <p className="mt-0.5 text-[11px] text-white/35">Tone guardrails for this conversation</p>
          <div className="mt-3">
            <FlatRows
              rows={[
                ['Formality', cultural.formality_level || intel.communication_preferences?.formality_level ? (
                  <span className="capitalize">{String(cultural.formality_level || intel.communication_preferences?.formality_level).replace(/_/g, ' ')}</span>
                ) : null],
                ['Small talk', cultural.small_talk_appetite ? <span className="capitalize">{String(cultural.small_talk_appetite).replace(/_/g, ' ')}</span> : null],
                ['Culture', cultural.primary_culture ? <span>{String(cultural.primary_culture)}</span> : null],
                ['Relationship pace', cultural.cultural_communication_norms?.relationship_building?.importance ? (
                  <span className="capitalize">{String(cultural.cultural_communication_norms.relationship_building.importance)} importance</span>
                ) : null],
              ]}
            />
          </div>
        </section>
      )}
      </div>

      {hasPsychologyRail && (
        <aside className="mt-8 min-w-0 border-t border-white/[0.08] pt-6 @min-[680px]:mt-0 @min-[680px]:border-l @min-[680px]:border-t-0 @min-[680px]:pl-6 @min-[680px]:pt-0" aria-label="Person psychology insights">
          {hasBehavioralPredictions && (
          <section aria-labelledby="isr-behavioral-predictions-title">
          <div className="flex items-start gap-3">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-cyan-300/10 text-cyan-200">
              <Icon icon="bx:brain" className="h-4 w-4" aria-hidden="true" />
            </span>
            <div className="min-w-0">
              <h2 id="isr-behavioral-predictions-title" className="text-[15px] font-medium text-white">Behavioral Predictions</h2>
              <p className="mt-0.5 text-[11px] leading-4 text-white/35">Likely person-level behaviors inferred from the enriched profile</p>
            </div>
          </div>

          {behavioralSummary && (
            <div className="mt-4 border-l-2 border-cyan-300/45 pl-3">
              <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-cyan-200/65">Expected response</p>
              <p className="mt-1.5 break-words text-[13px] leading-5 text-white/75">{behavioralSummary}</p>
            </div>
          )}

          {behavioralPredictions.length > 0 && (
            <dl className="mt-5 divide-y divide-white/[0.06] border-y border-white/[0.06]">
              {behavioralPredictions.map((prediction) => (
                <div key={prediction.key} className="flex min-w-0 items-baseline justify-between gap-4 py-2.5">
                  <dt className="min-w-0 break-words text-[10px] font-medium uppercase tracking-[0.09em] text-white/40">{prediction.label}</dt>
                  <dd className="shrink-0 text-right text-[12px] font-medium capitalize text-cyan-100/80">{prediction.value}</dd>
                </div>
              ))}
            </dl>
          )}

          {bigFive.ocean_confidence !== null && bigFive.ocean_confidence !== undefined && (
            <p className="mt-3 text-[10px] text-white/30">Profile confidence · {String(bigFive.ocean_confidence)}%</p>
          )}
          </section>
          )}

          {objections.length > 0 && (
            <section className={hasBehavioralPredictions ? 'mt-7 border-t border-white/[0.08] pt-6' : ''} aria-labelledby="isr-likely-objections-title">
              <div className="flex items-start gap-3">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-amber-300/10 text-amber-200">
                  <Icon icon="bx:error-circle" className="h-4 w-4" aria-hidden="true" />
                </span>
                <div className="min-w-0">
                  <h2 id="isr-likely-objections-title" className="text-[15px] font-medium text-white">Likely Objections</h2>
                  <p className="mt-0.5 text-[11px] leading-4 text-white/35">Anticipated resistance and a prepared response</p>
                </div>
              </div>

              <div className="mt-4 divide-y divide-white/[0.06] border-y border-white/[0.06]">
                {objections.map((objection, index) => (
                  <article key={`${objection.bias_source || 'objection'}-${index}`} className="py-3.5">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <h3 className="text-[10px] font-semibold uppercase tracking-[0.1em] text-amber-100/65">
                        {String(objection.bias_source || 'Likely resistance').replace(/_/g, ' ')}
                      </h3>
                      {objection.probability && (
                        <span className="rounded-md bg-amber-300/[0.08] px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-[0.08em] text-amber-100/70">
                          {String(objection.probability)} likelihood
                        </span>
                      )}
                    </div>
                    {objection.objection && (
                      <p className="mt-1.5 break-words text-[13px] leading-5 text-white/75">{String(objection.objection)}</p>
                    )}
                    {objection.counter_strategy && (
                      <p className="mt-2 break-words text-[12px] leading-5 text-emerald-100/65">
                        <Icon icon="bx:shield" className="mr-1.5 inline h-3.5 w-3.5 align-[-2px]" aria-hidden="true" />
                        {String(objection.counter_strategy)}
                      </p>
                    )}
                    {objection.reframing_approach && (
                      <p className="mt-1.5 break-words text-[11px] leading-4.5 text-white/40">
                        <span className="font-medium text-white/55">Reframe:</span> {String(objection.reframing_approach)}
                      </p>
                    )}
                  </article>
                ))}
              </div>
            </section>
          )}
        </aside>
      )}
      </div>
    </div>
  );
};

/** People row — name + role + company with initials tile. */
const PersonRow: React.FC<{ name: string; detail?: string | null }> = ({ name, detail }) => (
  <div className="flex min-w-0 items-center gap-3">
    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#5260FF]/12 text-[11px] font-bold text-[#8a93ff]">
      {getInitials(name)}
    </span>
    <span className="min-w-0">
      <span className="block truncate text-[13px] text-white/85">{name}</span>
      {detail && <span className="block truncate text-[11px] text-white/40">{detail}</span>}
    </span>
  </div>
);

/**
 * Team tab — who surrounds this lead: buying committee, colleagues, and
 * known connections. Flat list, one row per person, no stat cards.
 */
const ISRTeamTab: React.FC<{ lead: MappedLeadData }> = ({ lead }) => {
  const network = (lead.rawProfileIntel as Record<string, any> | undefined)?.network_intelligence || {};
  const internal = network.internal_network || {};
  const external = network.external_network || {};
  const committee = network.relationship_mapping?.buying_committee_estimate || {};
  const summary = internal.network_summary || {};

  // N8N connection entries arrive as objects `{name,title,company}` OR plain
  // strings (e.g. "Forbes Spain CIO Network") — normalize both to a label.
  const connectionLabel = (conn: unknown): string => {
    if (typeof conn === 'string') return conn;
    if (conn && typeof conn === 'object') {
      const c = conn as Record<string, unknown>;
      return firstPresentText(c.name, c.title, c.company);
    }
    return '';
  };

  const colleagues = (internal.department_colleagues || []).slice(0, 8);
  const internalConnections = (internal.leadership_connections || []).slice(0, 6);
  const externalConnections = (external.industry_connections || []).slice(0, 6)
    .map(connectionLabel)
    .filter(Boolean);
  const members = committee.identified_members || [];
  const missingRoles = committee.missing_roles || [];
  const hasAnything = members.length || colleagues.length || internalConnections.length || externalConnections.length;
  const hasTeamPair = members.length > 0 && colleagues.length > 0;
  const hasConnectionPair = internalConnections.length > 0 && externalConnections.length > 0;

  return (
    <div className="@container pb-8">
      <section>
        <h2 className="text-[15px] font-medium text-white">Team & network</h2>
        <p className="mt-0.5 text-[11px] text-white/35">Who surrounds this lead and how decisions get made</p>

        {(summary.total_colleagues_identified || summary.decision_making_style) && (
          <div className="mt-4">
            <FlatRows
              rows={[
                ['Colleagues found', summary.total_colleagues_identified ? String(summary.total_colleagues_identified) : null],
                ['Decision style', summary.decision_making_style ? <span className="capitalize">{String(summary.decision_making_style).replace(/_/g, ' ')}</span> : null],
                ['Org structure', summary.org_structure_insight ? <span className="capitalize">{String(summary.org_structure_insight).replace(/_/g, ' ')}</span> : null],
              ]}
            />
          </div>
        )}
      </section>

      {(members.length > 0 || colleagues.length > 0) && (
        <div className={`mt-6 ${hasTeamPair ? '@min-[680px]:grid @min-[680px]:grid-cols-2' : ''}`}>
          {members.length > 0 && (
            <section className={hasTeamPair ? 'pb-6 @min-[680px]:pb-0 @min-[680px]:pr-6' : ''}>
              <h3 className="text-[12px] font-semibold uppercase tracking-[0.12em] text-white/70">Buying committee</h3>
              <div className="mt-3 space-y-2.5">
                {members.map((member: string) => <PersonRow key={member} name={member} />)}
              </div>
              {missingRoles.length > 0 && (
                <p className="mt-3 text-[12px] leading-5 text-white/40">
                  <span className="text-amber-200/80">Missing roles:</span> {missingRoles.join(', ')}
                </p>
              )}
            </section>
          )}

          {colleagues.length > 0 && (
            <section className={hasTeamPair ? 'border-t border-white/[0.06] pt-6 @min-[680px]:border-l @min-[680px]:border-t-0 @min-[680px]:pl-6 @min-[680px]:pt-0' : ''}>
              <h3 className="text-[12px] font-semibold uppercase tracking-[0.12em] text-white/70">Colleagues</h3>
              <div className="mt-3 space-y-2.5">
                {colleagues.map((colleague: any, index: number) => (
                  <PersonRow key={`${colleague.name}-${index}`} name={colleague.name} detail={colleague.title} />
                ))}
              </div>
            </section>
          )}
        </div>
      )}

      {(internalConnections.length > 0 || externalConnections.length > 0) && (
        <section className="mt-6 border-t border-white/[0.06] pt-6">
          <h3 className="text-[12px] font-semibold uppercase tracking-[0.12em] text-white/70">Known connections</h3>
          <div className={`mt-3 ${hasConnectionPair ? '@min-[680px]:grid @min-[680px]:grid-cols-2' : ''}`}>
            {internalConnections.length > 0 && (
              <div className={hasConnectionPair ? 'pb-5 @min-[680px]:pb-0 @min-[680px]:pr-6' : ''}>
                <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-white/35">Internal</p>
                <div className="space-y-2.5">
                  {internalConnections.map((conn: any, index: number) => (
                    <PersonRow key={`int-${conn.name}-${index}`} name={conn.name} detail={conn.title} />
                  ))}
                </div>
              </div>
            )}
            {externalConnections.length > 0 && (
              <div className={hasConnectionPair ? 'border-t border-white/[0.06] pt-5 @min-[680px]:border-l @min-[680px]:border-t-0 @min-[680px]:pl-6 @min-[680px]:pt-0' : ''}>
                <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-white/35">External</p>
                <div className="space-y-2.5">
                  {externalConnections.map((conn, index) => (
                    <PersonRow key={`ext-${conn}-${index}`} name={conn} />
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {!hasAnything && (
        <p className="mt-6 rounded-xl border border-dashed border-white/10 px-5 py-8 text-center text-[13px] text-white/40">
          No team or network data was identified for this lead yet.
        </p>
      )}
    </div>
  );
};

/**
 * Signals tab — recent social activity with its AI insight, plus the
 * engagement themes. Skips the stat-card blocks the full Lead Details tab
 * carries; the timeline is what matters mid-call.
 */
const ISRSignalsTab: React.FC<{ lead: MappedLeadData }> = ({ lead }) => {
  const intel = (lead.rawProfileIntel || {}) as Record<string, any>;
  const activities = (lead.recentActivities || []).slice(0, 8);
  const contentSummary = intel.social_intelligence?.content_analysis?.content_summary || {};

  const themes: Array<{ label: string; items: string[]; tone: string }> = [
    { label: 'Hot topics', items: contentSummary.hot_topics || [], tone: 'text-purple-200/90' },
    { label: 'Pain themes', items: contentSummary.pain_themes || [], tone: 'text-rose-200/90' },
    { label: 'Aspirations', items: contentSummary.aspiration_themes || [], tone: 'text-emerald-200/90' },
  ].filter((group) => group.items.length > 0);

  return (
    <div className="space-y-8 pb-8">
      <section>
        <h2 className="text-[15px] font-medium text-white">Social signals</h2>
        <p className="mt-0.5 text-[11px] text-white/35">What this lead has been doing and talking about</p>

        {activities.length > 0 ? (
          <div className="mt-4 space-y-0">
            {activities.map((activity: any, index: number) => {
              const insight = activity?.analysis?.actionable_insight
                || activity?.analysis?.strategic_value
                || activity?.analysis?.summary
                || activity?.analysis?.headline;
              const text = activity?.data?.text || activity?.data?.activityDescription;
              return (
                <article key={activity?.id || index} className="border-b border-white/[0.06] py-3.5 last:border-b-0">
                  <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
                    <h3 className="text-[13px] font-medium capitalize text-white/85">
                      {activity?.type ? String(activity.type).replace(/_/g, ' ') : 'Activity'}
                    </h3>
                    {activity?.timestamp && (
                      <time className="text-[11px] text-white/35" dateTime={activity.timestamp}>
                        {new Date(activity.timestamp).toLocaleDateString([], { month: 'short', day: 'numeric' })}
                      </time>
                    )}
                  </div>
                  {text && <p className="mt-1 break-words text-[12px] leading-5 text-white/50">{text}</p>}
                  {insight && (
                    <p className="mt-1.5 break-words text-[13px] leading-6 text-cyan-100/70">
                      <Icon icon="bx:bulb" className="mr-1.5 inline h-3.5 w-3.5 align-[-2px]" aria-hidden="true" />
                      {insight}
                    </p>
                  )}
                </article>
              );
            })}
          </div>
        ) : (
          <p className="mt-4 rounded-xl border border-dashed border-white/10 px-5 py-8 text-center text-[13px] text-white/40">
            No recent social activity was captured for this lead.
          </p>
        )}
      </section>

      {themes.length > 0 && (
        <section>
          <h3 className="text-[12px] font-semibold uppercase tracking-[0.12em] text-white/70">Engagement themes</h3>
          <div className="mt-3 space-y-3">
            {themes.map((group) => (
              <div key={group.label}>
                <p className="text-[12px] uppercase tracking-[0.1em] text-white/40">{group.label}</p>
                <p className={`mt-1 text-[13px] leading-6 ${group.tone}`}>{group.items.join(' · ')}</p>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

type ISRPainCategory = 'critical' | 'constraint' | 'challenge' | 'customer';

const ISR_PAIN_CATEGORY_ORDER: ISRPainCategory[] = ['critical', 'constraint', 'challenge', 'customer'];

const ISR_PAIN_CATEGORY_META: Record<ISRPainCategory, {
  label: string;
  icon: string;
  dotClass: string;
  iconClass: string;
  labelClass: string;
}> = {
  critical: {
    label: 'Critical risk',
    icon: 'bx:shield-x',
    dotClass: 'bg-amber-300/80',
    iconClass: 'bg-amber-300/10 text-amber-200',
    labelClass: 'text-amber-100/80',
  },
  constraint: {
    label: 'Constraint',
    icon: 'bx:time-five',
    dotClass: 'bg-violet-300/75',
    iconClass: 'bg-violet-300/10 text-violet-200',
    labelClass: 'text-violet-100/80',
  },
  challenge: {
    label: 'Challenge',
    icon: 'bx:target-lock',
    dotClass: 'bg-sky-300/75',
    iconClass: 'bg-sky-300/10 text-sky-200',
    labelClass: 'text-sky-100/80',
  },
  customer: {
    label: 'Customer need',
    icon: 'bx:user-voice',
    dotClass: 'bg-emerald-300/75',
    iconClass: 'bg-emerald-300/10 text-emerald-200',
    labelClass: 'text-emerald-100/80',
  },
};

const getISRPainCategory = (key: string): ISRPainCategory => {
  const normalizedKey = key.toLowerCase().replace(/[_-]+/g, ' ');
  if (/customer|client|buyer/.test(normalizedKey)) return 'customer';
  if (/compliance|risk|blocker|security/.test(normalizedKey)) return 'critical';
  if (/constraint|bottleneck|gap|resource need|time constraint/.test(normalizedKey)) return 'constraint';
  return 'challenge';
};

/**
 * Pain Points & Challenges — company and lead blockers grouped by meaning,
 * not painted as one undifferentiated red warning. The restrained impact key
 * lets an ISR separate true risks from constraints and ordinary challenges.
 */
const ISRPainsTab: React.FC<{ lead: MappedLeadData }> = ({ lead }) => {
  const companySummary = mergeCompanySummaries(lead.rawCompanySummary, lead.companySummary);
  const summaryGroups = buildCompanyTextGroups(companySummary?.pain_points);
  const fallbackItems = uniqueItems([
    ...toDisplayItems(companySummary?.pain_points),
    ...toDisplayItems(lead.painPoints),
  ]);
  const sourceGroups = summaryGroups.length > 0
    ? summaryGroups
    : fallbackItems.length > 0
      ? [{ key: 'identified-challenges', label: 'Identified challenges', items: fallbackItems }]
      : [];
  const groups = sourceGroups
    .map((group, sourceIndex) => ({
      ...group,
      category: getISRPainCategory(group.key),
      sourceIndex,
    }))
    .sort((first, second) => (
      ISR_PAIN_CATEGORY_ORDER.indexOf(first.category) - ISR_PAIN_CATEGORY_ORDER.indexOf(second.category)
      || first.sourceIndex - second.sourceIndex
    ));
  const categoryCounts = groups.reduce<Record<ISRPainCategory, number>>((counts, group) => {
    counts[group.category] += group.items.length;
    return counts;
  }, { critical: 0, constraint: 0, challenge: 0, customer: 0 });
  const total = groups.reduce((count, group) => count + group.items.length, 0);

  return (
    <div className="@container pb-8">
      <section>
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="text-[15px] font-medium text-white">Pain Points &amp; Challenges</h2>
            <p className="mt-0.5 text-[11px] text-white/35">Prioritized blockers, constraints, and customer needs</p>
          </div>
          {total > 0 && <span className="text-[11px] tabular-nums text-white/35">{total} identified</span>}
        </div>

        {groups.length > 0 ? (
          <>
            <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 border-y border-white/[0.06] py-2.5" aria-label="Issue impact key">
              <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-white/30">Impact key</span>
              {ISR_PAIN_CATEGORY_ORDER.map((category) => {
                const count = categoryCounts[category];
                if (!count) return null;
                const meta = ISR_PAIN_CATEGORY_META[category];
                return (
                  <span key={category} className="inline-flex items-center gap-1.5 text-[11px] text-white/45">
                    <span className={`h-1.5 w-1.5 rounded-full ${meta.dotClass}`} aria-hidden="true" />
                    {meta.label}
                    <span className="font-semibold text-white/65">{count}</span>
                  </span>
                );
              })}
            </div>

            <div className={`mt-5 grid gap-x-8 gap-y-6 ${groups.length > 1 ? '@min-[680px]:grid-cols-2' : ''}`}>
              {groups.map((group) => {
                const meta = ISR_PAIN_CATEGORY_META[group.category];
                return (
                  <section key={group.key} className="min-w-0 border-t border-white/[0.07] pt-3" aria-label={group.label}>
                    <div className="flex min-w-0 items-center justify-between gap-3">
                      <h3 className="flex min-w-0 items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.1em] text-white/70">
                        <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md ${meta.iconClass}`}>
                          <Icon icon={meta.icon} className="h-3.5 w-3.5" aria-hidden="true" />
                        </span>
                        <span className="min-w-0 break-words">{group.label}</span>
                      </h3>
                      <span className={`shrink-0 text-[10px] font-medium ${meta.labelClass}`}>{meta.label}</span>
                    </div>
                    <ul className="mt-2.5 space-y-2">
                      {group.items.map((item) => (
                        <li key={item} className="flex min-w-0 items-start gap-2.5 text-[13px] leading-5 text-white/70">
                          <span className={`mt-[7px] h-1 w-1 shrink-0 rounded-full ${meta.dotClass}`} aria-hidden="true" />
                          <span className="min-w-0 break-words">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </section>
                );
              })}
            </div>
          </>
        ) : (
          <p className="mt-4 rounded-xl border border-dashed border-white/10 px-5 py-8 text-center text-[13px] text-white/40">
            No pain points or challenges have been identified for this lead yet.
          </p>
        )}
      </section>
    </div>
  );
};

/**
 * Growth Opportunities tab — surfaces the lead's identified opportunities:
 * campaign-specific ones first (lvl2, detected for THIS campaign/task), then
 * the person-level ones (lvl1: need, evidence, urgency, confidence, amount),
 * and the company's growth vectors / strategic partnerships / digital
 * transformation from the company summary. Flat rows, no cards.
 */
const ISROpportunitiesTab: React.FC<{ lead: MappedLeadData }> = ({ lead }) => {
  // Campaign-specific opportunities (lvl2, parsed array — the transformer
  // already JSON-parses the column; the record guard mirrors personOpps).
  const campaignOpps = (lead.campaignOpportunities || []).filter(
    (opp: any) => opp && typeof opp === 'object' && (opp.need || opp.type || opp.category),
  );

  // Person-level opportunities (array of structured objects from lvl1_person).
  const personOpps = (lead.opportunities || []).filter(
    (opp: any) => opp && (opp.need || opp.type || opp.category),
  );

  // Use the same schema-aware normalizer as Lead Details. Production payloads
  // use growth_opportunities / market_expansion / strategic_priorities as well
  // as the older growth_vectors shape; reading three keys by hand dropped data.
  const companySummary = mergeCompanySummaries(lead.rawCompanySummary, lead.companySummary);
  const companyOpportunitySource = companySummary?.opportunities
    || (lead.rawProfileIntel as Record<string, any> | undefined)?.opportunities;
  const companyOpportunityGroups = buildCompanyOpportunityGroups(companyOpportunitySource);

  const hasCampaign = campaignOpps.length > 0;
  const hasPerson = personOpps.length > 0;
  const hasCompany = companyOpportunityGroups.length > 0;

  if (!hasCampaign && !hasPerson && !hasCompany) {
    return (
      <div className="pb-8">
        <h2 className="text-[15px] font-medium text-white">Growth opportunities</h2>
        <div className="mt-4">
          {/* Fully empty tab still distinguishes the two sources: nothing
              campaign-scoped AND nothing at all. */}
          <CampaignOpportunitiesNotice />
        </div>
        <p className="mt-4 rounded-xl border border-dashed border-white/10 px-5 py-8 text-center text-[13px] text-white/40">
          No growth opportunities have been identified for this lead yet.
        </p>
      </div>
    );
  }

  const urgencyTone: Record<string, string> = {
    high: 'text-rose-200/80 border-rose-400/30',
    medium: 'text-amber-200/80 border-amber-300/30',
    low: 'text-white/50 border-white/15',
  };

  return (
    <div className="@container pb-8">
      {/* Campaign-specific opportunities — highlighted as the primary signal:
          emerald accent container + MAIN badge set it apart from the general
          person/company sources below. When NONE were detected the slot shows
          an explicit notice instead of vanishing, so the person/company lists
          are not mistaken for campaign detections. */}
      {!hasCampaign && <CampaignOpportunitiesNotice />}
      {hasCampaign && (
        <section className={`min-w-0 ${hasPerson || hasCompany ? 'mb-8' : ''}`}>
          <div className="rounded-2xl border border-emerald-300/20 bg-emerald-300/[0.045] p-4">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-[15px] font-medium text-white">Campaign opportunities</h2>
              <span className="inline-flex items-center gap-1 rounded-md border border-emerald-300/30 bg-emerald-300/[0.12] px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.1em] text-emerald-200">
                <Icon icon="bx:target-lock" className="h-3 w-3" aria-hidden="true" />
                Main
              </span>
            </div>
            <p className="mt-0.5 text-[11px] text-emerald-100/50">Specifically detected for this campaign</p>
            <div className="mt-4 divide-y divide-emerald-300/[0.08]">
            {campaignOpps.map((opp: any, index: number) => {
              const evidence = uniqueItems(toDisplayItems(opp.evidence));
              const moneyValue = formatMoneyValue(opp.amount_usd);
              const timeline = formatTimeline(opp.timeline_months);
              return (
              <article key={opp.id || index} className="py-3.5 first:pt-0">
                <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
                  <h3 className="flex items-center gap-1.5 text-[13px] font-medium capitalize text-emerald-100/85">
                    <Icon icon="bx:target-lock" className="h-3.5 w-3.5 shrink-0 text-emerald-300/80" aria-hidden="true" />
                    {String(opp.type || opp.category || 'Opportunity').replace(/_/g, ' ')}
                  </h3>
                  <div className="flex items-center gap-2">
                    {opp.urgency && (
                      <span className={`rounded-md border px-1.5 py-0.5 text-[10px] font-semibold uppercase ${urgencyTone[String(opp.urgency).toLowerCase()] || urgencyTone.low}`}>
                        {String(opp.urgency)}
                      </span>
                    )}
                    {opp.confidence && (
                      <span className="text-[10px] text-white/35">{String(opp.confidence)}% conf.</span>
                    )}
                  </div>
                </div>
                {opp.need && <p className="mt-1.5 break-words text-[13px] leading-6 text-white/75">{String(opp.need)}</p>}
                {evidence.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {evidence.map((item) => (
                      <li key={item} className="flex items-start gap-2 break-words text-[12px] leading-5 text-cyan-100/55">
                        <Icon icon="bx:search-alt" className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-white/40">
                  {moneyValue && <span className="inline-flex items-center gap-1"><Icon icon="bx:dollar-circle" className="h-3.5 w-3.5 text-emerald-300/70" aria-hidden="true" />{moneyValue}</span>}
                  {timeline && <span className="inline-flex items-center gap-1"><Icon icon="bx:time-five" className="h-3.5 w-3.5 text-cyan-300/70" aria-hidden="true" />{timeline}</span>}
                  {opp.country && <span className="inline-flex items-center gap-1"><Icon icon="bx:map" className="h-3.5 w-3.5 text-amber-300/70" aria-hidden="true" />{String(opp.country)}</span>}
                  {opp.decision_maker && <span className="inline-flex items-center gap-1"><Icon icon="bx:user-voice" className="h-3.5 w-3.5 text-violet-300/70" aria-hidden="true" />{String(opp.decision_maker).replace(/_/g, ' ')}</span>}
                </div>
              </article>
              );
            })}
            </div>
          </div>
        </section>
      )}

      <div className={hasPerson && hasCompany ? '@min-[680px]:grid @min-[680px]:grid-cols-[minmax(0,1.4fr)_minmax(15rem,1fr)] @min-[680px]:gap-7' : ''}>
      {/* Person-level opportunities */}
      {hasPerson && (
        <section className="min-w-0">
          <h2 className="text-[15px] font-medium text-white">Identified opportunities</h2>
          <p className="mt-0.5 text-[11px] text-white/35">What this lead needs and the evidence behind it</p>
          <div className="mt-4 divide-y divide-white/[0.06]">
            {personOpps.map((opp: any, index: number) => {
              const evidence = uniqueItems(toDisplayItems(opp.evidence));
              const moneyValue = formatMoneyValue(opp.amount_usd);
              const timeline = formatTimeline(opp.timeline_months);
              return (
              <article key={opp.id || index} className="py-3.5 first:pt-0">
                <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
                  <h3 className="text-[13px] font-medium capitalize text-white/85">
                    {String(opp.type || opp.category || 'Opportunity').replace(/_/g, ' ')}
                  </h3>
                  <div className="flex items-center gap-2">
                    {opp.urgency && (
                      <span className={`rounded-md border px-1.5 py-0.5 text-[10px] font-semibold uppercase ${urgencyTone[String(opp.urgency).toLowerCase()] || urgencyTone.low}`}>
                        {String(opp.urgency)}
                      </span>
                    )}
                    {opp.confidence && (
                      <span className="text-[10px] text-white/35">{String(opp.confidence)}% conf.</span>
                    )}
                  </div>
                </div>
                {opp.need && <p className="mt-1.5 break-words text-[13px] leading-6 text-white/75">{String(opp.need)}</p>}
                {evidence.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {evidence.map((item) => (
                      <li key={item} className="flex items-start gap-2 break-words text-[12px] leading-5 text-cyan-100/55">
                        <Icon icon="bx:search-alt" className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-white/40">
                  {moneyValue && <span className="inline-flex items-center gap-1"><Icon icon="bx:dollar-circle" className="h-3.5 w-3.5 text-emerald-300/70" aria-hidden="true" />{moneyValue}</span>}
                  {timeline && <span className="inline-flex items-center gap-1"><Icon icon="bx:time-five" className="h-3.5 w-3.5 text-cyan-300/70" aria-hidden="true" />{timeline}</span>}
                  {opp.decision_maker && <span className="inline-flex items-center gap-1"><Icon icon="bx:user-voice" className="h-3.5 w-3.5 text-violet-300/70" aria-hidden="true" />{String(opp.decision_maker).replace(/_/g, ' ')}</span>}
                </div>
              </article>
              );
            })}
          </div>
        </section>
      )}

      {/* Company-level growth vectors */}
      {hasCompany && (
        <section className={`min-w-0 ${hasPerson ? 'mt-8 border-t border-white/[0.08] pt-6 @min-[680px]:mt-0 @min-[680px]:border-l @min-[680px]:border-t-0 @min-[680px]:pl-6 @min-[680px]:pt-0' : ''}`}>
          <h2 className="text-[15px] font-medium text-white">Company growth vectors</h2>
          <p className="mt-0.5 text-[11px] text-white/35">Strategic directions identified for the account</p>
          <div className="mt-4 divide-y divide-white/[0.06] border-y border-white/[0.06]">
            {companyOpportunityGroups.map((group) => (
              <section key={group.key} className="py-3.5">
                <h3 className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.1em] text-emerald-100/75">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-emerald-300/[0.08] text-emerald-200">
                    <Icon icon={group.icon} className="h-3.5 w-3.5" aria-hidden="true" />
                  </span>
                  {group.label}
                </h3>
                <ul className="mt-2 space-y-1.5">
                  {group.items.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-[12px] leading-5 text-white/65">
                      <span className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-emerald-300/70" aria-hidden="true" />
                      <span className="min-w-0 break-words">{item}</span>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        </section>
      )}
      </div>
    </div>
  );
};

/**
 * Rotating headlines for the identity panel's pinned bottom strip — title +
 * derived summary, clickable, crossfading every ~7s. Cache-fed only.
 */
const BottomNewsTicker: React.FC<{ headlines: LocalNewsItem[] }> = ({ headlines }) => {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    setIndex(0);
  }, [headlines]);

  useEffect(() => {
    if (headlines.length <= 1) return undefined;
    const timer = window.setInterval(
      () => setIndex((current) => (current + 1) % headlines.length),
      7000,
    );
    return () => window.clearInterval(timer);
  }, [headlines]);

  if (!headlines.length) return null;
  const item = headlines[index % headlines.length];

  return (
    <a
      key={`${item.url}-${index}`}
      href={item.url}
      target="_blank"
      rel="noopener noreferrer"
      title={item.title}
      className="mt-2 block animate-[fadeIn_0.6s_ease] px-2 py-1"
    >
      <span className="block truncate text-[12px] font-medium text-white/80">{item.title}</span>
      {item.summary && item.summary !== item.title && (
        <span className="mt-0.5 block truncate text-[10px] text-white/35">{item.summary}</span>
      )}
    </a>
  );
};

/**
 * Pinned bottom strip for the left panel — renders at the SAME vertical level
 * as the right panel's "All SDR information is up to date" footer. Auto-
 * rotates between local clock & weather and cached news headlines. Self-
 * contained: owns its own timezone/weather/news state so the identity panel
 * above stays pure content.
 */
const ContextBottomStrip: React.FC<{ lead: MappedLeadData; hydrating?: boolean }> = ({ lead, hydrating = false }) => {
  const timezone = resolveTimezone(lead);

  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(timer);
  }, []);
  const localTime = useMemo(() => getLocalTimeString(timezone), [timezone, now]);
  const dayPart = useMemo(() => getLocalDayPart(timezone), [timezone, now]);

  const weatherPlace = [lead.companyCity, lead.companyCountry].filter(Boolean).join(', ');
  const [weather, setWeather] = useState<WeatherResult | null>(null);
  const [weatherCity, setWeatherCity] = useState('');
  useEffect(() => {
    let cancelled = false;
    const lat = Number(lead.companyLat);
    const lon = Number(lead.companyLon);
    const load = async () => {
      let coords: { lat: number; lon: number } | null = null;
      let city = lead.companyCity || '';
      if (Number.isFinite(lat) && Number.isFinite(lon) && (lat !== 0 || lon !== 0)) {
        coords = { lat, lon };
      } else if (weatherPlace) {
        coords = await geocodePlace(weatherPlace);
        if (coords) city = lead.companyCity || weatherPlace;
      }
      if (!coords || cancelled) return;
      const result = await fetchWeather(coords.lat, coords.lon);
      if (!cancelled) { setWeather(result); setWeatherCity(city); }
    };
    void load();
    return () => { cancelled = true; };
  }, [lead.companyLat, lead.companyLon, weatherPlace]);

  const newsPlace = newsPlaceFor(lead);
  const [newsHeadlines, setNewsHeadlines] = useState<LocalNewsItem[]>([]);
  useEffect(() => {
    if (hydrating || !newsPlace || !isLocalNewsEnabled()) { setNewsHeadlines([]); return undefined; }
    let cancelled = false;
    // Try cache first (cheap DB read). If empty, fetch from Webz.io which
    // writes the 24h cache — so the first ISR per city per day pays one API
    // call, everyone else reads the cache. Dynamic per the leads users open.
    readLocalNews(newsPlace).then((cached) => {
      if (cancelled) return;
      if (cached && cached.length) {
        setNewsHeadlines(cached.slice(0, 6));
        return;
      }
      // Cache miss — fetch and cache for the team.
      fetchLocalNews(newsPlace).then((fetched) => {
        if (!cancelled) setNewsHeadlines(fetched ? fetched.slice(0, 6) : []);
      });
    });
    return () => { cancelled = true; };
  }, [hydrating, newsPlace]);

  const [rotatingBottom, setRotatingBottom] = useState<number | null>(null);
  const [bottomPaused, setBottomPaused] = useState(false);
  useEffect(() => { setRotatingBottom(null); }, [newsHeadlines]);
  useEffect(() => {
    if (bottomPaused) return undefined;
    const total = newsHeadlines.length;
    const delay = rotatingBottom === null ? 8000 : 7000;
    const timer = window.setTimeout(() => {
      setRotatingBottom((current) => {
        if (current === null) return total > 0 ? 0 : null;
        return current + 1 < total ? current + 1 : null;
      });
    }, delay);
    return () => window.clearTimeout(timer);
  }, [rotatingBottom, bottomPaused, newsHeadlines.length]);

  return (
    <div className="pointer-events-auto mt-2 shrink-0 rounded-2xl border border-white/[0.09] bg-[#0b0e14]/95 p-2.5 shadow-[0_14px_34px_rgba(0,0,0,0.24)] backdrop-blur-xl">
      {(localTime || weather) ? (
        <div
          className="group/bottom flex items-center justify-between gap-3 px-2 py-1.5"
          onMouseEnter={() => setBottomPaused(true)}
          onMouseLeave={() => setBottomPaused(false)}
          onFocus={() => setBottomPaused(true)}
          onBlur={() => setBottomPaused(false)}
        >
          {rotatingBottom === null ? (
            <div className="flex min-w-0 flex-1 items-center justify-between gap-3 animate-[fadeIn_0.6s_ease]" title={`Timezone: ${timezone}`}>
              {localTime ? (
                <p className="flex min-w-0 items-baseline gap-2">
                  <span className="text-[15px] font-semibold tabular-nums text-white/90">{localTime}</span>
                  {dayPart && <span className="truncate text-[11px] text-white/40">{dayPart} · their time</span>}
                </p>
              ) : <span />}
              {weather && (
                <p className="flex shrink-0 items-center gap-1.5 text-[13px] text-white/75" title={`Weather in ${weatherPlace} · timezone ${timezone}`}>
                  <Icon icon={getWeatherIcon(weather.weatherCode)} className={`h-4 w-4 ${WEATHER_ICON_TONE[getWeatherIcon(weather.weatherCode)] || 'text-white/70'}`} aria-hidden="true" />
                  <span className="tabular-nums">{weather.temperature}°C</span>
                  <span className="hidden truncate text-[11px] text-white/40 sm:inline">{weatherCity}</span>
                </p>
              )}
            </div>
          ) : (
            <a
              key={`${newsHeadlines[rotatingBottom].url}-${rotatingBottom}`}
              href={newsHeadlines[rotatingBottom].url}
              target="_blank"
              rel="noopener noreferrer"
              title={newsHeadlines[rotatingBottom].title}
              className="min-w-0 flex-1 animate-[fadeIn_0.6s_ease]"
            >
              <span className="flex items-center gap-2">
                <span className="flex h-4 shrink-0 items-center gap-1 rounded bg-cyan-400/15 px-1.5 text-[8px] font-black uppercase tracking-[0.14em] text-cyan-200">
                  <span className="relative flex h-1 w-1">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-cyan-300 opacity-60 motion-reduce:animate-none" />
                    <span className="relative inline-flex h-1 w-1 rounded-full bg-cyan-300" />
                  </span>
                  Local news
                </span>
                <span className="min-w-0 truncate text-[12px] font-medium text-white/80">{newsHeadlines[rotatingBottom].title}</span>
              </span>
              {newsHeadlines[rotatingBottom].summary && newsHeadlines[rotatingBottom].summary !== newsHeadlines[rotatingBottom].title && (
                <span className="mt-0.5 block truncate pl-1 text-[10px] text-white/35">{newsHeadlines[rotatingBottom].summary}</span>
              )}
            </a>
          )}
        </div>
      ) : (
        <p className="rounded-xl border border-dashed border-white/10 px-3.5 py-2.5 text-center text-[11px] text-white/30">
          Local time unavailable for this lead
        </p>
      )}
    </div>
  );
};

/**
 * Lead identity rendered OUTSIDE the drawer, in the dimmed backdrop area on
 * wide screens. Owns everything the ISR needs to know "who am I calling":
 * big avatar + company logo, name/role/company once with quick call/email/
 * WhatsApp actions, ICP / location / language / local time facts, and the
 * lead overview text. The drawer itself never repeats identity data.
 */
/** Small click-to-copy label showing a database ID. */
const CopyId: React.FC<{ label: string; id: string }> = ({ label, id }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(id);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch { /* clipboard unavailable */ }
  }, [id]);
  return (
    <button type="button" onClick={handleCopy} title={`Copy ${label} ID: ${id}`} className="group inline-flex items-center gap-1 text-[10px] text-white/25 transition-colors hover:text-white/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/40">
      <span className="font-mono">{copied ? 'Copied!' : `${label}: ${id}`}</span>
      {!copied && <Icon icon="bx:copy" className="h-3 w-3 opacity-0 transition-opacity group-hover:opacity-100" aria-hidden="true" />}
    </button>
  );
};

const IdentityFactsSkeleton: React.FC = () => (
  <div className="mt-7" role="status" aria-busy="true" aria-label="Loading facts and profile">
    <div className="space-y-3 border-t border-white/[0.1] pt-5">
      <div className="flex items-center justify-between gap-4">
        <SkeletonLine className="h-2.5 w-16" />
        <SkeletonLine className="h-6 w-12 rounded-lg" />
      </div>
      <div className="flex items-center justify-between gap-4">
        <SkeletonLine className="h-2.5 w-14" />
        <SkeletonLine className="h-3 w-28" />
      </div>
      <div className="flex items-center justify-between gap-4">
        <SkeletonLine className="h-2.5 w-16" />
        <SkeletonLine className="h-3 w-20" />
      </div>
    </div>
    <div className="mt-5 border-t border-white/[0.1] pt-4">
      <SkeletonLine className="h-2.5 w-24" />
      <div className="mt-3 space-y-2.5">
        <div className="flex items-center justify-between gap-4">
          <SkeletonLine className="h-2.5 w-16" />
          <SkeletonLine className="h-3 w-20" />
        </div>
        <div className="flex items-center justify-between gap-4">
          <SkeletonLine className="h-2.5 w-14" />
          <SkeletonLine className="h-3 w-24" />
        </div>
        <div className="flex items-center justify-between gap-4">
          <SkeletonLine className="h-2.5 w-20" />
          <SkeletonLine className="h-3 w-16" />
        </div>
      </div>
      <SkeletonLine className="mt-4 h-2.5 w-12" />
      <SkeletonLine className="mt-2 h-3 w-full" />
      <SkeletonLine className="mt-1.5 h-3 w-3/4" />
    </div>
    <span className="sr-only">Facts and profile are loading.</span>
  </div>
);

const LeadIdentityPanel: React.FC<{
  lead: MappedLeadData;
  campaign?: CampaignMetadata;
  quickPhone?: string;
  quickEmail?: string;
  quickWhatsapp?: string;
  /** True while the full record hydrates — overview + facts shimmer. */
  hydrating?: boolean;
  /** Opens the full lead profile stacked over the workspace. */
  onOpenFullLead?: () => void;
}> = ({ lead, campaign, quickPhone = '', quickEmail = '', quickWhatsapp = '', hydrating = false, onOpenFullLead }) => {
  const overview = firstPresentText(
    lead.leadExecutiveOverview,
    lead.linkedinSummary,
    lead.generatorSummary,
  );
  // Toggle between the fact sheets (ICP/location/professional) and the
  // Overview narrative — the overview is long-form and easy to miss below
  // the fold, so both views get equal one-tap discoverability. While
  // hydrating the overview is unknown, so both options render; once loaded,
  // the Overview tab hides itself when there's nothing to show.
  const overviewAvailable = hydrating || Boolean(overview);
  const [identityView, setIdentityView] = useState<'facts' | 'overview'>('facts');
  const activeView = identityView === 'overview' && !overviewAvailable ? 'facts' : identityView;
  // Person location — this panel is about the PERSON, not the company (the
  // company HQ already shows in the header/company card). Same lvl2 → lvl1
  // precedence deriveContact uses for the editable contact draft.
  const personLvl2 = (lead.personLvl2 || (lead.rawOriginalData as any)?.lvl2 || {}) as Record<string, any>;
  const personLvl1 = (lead.personLvl1 || (lead.rawOriginalData as any)?.lvl1_person || {}) as Record<string, any>;
  const locationParts = [
    personLvl2.city || personLvl1.city,
    personLvl2.state || personLvl1.state,
    personLvl2.country || personLvl1.country,
  ].map((part) => (part && String(part).trim().toLowerCase() !== 'unknown' ? String(part).trim() : ''));
  const location = locationParts.filter(Boolean).join(', ');
  const language = firstPresentText(lead.language, lead.languageCode);
  // Industry — CNAE text first (ES market), then generic industry; the full
  // CNAE detail (code + description) rides along on hover. The platform uses
  // ONE CNAE per company, so the raw value is always the first code.
  const cnaeDetail = firstPresentText(
    lead.cnae_text && lead.cnae_code ? `${firstCnaeCode(lead.cnae_code)} · ${firstPresentText(lead.cnae_text)}` : lead.cnae_text,
  );
  const industry = firstPresentText(
    lead.cnae_text ? lead.cnae_text.split(/[,;]/)[0].trim() : '',
    lead.companyIndustry,
    lead.industry,
  );
  // Same ICP pill used across lists/cards (utils/helpers getICPConfig).
  const icp = lead.icpScore !== null && lead.icpScore !== undefined ? getICPConfig(lead.icpScore) : null;

  // Professional stats — the "Key Insights" block from the Details view.
  const professional: Array<[string, string]> = [
    ['Experience', (() => { const y = firstPresentText(lead.yearsExperience); return y ? `${y} years` : ''; })()],
    ['Followers', firstPresentText(lead.linkedinFollowers)],
    ['Career stage', firstPresentText(lead.careerStage)],
    ['Focus', firstPresentText(lead.professionalFocus)],
    ['Decision power', firstPresentText(lead.decisionAuthority)],
    ['Influence', firstPresentText(lead.influenceLevel)],
  ].filter((entry): entry is [string, string] => Boolean(entry[1])
    && !['unknown', 'n/a'].includes(entry[1].toLowerCase()));
  const skills = (lead.skills || []).map((skill) => String(skill).trim()).filter(Boolean).slice(0, 8);

  return (
    <div className="flex h-full flex-col text-white">
      {/* Identity — avatar, logo, name — FIXED, never scrolls. */}
      <div className="flex shrink-0 flex-col items-center text-center">
        <div className="relative">
          <ReplacementLeadBadge key={String(lead.id)} lead={lead} campaignId={campaign?.id} />
          <AvatarWithFallback
            src={lead.profilePicture}
            name={lead.leadName || 'Lead'}
            sizeClass="h-32 w-32"
            textClass="text-3xl"
            roundedClass="rounded-full"
            alt={`${lead.leadName || 'Lead'} photo`}
            href={normalizeUrl(lead.leadLinkedin) || undefined}
          />
          <span className="absolute -bottom-2 -right-2">
            <CompanyLogoBadge src={lead.companyLogo} name={lead.companyName || 'CO'} />
          </span>
        </div>
        <h2 className="mt-5 break-words text-2xl font-semibold leading-tight text-white">{lead.leadName || 'Unnamed lead'}</h2>
        <p className="mt-1 break-words text-[13px] leading-5 text-white/50">{lead.leadRole}</p>
        <p className="mt-2.5 flex items-center justify-center gap-1.5 break-words text-[14px] font-medium leading-5 text-white/85">
          <Icon icon="bx:buildings" className="h-4 w-4 shrink-0 text-white/45" aria-hidden="true" />
          {lead.companyName}
        </p>

        {/* Quick actions — call / email / WhatsApp, right under the identity. */}
        {(quickPhone || quickEmail || quickWhatsapp) && (
          <div className="mt-4 flex items-center gap-1 rounded-xl border border-white/10 bg-white/[0.05] p-1 backdrop-blur-sm">
            <a aria-label={quickPhone ? `Call ${lead.leadName}` : 'Add a phone number to call'} aria-disabled={!quickPhone} href={quickPhone ? `tel:+${quickPhone}` : undefined} className={`flex h-9 w-9 items-center justify-center rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/40 ${quickPhone ? 'text-white/80 hover:bg-white/[0.09] hover:text-white' : 'pointer-events-none text-white/18'}`}><Icon icon="bx:phone-call" className="h-4.5 w-4.5" aria-hidden="true" /></a>
            <a aria-label={quickEmail ? `Email ${lead.leadName}` : 'Add an email address to send email'} aria-disabled={!quickEmail} href={quickEmail ? `mailto:${encodeURIComponent(quickEmail)}` : undefined} className={`flex h-9 w-9 items-center justify-center rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/40 ${quickEmail ? 'text-white/80 hover:bg-white/[0.09] hover:text-white' : 'pointer-events-none text-white/18'}`}><Icon icon="bx:envelope" className="h-4.5 w-4.5" aria-hidden="true" /></a>
            <a aria-label={quickWhatsapp ? `Open WhatsApp for ${lead.leadName}` : 'Add a WhatsApp number'} aria-disabled={!quickWhatsapp} href={quickWhatsapp ? `https://wa.me/${quickWhatsapp}` : undefined} target={quickWhatsapp ? '_blank' : undefined} rel={quickWhatsapp ? 'noreferrer' : undefined} className={`flex h-9 w-9 items-center justify-center rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/40 ${quickWhatsapp ? 'text-white/80 hover:bg-white/[0.09] hover:text-white' : 'pointer-events-none text-white/18'}`}><Icon icon="bx:bxl-whatsapp" className="h-4.5 w-4.5" aria-hidden="true" /></a>
            {onOpenFullLead && (
              <button type="button" onClick={onOpenFullLead} title="Open the complete lead profile (closing it returns here)" className="ml-1 flex h-9 items-center gap-1.5 rounded-lg border border-white/12 bg-white/[0.03] px-2.5 text-[12px] font-medium text-white/70 transition-colors hover:border-white/30 hover:bg-white/[0.07] hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/40">
                <Icon icon="bx:window-open" className="h-4 w-4" aria-hidden="true" />
                Full lead
              </button>
            )}
          </div>
        )}
      </div>

      {/* View switch — Facts ↔ Overview. */}
      <div className="mx-8 mt-6 flex shrink-0 items-center gap-1 rounded-xl border border-white/10 bg-white/[0.035] p-1 2xl:mx-12" role="tablist" aria-label="Identity sections">
        <button
          type="button"
          role="tab"
          aria-selected={activeView === 'facts'}
          onClick={() => setIdentityView('facts')}
          className={`flex h-8 flex-1 items-center justify-center gap-1.5 rounded-lg text-[12px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/40 ${
            activeView === 'facts' ? 'bg-white/[0.09] text-white' : 'text-white/45 hover:text-white/80'
          }`}
        >
          <Icon icon="bx:bar-chart-alt-2" className="h-3.5 w-3.5" aria-hidden="true" />
          Facts & profile
        </button>
        {overviewAvailable && (
          <button
            type="button"
            role="tab"
            aria-selected={activeView === 'overview'}
            onClick={() => setIdentityView('overview')}
            className={`flex h-8 flex-1 items-center justify-center gap-1.5 rounded-lg text-[12px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/40 ${
              activeView === 'overview' ? 'bg-white/[0.09] text-white' : 'text-white/45 hover:text-white/80'
            }`}
          >
            <Icon icon="bx:file-find" className="h-3.5 w-3.5" aria-hidden="true" />
            Overview
          </button>
        )}
      </div>

      {/* Scrollable — ONLY the tab content (facts/overview). Identity,
          toggle, and the pinned bottom strip stay fixed. */}
      <div className="custom-scrollbar custom-scrollbar--autohide min-h-0 flex-1 overflow-y-auto px-8 [overscroll-behavior:contain] 2xl:px-12">
      {/* Facts: ICP pill, location, language, local time */}
      {activeView === 'facts' && (
      hydrating ? <IdentityFactsSkeleton /> : <>
      <dl className="mt-7 space-y-2.5 border-t border-white/[0.1] pt-5">
        {icp && (
          <div className="flex items-center justify-between gap-4">
            <dt className="text-[11px] uppercase tracking-[0.12em] text-white/40">ICP score</dt>
            <dd className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-[12px] font-bold ${icp.wrapperClass} ${icp.textClass}`}>
              {lead.icpScore}%
            </dd>
          </div>
        )}
        {location && (
          <div className="flex items-baseline justify-between gap-4">
            <dt className="text-[11px] uppercase tracking-[0.12em] text-white/40">Location</dt>
            <dd className="break-words text-right text-[13px] text-white/80">{location}</dd>
          </div>
        )}
        {language && (
          <div className="flex items-baseline justify-between gap-4">
            <dt className="text-[11px] uppercase tracking-[0.12em] text-white/40">Language</dt>
            <dd className="text-[13px] text-white/80">{language}</dd>
          </div>
        )}
        </dl>

      {/* Professional profile — the "who is this person" stats the Details
          view surfaces (experience, reach, seniority, authority) plus skills.
          Compact fact rows; hidden entirely when enrichment has none. */}
      {professional.length > 0 && (
        <div className="mt-5 border-t border-white/[0.1] pt-4">
          <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-white/45">Professional</p>
          <dl className="mt-2 space-y-1.5">
            {professional.map(([label, value]) => (
              <div key={label} className="flex items-baseline justify-between gap-4">
                <dt className="text-[11px] uppercase tracking-[0.12em] text-white/40">{label}</dt>
                <dd className="text-[12px] font-semibold uppercase tracking-wide text-white/80">{value}</dd>
              </div>
            ))}
          </dl>
          {skills.length > 0 && (
            <div className="mt-3">
              <p className="text-[11px] uppercase tracking-[0.12em] text-white/40">Skills</p>
              <p className="mt-1.5 break-words text-[12px] leading-5 text-white/70">{skills.join(' · ')}</p>
            </div>
          )}
        </div>
      )}
      </>
      )}

      {/* Overview view — the narrative profile, full text. Shimmers while
          hydrating; when no overview exists the toggle stays on Facts. */}
      {activeView === 'overview' && (
        hydrating ? (
          <div className="mt-6 border-t border-white/[0.1] pt-5" aria-busy="true">
            <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-white/45">Overview</p>
            <SkeletonLine className="mt-2.5 h-3 w-full" />
            <SkeletonLine className="mt-2 h-3 w-11/12" />
            <SkeletonLine className="mt-2 h-3 w-4/5" />
            <SkeletonLine className="mt-2 h-3 w-2/3" />
          </div>
        ) : overview ? (
          <div className="mt-6 border-t border-white/[0.1] pt-5">
            <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-white/45">Overview</p>
            <p className="mt-2 break-words text-[13px] leading-6 text-white/75">{overview}</p>
          </div>
        ) : (
          <p className="mt-6 rounded-xl border border-dashed border-white/10 px-4 py-8 text-center text-[12px] text-white/35">
            No overview is available for this lead yet.
          </p>
        )
      )}
      </div>
    </div>
  );
};

/** Single source of truth for the news place key — the identity panel's
 *  bottom news strip reads the shared cache; keep one key shape. The news
 *  strip is "local news about the lead's city", so it keys off the PERSON's
 *  location (lvl2 → lvl1), falling back to the company city only when the
 *  person record has none. */
const newsPlaceFor = (lead: MappedLeadData): string => {
  const lvl2 = (lead.personLvl2 || (lead.rawOriginalData as any)?.lvl2 || {}) as Record<string, any>;
  const lvl1 = (lead.personLvl1 || (lead.rawOriginalData as any)?.lvl1_person || {}) as Record<string, any>;
  const person = [lvl2.city || lvl1.city, lvl2.state || lvl1.state]
    .map((part) => (part && String(part).trim().toLowerCase() !== 'unknown' ? String(part).trim() : ''))
    .filter(Boolean);
  if (person.length > 0) return person.slice(0, 2).join(', ');
  return [lead.companyCity, lead.companyState].filter(Boolean).slice(0, 2).join(', ');
};

export const ISRWorkspace: React.FC<ISRWorkspaceProps> = ({
  lead,
  campaign,
  editable = true,
  salesEditable,
  saving = false,
  saveError,
  currentUser,
  onSaveSnapshot,
  onSaveSalesState,
  onClose,
  onOpenFullLead,
  onDirtyChange,
  initialTab = 'briefing',
  expanded = false,
  showCloseButton = true,
  className = '',
  hydrating = false,
  onPreviousLead,
  onNextLead,
  hasPreviousLead,
  hasNextLead,
  onGenerateReport,
  leadPosition,
  totalLeads,
}) => {
  // The stepper is interactive when EITHER full editing or sales-only editing
  // is available (sales defaults to editable when not explicitly disabled).
  const salesEnabled = salesEditable !== undefined ? salesEditable : editable;
  // mod_sales gate — the team pref that owns the Outreach engine. When false,
  // the Start Outreach button is locked and opens the activation request modal.
  const { data: teamPrefs } = useTeamPreferences();
  const hasSalesModule = Boolean(teamPrefs?.modules?.sales);
  const [showUnlockOutreachModal, setShowUnlockOutreachModal] = useState(false);
  const queryClient = useQueryClient();
  const { targetEmail, currentUser: authUser } = useAuth();
  const impersonatedEmail = targetEmail && targetEmail !== authUser?.email ? targetEmail : null;
  const effectiveUserEmail = impersonatedEmail || authUser?.email || undefined;
  const initialState = useMemo(() => deriveState(lead), [lead]);
  // Log deriveState only on FIRST mount per lead identity, not on every
  // re-render (the useMemo re-runs on lead identity change — one log per open).
  const didFirstDeriveRef = useRef<string | null>(null);
  const leadKey = `${lead.campaignId ?? '?'}:${lead.id ?? '?'}`;
  if (didFirstDeriveRef.current !== leadKey) {
    didFirstDeriveRef.current = leadKey;
    timingLog('init', 'deriveState', {
      lead: lead.id,
      personFields: Object.keys(initialState.contact.person).length,
      companyFields: Object.keys(initialState.contact.company).length,
      salesStatus: initialState.sales.lead_status,
      salesSubstatus: initialState.sales.lead_substatus,
      notesCount: initialState.notes.notes?.length ?? 0,
    });
  }
  const [workspaceState, setWorkspaceState] = useState<WorkspaceState>(initialState);
  const [lastSavedState, setLastSavedState] = useState<WorkspaceState>(initialState);
  const [activeTab, setActiveTab] = useState<WorkspaceTab>(initialTab);

  // GSAP "liquid reveal" loading FX. Stage remounts on lead id change so the
  // blur-in re-fires when the ISR navigates between leads (prev/next) without
  // closing the drawer. revealOnFirstMount so opening the drawer plays it too.
  // Tagged sections (header / tab bar / tab panel / activity rail) stagger in.
  const isrStageRef = useStageReveal(String(lead.id), { revealOnFirstMount: true });

  // Stage 2: Interaction Summary modal state + draft.
  const [summaryModalOpen, setSummaryModalOpen] = useState(false);
  const [summaryDraft, setSummaryDraft] = useState('');
  const [summarySaving, setSummarySaving] = useState(false);

  const [editingContact, setEditingContact] = useState(false);
  /** Field paths the user has typed into while editing. Only these may raise
   *  blocking validation errors — seeded n8n values (possibly legacy formats)
   *  must never disable the Edit/Save buttons on their own. */
  const [touchedContactKeys, setTouchedContactKeys] = useState<Set<string>>(new Set());
  const [noteDraft, setNoteDraft] = useState('');
  const [localSaving, setLocalSaving] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [savedMessage, setSavedMessage] = useState('');
  /** 'success' (green) or 'approval' (amber) — company edits are suggestions. */
  const [savedMessageTone, setSavedMessageTone] = useState<'success' | 'approval'>('success');
  const savedTimerRef = useRef<number | null>(null);

  // ── Sales autosave (debounced) ─────────────────────────────────────────────
  // The stepper updates the UI INSTANTLY; the n8n save fires after a five-second
  // quiet period so rapid status/substatus exploration collapses into one
  // activity and one request. A pending change
  // flushes immediately on close / lead change / unmount. The refs are
  // declared BEFORE the lead-reset effect so the flush effect below runs
  // first on a lead change (effects run in declaration order) — a pending
  // save from the PREVIOUS lead lands before the state is wiped.
  const salesAutosaveDelayMs = 8000;
  const salesSaveTimerRef = useRef<number | null>(null);
  const pendingSalesRef = useRef<ISRSalesState | null>(null);
  const pendingSalesOriginRef = useRef<ISRSalesState | null>(null);
  // Timestamp of the last COMPLETED sales flush. The save optimistically
  // bumps the lead's updatedAt in the caches, which retriggers the lead-reset
  // effect below — that reset must not clobber the stepper while a change is
  // pending or has just landed.
  const salesJustSavedRef = useRef<number>(0);
  // Always points at the latest flush implementation (assigned each render
  // after `persist` is defined below).
  const salesFlushRef = useRef<() => void>(() => undefined);
  /** Latest flush implementation for the unload backstops — assigned each
   *  render right after salesFlushRef (see flushPendingSales below). */
  const unloadFlushRef = useRef<() => void>(() => undefined);

  // Tab close / refresh / navigate-away while a debounced sales save is still
  // pending: fire it during pagehide so it races the teardown instead of dying
  // with the page. (beforeunload is NOT used — Chrome's permissions policy
  // rejects unload handlers in this document. pagehide is the supported
  // lifecycle event and fires on close, refresh, and navigation alike;
  // best-effort — a hard kill mid-request can still cancel the save.)
  useEffect(() => {
    const flushOnUnload = () => {
      if (pendingSalesRef.current !== null) unloadFlushRef.current();
    };
    window.addEventListener('pagehide', flushOnUnload);
    return () => {
      window.removeEventListener('pagehide', flushOnUnload);
    };
  }, []);

  useEffect(() => {
    // Lead change (prev/next navigation): save whatever sales change is still
    // pending before the reset effect swaps the workspace to the new lead.
    salesFlushRef.current();
  }, [lead.id, lead.updatedAt]);
  useEffect(() => () => { salesFlushRef.current(); }, []);

  // Track the lead ID this effect last processed, so a same-lead updatedAt
  // bump (post-save refetch landing) can never interrupt an ACTIVE contact
  // editing session — it used to force-close the editor and wipe the draft.
  const resetLeadIdRef = useRef<string | number | null>(null);
  useEffect(() => {
    // A sales change is pending or was just saved: the workspace state already
    // holds the user's (saved) values, and re-seeding from the lead prop here
    // could paint a stale status over them (the save's optimistic cache patch
    // bumps updatedAt, which retriggers this effect). Lead-switch resets are
    // unaffected — the flush effect above clears pendingSalesRef first, and
    // the 1.5s grace window only shields the just-saved tick.
    if (pendingSalesRef.current || Date.now() - salesJustSavedRef.current < 1500) return;

    const isNewLead = resetLeadIdRef.current !== lead.id;
    resetLeadIdRef.current = lead.id;
    // Same lead, refreshed record (post-save invalidation landed): re-seeding
    // would close an open editor and discard the in-progress draft. Skip.
    if (!isNewLead && editingContact) return;

    setWorkspaceState(initialState);
    setLastSavedState(initialState);
    setActiveTab(initialTab);
    setEditingContact(false);
    setNoteDraft('');
    setLocalError(null);
    setTouchedContactKeys(new Set());
  }, [lead.id, lead.updatedAt, initialTab, editingContact]);

  // Phone country-code prefill now happens inline in the "Edit details" button
// handler (same state update that sets editingContact), so validation always
// sees the prefilled value on the first render. No useEffect needed.

  const snapshot = useMemo(() => snapshotFromState(workspaceState), [workspaceState]);
  const isDirty = serializeSnapshot(snapshot) !== serializeSnapshot(snapshotFromState(lastSavedState));
  const isSalesActivityOnlyDirty = useMemo(() => {
    const salesChanged = workspaceState.sales.lead_status !== lastSavedState.sales.lead_status ||
      workspaceState.sales.lead_substatus !== lastSavedState.sales.lead_substatus;
    if (!salesChanged || JSON.stringify(workspaceState.contact) !== JSON.stringify(lastSavedState.contact)) return false;

    const currentNotes = workspaceState.notes.notes;
    const savedNotes = lastSavedState.notes.notes;
    const notesAreUnchanged = JSON.stringify(currentNotes) === JSON.stringify(savedNotes);
    const onlyStatusActivityWasAppended = currentNotes.length === savedNotes.length + 1 &&
      currentNotes[currentNotes.length - 1]?.activity_type === 'status_change' &&
      JSON.stringify(currentNotes.slice(0, -1)) === JSON.stringify(savedNotes);
    return notesAreUnchanged || onlyStatusActivityWasAppended;
  }, [lastSavedState, workspaceState]);
  const needsDiscardConfirmation = isDirty && !isSalesActivityOnlyDirty;
  const validation = useMemo(
    () => validateContact(workspaceState.contact, touchedContactKeys),
    [workspaceState.contact, touchedContactKeys],
  );
  const validationErrors = validation.errors;
  const validationWarnings = validation.warnings;
  const hasValidationErrors = Object.keys(validationErrors).length > 0;
  const notesParseError = lead.isrNotesParseError
    ? 'The stored notes value is not valid SDR notes JSON.'
    : '';
  const busy = saving || localSaving;
  const blocked = busy || Boolean(notesParseError);

  useEffect(() => {
    // Status-only work auto-saves after the quiet period or flushes on drawer
    // unmount, so it must not trigger the shell's "discard changes" guard.
    onDirtyChange?.(needsDiscardConfirmation);
  }, [needsDiscardConfirmation, onDirtyChange]);

  // Workspace mount timing: log when the component finishes its first render.
  // Separate from the controller-level "hydrating→ready" which measures the
  // data-fetch span — this captures the component lifecycle itself.
  const workspaceMountedRef = useRef(false);
  useEffect(() => {
    if (workspaceMountedRef.current) return;
    workspaceMountedRef.current = true;
    timingLog('init', 'workspace mounted', {
      lead: lead.id,
      campaign: lead.campaignId,
      hydrating,
      editable,
      totalCalls: getExecCount(),
    });
  }, [lead.id, hydrating]);

  useEffect(() => () => {
    if (savedTimerRef.current) window.clearTimeout(savedTimerRef.current);
  }, []);

  const showSaved = (message: string, tone: 'success' | 'approval' = 'success') => {
    setSavedMessage(message);
    setSavedMessageTone(tone);
    if (savedTimerRef.current) window.clearTimeout(savedTimerRef.current);
    savedTimerRef.current = window.setTimeout(() => setSavedMessage(''), 2600);
  };

  /**
   * Diff the next state against the last saved state to produce
   * changed_fields + previous_values + a rich updated structure.
   * Every field is compared as a string (null → '') so n8n receives
   * consistent text/null values, matching the format it stores — no
   * type mismatches.  The updated object includes both new and previous
   * values, categorized by change domain (status, substatus,
   * contact/personLvl2, contact/company, notes).
   *
   * PERSON LVL2 contact changes are applied directly to the lead's lvl2 record.
   * COMPANY LVL1 contact changes are SUGGESTIONS only — companies are shared
   * records across all users; a backend approval process reviews and applies
   * them separately.
   */
  const computeDiff = (next: WorkspaceState, prev: WorkspaceState) => {
    const changed: string[] = [];
    const previous: Record<string, string | null> = {};
    const personLvl2Updates: NonNullable<NonNullable<ISRUpdateDiff['values']['personLvl2']>> = {};
    const companyUpdates: NonNullable<ISRUpdateDiff['values']['company']> = {};

    const flatField = (scope: 'person' | 'company', key: string, state: WorkspaceState): string | null => {
      const val = (state.contact[scope] as unknown as Record<string, unknown>)[key];
      if (val === null || val === undefined) return null;
      return String(val);
    };

    // Contact fields
    (['person', 'company'] as const).forEach((scope) => {
      const keys = Object.keys(next.contact[scope]).filter((k) => k !== 'id');
      keys.forEach((key) => {
        const nextVal = flatField(scope, key, next);
        const prevVal = flatField(scope, key, prev);
        if (nextVal !== prevVal) {
          const path = `${scope}.${key}`;
          changed.push(path);
          previous[path] = prevVal;
          if (scope === 'person') personLvl2Updates[key] = { new_value: nextVal, previous_value: prevVal };
          // Company fields are shared lvl1 records — mark every change as a
          // suggestion requiring backend approval, never a direct write.
          if (scope === 'company') companyUpdates[key] = { new_value: nextVal, previous_value: prevVal, requires_approval: true };
        }
      });
    });

    // Sales status
    const statusChanged = next.sales.lead_status !== prev.sales.lead_status;
    if (statusChanged) {
      changed.push('lead_status');
      previous.lead_status = prev.sales.lead_status ?? null;
    }
    const substatusChanged = next.sales.lead_substatus !== prev.sales.lead_substatus;
    if (substatusChanged) {
      changed.push('lead_substatus');
      previous.lead_substatus = prev.sales.lead_substatus ?? null;
    }

    // Notes — compare serialized to detect any change
    const nextNotes = JSON.stringify(next.notes);
    const prevNotes = JSON.stringify(prev.notes);
    const notesChanged = nextNotes !== prevNotes;
    if (notesChanged) {
      changed.push('notes');
      previous.notes = prevNotes !== 'undefined' ? prevNotes : null;
    }

    const hasContactChanges = Object.keys(personLvl2Updates).length > 0 || Object.keys(companyUpdates).length > 0;

    /** Build the rich updated object only when at least one thing changed. */
    const updated: ISRUpdateDiff | undefined =
      statusChanged || substatusChanged || hasContactChanges || notesChanged
        ? {
            status: statusChanged,
            substatus: substatusChanged,
            contact: hasContactChanges,
            notes: notesChanged,
            values: {
              ...(statusChanged
                ? {
                    status: {
                      new_value: next.sales.lead_status,
                      previous_value: prev.sales.lead_status ?? null,
                    },
                  }
                : {}),
              ...(substatusChanged
                ? {
                    substatus: {
                      new_value: next.sales.lead_substatus,
                      previous_value: prev.sales.lead_substatus ?? null,
                    },
                  }
                : {}),
              ...(notesChanged
                ? {
                    notes: {
                      new_value: next.notes,
                      previous_value: prev.notes,
                    },
                  }
                : {}),
              ...(Object.keys(personLvl2Updates).length > 0 ? { personLvl2: personLvl2Updates } : {}),
              ...(Object.keys(companyUpdates).length > 0 ? { company: companyUpdates } : {}),
            },
          }
        : undefined;

    return { changed_fields: changed, previous_values: previous, updated };
  };

  const persist = useCallback(async (next: WorkspaceState, successMessage = 'SDR changes saved') => {
    if (notesParseError) return false;
    setWorkspaceState(next);
    setLocalSaving(true);
    setLocalError(null);
    try {
      // Compute the diff so n8n receives ONLY the fields that actually changed,
      // plus their previous values for validation/audit. The full snapshot is
      // still sent (the contract requires it), but changed_fields + previous_values
      // tell the workflow exactly what to write.
      const diff = computeDiff(next, lastSavedState);
      await Promise.resolve(onSaveSnapshot(snapshotFromState(next), diff));
      setLastSavedState(next);
      // Saved values are now the source of truth — a re-edit starts clean.
      setTouchedContactKeys(new Set());

      // Company edits are shared-lvl1-record SUGGESTIONS (never a direct
      // write). Reflect that in the confirmation: amber "submitted for
      // approval", not green "saved". Person/notes/sales keep the success tone.
      const hasCompanyChange =
        Boolean(diff?.updated?.values.company) &&
        Object.keys(diff.updated.values.company).length > 0;
      // Company-only change: no person fields AND no status/substatus/notes
      // category actually changed (the updated booleans are false).
      const haveOnlyCompanyChanges = hasCompanyChange &&
        diff.updated !== undefined &&
        diff.updated.values.personLvl2 === undefined &&
        diff.updated.status === false &&
        diff.updated.substatus === false &&
        diff.updated.notes === false;

      if (haveOnlyCompanyChanges) {
        showSaved('Company changes submitted for approval', 'approval');
      } else if (hasCompanyChange) {
        showSaved('Saved — company changes submitted for approval', 'approval');
      } else {
        showSaved(successMessage, 'success');
      }
      return true;
    } catch (error) {
      setLocalError(error instanceof Error ? error.message : 'Could not save SDR changes.');
      return false;
    } finally {
      setLocalSaving(false);
    }
  }, [notesParseError, onSaveSnapshot, lastSavedState, showSaved]);

  // ── Sales autosave (debounced) — see the refs/effects declared before the
  // lead-reset effect above. This is the implementation those refs point to.
  const flushPendingSales = useCallback(() => {
    if (salesSaveTimerRef.current !== null) {
      window.clearTimeout(salesSaveTimerRef.current);
      salesSaveTimerRef.current = null;
    }
    const sales = pendingSalesRef.current;
    const previousSales = pendingSalesOriginRef.current || lastSavedState.sales;
    pendingSalesRef.current = null;
    pendingSalesOriginRef.current = null;
    if (!sales) { return; }

    const activity = salesActivityCopy(previousSales, sales);
    // If the user explores the pipeline and returns to the original state
    // within the quiet period, there is nothing to persist or log.
    if (!activity) { return; }

    timingLog('salesSave', 'flush start', {
      previousStatus: previousSales.lead_status,
      previousSubstatus: previousSales.lead_substatus,
      newStatus: sales.lead_status,
      newSubstatus: sales.lead_substatus,
      activityBody: activity.body,
    });
    const flushT0 = performance.now();
    const statusNote: ISRNote | null = salesEnabled ? {
      id: globalThis.crypto?.randomUUID?.() || `isr-status-${Date.now()}`,
      title: activity.title,
      body: activity.body,
      icon: activity.icon,
      activity_type: 'status_change',
      created_at: new Date().toISOString(),
      created_by: currentUser.id || currentUser.email || undefined,
      created_by_name: currentUser.name || currentUser.email || 'SDR',
    } : null;
    const nextNotes: ISRNotesDocument = statusNote ? {
      schema_version: 1,
      notes: [...workspaceState.notes.notes, statusNote],
    } : workspaceState.notes;

    if (onSaveSalesState) {
      setLocalSaving(true);
      setLocalError(null);

      // Build the structured updated object for the sales save path.
      const salesUpdated: ISRUpdateDiff = {
        status: sales.lead_status !== previousSales.lead_status,
        substatus: sales.lead_substatus !== previousSales.lead_substatus,
        contact: false,
        notes: true,
        values: {
          status: {
            new_value: sales.lead_status,
            previous_value: previousSales.lead_status ?? null,
          },
          substatus: {
            new_value: sales.lead_substatus,
            previous_value: previousSales.lead_substatus ?? null,
          },
          notes: {
            new_value: nextNotes,
            previous_value: workspaceState.notes,
          },
        },
      };

      Promise.resolve(onSaveSalesState(sales, nextNotes, salesUpdated))
        .then(() => {
          timingLog('salesSave', 'flush complete (onSaveSalesState)', {
            durationMs: performance.now() - flushT0,
            status: sales.lead_status,
            substatus: sales.lead_substatus,
          });
          salesJustSavedRef.current = Date.now();
          setWorkspaceState((current) => ({ ...current, sales, notes: nextNotes }));
          setLastSavedState((current) => ({ ...current, sales, notes: nextNotes }));
          showSaved('Sales status updated');
        })
        .catch((error) => {
          timingLog('salesSave', 'flush error (onSaveSalesState)', {
            durationMs: performance.now() - flushT0,
            error: (error as Error)?.message,
          });
          // The flush often runs during/after drawer teardown (close-flush,
          // unload backstop) — setLocalError alone would vanish with the
          // component and the user would never learn the save failed. Toasts
          // live at the app root, so they survive unmount.
          showErrorToast(error instanceof Error
            ? `Sales status not saved: ${error.message}`
            : 'Sales status not saved.');
          setLocalError(error instanceof Error ? error.message : 'Could not save the sales status.');
        })
        .finally(() => setLocalSaving(false));
      return;
    }
    persist({
      ...workspaceState,
      sales,
      notes: nextNotes,
    }, 'Sales status updated').then((saved) => {
      if (saved) salesJustSavedRef.current = Date.now();
      timingLog('salesSave', 'flush complete (persist)', { durationMs: performance.now() - flushT0, saved });
    }).catch(() => {
      // Error already handled inside persist.
      timingLog('salesSave', 'flush complete (persist)', { durationMs: performance.now() - flushT0, saved: false });
    });
  }, [currentUser.email, currentUser.id, currentUser.name, lastSavedState.sales, onSaveSalesState, persist, salesEnabled, showSaved, workspaceState]);
  salesFlushRef.current = flushPendingSales;
  unloadFlushRef.current = flushPendingSales;

  /**
   * Sales status/substatus — instant UI update + DEBOUNCED save. Clicks feel
   * immediate; the persistence lands once the user stops changing the state
   * (or on close/lead-switch via the flush effects).
   */
  const handleSalesChange = useCallback((sales: ISRSalesState) => {
    // UI reflects the new state immediately — nothing waits on the network.
    setWorkspaceState((current) => ({ ...current, sales }));
    const statusChanged = lastSavedState.sales.lead_status !== sales.lead_status;
    const substatusChanged = lastSavedState.sales.lead_substatus !== sales.lead_substatus;
    if (!pendingSalesRef.current) pendingSalesOriginRef.current = lastSavedState.sales;
    pendingSalesRef.current = sales;
    if (salesSaveTimerRef.current !== null) window.clearTimeout(salesSaveTimerRef.current);
    timingLog('salesSave', 'status click', {
      newStatus: sales.lead_status,
      newSubstatus: sales.lead_substatus,
      previousStatus: lastSavedState.sales.lead_status,
      previousSubstatus: lastSavedState.sales.lead_substatus,
      statusChanged,
      substatusChanged,
      debounceMs: salesAutosaveDelayMs,
    });
    salesSaveTimerRef.current = window.setTimeout(() => {
      salesSaveTimerRef.current = null;
      salesFlushRef.current();
    }, salesAutosaveDelayMs);
  }, [lastSavedState.sales]);

  /**
   * Start auto-outreach — fires the N8N outreach workflow for this lead.
   * Mirrors the Sidebar's handleOutreach: optimistic status flip to
   * OUTREACHING, then the n8n sendToOutreach call. On error the caches are
   * invalidated so the status reverts from the server.
   */
  const handleStartOutreach = useCallback(async () => {
    if (!effectiveUserEmail || !lead.campaignId || !lead.id) return;
    const campaignId = String(lead.campaignId);
    const lvl2Id = String(lead.id);
    const traceId = newTraceId();
    timingLog('outreach', 'startOutreach click', { campaign: campaignId, lead: lvl2Id, trace: traceId, totalCalls: getExecCount() });
    const t0 = performance.now();
    try {
      // Optimistic: flip both the detail + list caches to OUTREACHING so the
      // UI reflects the start immediately (same pattern as the Sidebar).
      queryClient.setQueryData(
        queryKeys.leads.detail(campaignId, lvl2Id),
        (old: any) => old ? { ...old, status: 'OUTREACHING' } : old,
      );
      timingLog('outreach', 'optimistic cache set', { campaign: campaignId, lead: lvl2Id, trace: traceId, status: 'OUTREACHING' });
      queryClient.setQueryData(
        queryKeys.leads.byCampaign(campaignId, effectiveUserEmail),
        (oldData: any[] | undefined) => oldData
          ? oldData.map((row: any) => String(row.id) === lvl2Id ? { ...row, status: 'OUTREACHING' } : row)
          : oldData,
      );
      await n8nService.sendToOutreach({
        campaign_id: campaignId,
        lead_ids: [lvl2Id],
        outreach_type: 'both',
        client_email: effectiveUserEmail,
        action: 'outreach',
      });
      timingLog('outreach', 'startOutreach complete', { campaign: campaignId, lead: lvl2Id, trace: traceId, durationMs: performance.now() - t0, totalCalls: getExecCount() });
    } catch (error) {
      timingLog('outreach', 'startOutreach error', { campaign: campaignId, lead: lvl2Id, trace: traceId, durationMs: performance.now() - t0, error: (error as Error)?.message });
      // Revert by invalidating so the server truth wins.
      queryClient.invalidateQueries({ queryKey: queryKeys.leads.detail(campaignId, lvl2Id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.leads.byCampaign(campaignId) });
      setLocalError(error instanceof Error ? error.message : 'Could not start outreach. Please try again.');
    }
  }, [effectiveUserEmail, lead.campaignId, lead.id, queryClient]);

  const handleRequestOutreach = useCallback(() => {
    setShowUnlockOutreachModal(true);
  }, []);

  const handleContactChange = (scope: 'person' | 'company', key: string, value: string) => {
    // Social handles are normalized live: lowercase + expanded to a full URL.
    const nextValue = key in SOCIAL_PROFILE_BUILDERS ? normalizeSocialValue(key, value) : value;
    setTouchedContactKeys((touched) => new Set(touched).add(`${scope}.${key}`));
    setWorkspaceState((current) => ({
      ...current,
      contact: {
        ...current.contact,
        [scope]: { ...current.contact[scope], [key]: nextValue },
      } as ContactDraft,
    }));
  };

  /** Icon-to-title mapping so "SDR Call", "SDR Insight", etc. replace the
   *  generic "SDR note" — the icon selector determines the title. */
  const NOTE_TITLE_BY_ICON: Record<string, string> = {
    'bx:phone-call': 'SDR Call',
    'bx:envelope': 'SDR Email',
    'bx:calendar': 'SDR Meeting',
    'bx:check-square': 'SDR Task',
    'bx:bulb': 'SDR Insight',
    'bx:magnet': 'SDR Magnet',
  };

  const handleAddNote = async (icon: string): Promise<boolean> => {
    const body = noteDraft.trim();
    if (!body || blocked) return false;
    const newNote: ISRNote = {
      id: globalThis.crypto?.randomUUID?.() || `isr-note-${Date.now()}`,
      title: NOTE_TITLE_BY_ICON[icon] || 'SDR Note',
      body,
      icon,
      activity_type: 'note',
      created_at: new Date().toISOString(),
      created_by: currentUser.id || currentUser.email || undefined,
      created_by_name: currentUser.name || currentUser.email || 'SDR',
    } as ISRNote;
    const next: WorkspaceState = {
      ...workspaceState,
      notes: {
        schema_version: 1,
        notes: [...workspaceState.notes.notes, newNote],
      } as ISRNotesDocument,
    };
    const saved = await persist(next, 'Note added to the SDR timeline');
    if (saved) setNoteDraft('');
    return saved;
  };

  const handleUpdateNote = async (noteId: string, body: string) => {
    if (blocked) return;
    const next: WorkspaceState = {
      ...workspaceState,
      notes: {
        schema_version: 1,
        notes: workspaceState.notes.notes.map((note) =>
          note.id === noteId ? { ...note, body } : note,
        ),
      } as ISRNotesDocument,
    };
    await persist(next, 'Note updated');
  };

  const handleDeleteNote = async (noteId: string) => {
    if (blocked) return;
    const next: WorkspaceState = {
      ...workspaceState,
      notes: {
        schema_version: 1,
        notes: workspaceState.notes.notes.filter((note) => note.id !== noteId),
      } as ISRNotesDocument,
    };
    await persist(next, 'Note deleted');
  };

  /** Stage 2: Save the Interaction Summary — creates/replaces the summary note. */
  const handleSaveSummary = async () => {
    const trimmed = summaryDraft.trim();
    if (!trimmed || blocked) return;
    const existingIndex = workspaceState.notes.notes.findIndex(
      (n) => n.activity_type === 'interaction_summary',
    );
    const summaryNote: ISRNote = {
      id: globalThis.crypto?.randomUUID?.() || `isr-summary-${Date.now()}`,
      title: 'Interaction Summary',
      body: trimmed,
      icon: 'bx:conversation',
      activity_type: 'interaction_summary',
      created_at: new Date().toISOString(),
      created_by: currentUser.id || currentUser.email || undefined,
      created_by_name: currentUser.name || currentUser.email || 'SDR',
    } as ISRNote;
    const nextNotes = [...workspaceState.notes.notes];
    if (existingIndex >= 0) {
      nextNotes[existingIndex] = summaryNote;
    } else {
      nextNotes.push(summaryNote);
    }
    const next: WorkspaceState = {
      ...workspaceState,
      notes: { schema_version: 1, notes: nextNotes } as ISRNotesDocument,
    };
    setSummarySaving(true);
    try {
      const saved = await persist(next, 'Interaction summary saved');
      if (saved) {
        setSummaryModalOpen(false);
        setSummaryDraft('');
      }
    } finally {
      setSummarySaving(false);
    }
  };

  const requestClose = () => {
    const hasPendingSalesSave = pendingSalesRef.current !== null;
    if (hasPendingSalesSave) {
      // A pending sales selection is intentionally saved on close. Only warn
      // when contact/note edits outside that autosave are also still dirty.
      const stateWithoutPendingSales: WorkspaceState = {
        ...workspaceState,
        sales: lastSavedState.sales,
      };
      const hasOtherUnsavedChanges = serializeSnapshot(snapshotFromState(stateWithoutPendingSales)) !==
        serializeSnapshot(snapshotFromState(lastSavedState));
      if (hasOtherUnsavedChanges && !window.confirm('Discard your other unsaved SDR changes?')) return;
      salesFlushRef.current();
      onClose();
      return;
    }
    // An in-flight save is already durable work; closing must not ask the user
    // to discard it or trigger a duplicate request.
    if (isDirty && !busy && !window.confirm('Discard your unsaved SDR changes?')) return;
    onClose();
  };

  const displayError = saveError || localError;
  const [reEnriching, setReEnriching] = useState(false);
  // SQL (Activity) report link persisted on the lvl2 row by the reports
  // pipeline — when present, the header shows a grouped Download button.
  const activityReportUrl = useMemo(() => {
    const u = (lead.personLvl2 as any)?.report_activity_url ?? (lead.rawOriginalData as any)?.lvl2?.report_activity_url;
    return typeof u === 'string' && u.trim().startsWith('http') ? u.trim() : null;
  }, [lead]);

  const handleReEnrich = useCallback(async () => {
    if (reEnriching || !lead.campaignId) return;
    setReEnriching(true);
    setLocalError(null);
    try {
      const response = await n8nService.enrichUpdateLeads({
        campaign_id: String(lead.campaignId),
        lead_ids: [String(lead.id)],
        client_email: currentUser.email || currentUser.name || '',
        client_id: currentUser.id || undefined,
        client_name: currentUser.name || undefined,
      });
      if (!response.success) {
        throw new Error(response.error || 'Re-enrich request failed');
      }
      showSaved('Re-enrich triggered — refreshed data will appear shortly', 'success');
    } catch (error: any) {
      setLocalError(error.message || 'Could not trigger re-enrich');
    } finally {
      setReEnriching(false);
    }
  }, [lead.campaignId, lead.id, currentUser, reEnriching]);

  // lvl1 IDs for Person and Company — used as click-to-copy #tags in the
  // contact card headers.
  const rawOriginal = (lead.rawOriginalData || {}) as Record<string, any>;
  const personLvl1Id = lead.personLvl1?.id ?? rawOriginal.lvl1_person?.id;
  const companyLvl1Id = lead.companyLvl1?.id ?? rawOriginal.lvl1_company?.id;

  // Header account line — industry parsed from CNAE text. N8N packs multiple
  // codes into one string like "69.20 Accounting 69.10 Legal 70.22 Consulting"
  // (with 'unknown' segments for failed enrichment). The platform manages ONE
  // CNAE per company — keep only the first entry with a REAL label so the
  // header never renders a comma blob or the sentinel.
  const cnaeEntries: Array<{ code: string; label: string }> = (() => {
    const raw = firstPresentText(lead.cnae_text);
    if (!raw) return [];
    const isSentinel = (text: string) => ['unknown', 'n/a', ''].includes(text.trim().toLowerCase());
    // Match patterns like "69.20 Accounting and auditing" — code + text until
    // the next code or end.
    const matches = raw.match(/\d{2}\.\d{2}\s+[^0-9]+/g);
    if (matches && matches.length > 0) {
      return matches
        .map((entry) => {
          const trimmed = entry.trim();
          const codeMatch = trimmed.match(/^(\d{2}\.\d{2})\s+(.+)$/);
          return codeMatch
            ? { code: codeMatch[1], label: codeMatch[2].trim() }
            : { code: '', label: trimmed };
        })
        .filter((entry) => !isSentinel(entry.label))
        .slice(0, 1);
    }
    // No code pattern — treat the whole string as one label.
    return isSentinel(raw) ? [] : [{ code: firstCnaeCode(lead.cnae_code) || '', label: raw }];
  })();
  const headerIndustry = cnaeEntries.length
    ? cnaeEntries[0].label
    : firstPresentText(lead.companyIndustry, lead.industry);

  return (
    <div ref={isrStageRef} className={`flex h-full min-h-0 flex-col overflow-hidden bg-[#0a0b0e] text-white ${className}`}>
      {/* Sales status band — at the very top so the stage is the first thing
          the user sees, before company name / industry / contact info.
          Lead navigation sits at the right edge, inline with the substatus
          row, styled with the Activity column background to bridge the two. */}
      <div className="shrink-0">
        <SalesStateControl
          leadStatus={lead.status}
          state={workspaceState.sales}
          disabled={!salesEnabled || Boolean(notesParseError)}
          onChange={handleSalesChange}
          onPreviousLead={onPreviousLead}
          onNextLead={onNextLead}
          hasPreviousLead={hasPreviousLead}
          hasNextLead={hasNextLead}
          leadPosition={leadPosition}
          totalLeads={totalLeads}
          onClose={onClose}
          hasSalesModule={hasSalesModule}
          onStartOutreach={handleStartOutreach}
          onRequestOutreach={handleRequestOutreach}
        />
      </div>

      {/* Main content + Activity side-by-side. The header is INSIDE the main
          column so the Activity aside starts at the SAME vertical position as
          the company name — no gap, no offset. */}
      <div className="flex min-h-0 flex-1">
        <div className="flex min-w-0 flex-1 flex-col">
        {/* Close / nav buttons float top-right in the content area. */}
        {showCloseButton && (
          <button type="button" onClick={requestClose} aria-label="Close SDR workspace" className="absolute right-3 top-3 z-20 flex h-8 w-8 items-center justify-center rounded-lg bg-white/[0.06] text-white/60 backdrop-blur-sm transition-colors hover:bg-white/[0.12] hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60"><Icon icon="bx:x" className="h-4.5 w-4.5" aria-hidden="true" /></button>
        )}

        {/* Company name + industry + ICP — header row. Industry falls back to
            the first CNAE entry label when no explicit companyIndustry exists. */}
        <div className="flex min-w-0 flex-col px-4 pb-0.5 pt-4 sm:px-6">
          <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
            <h1 id="isr-workspace-title" className="flex min-w-0 items-center gap-1.5 truncate text-[14px] font-semibold text-white/90">
              <Icon icon="bx:buildings" className="h-3.5 w-3.5 shrink-0 text-white/45" aria-hidden="true" />
              <span className="truncate">{lead.companyName || 'Company'}</span>
            </h1>
            {headerIndustry && (
              <span className="truncate text-[11px] text-white/50">{headerIndustry}</span>
            )}
            <span className="rounded-md border border-white/10 px-1.5 py-0.5 text-[11px] text-white/45">ICP {lead.icpScore ?? '—'}</span>
          </div>
        </div>

        {/* CNAE industry codes LEFT + Edit details button RIGHT — same row.
            The row always renders so the button stays reachable when a lead
            carries no CNAE codes (n8n packs multiple codes into one field;
            structured entries need their own line for readability). */}
        <div className="flex items-center justify-between gap-3 px-4 pb-4 sm:px-6">
          <div className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-2 gap-y-0.5 text-[12px] text-white/35">
            {cnaeEntries.length > 0 && (
              <>
                <span className="mr-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-white/45">Industry (NACE/CNAE)</span>
                {cnaeEntries.map((entry, index) => (
                  <span key={`${entry.code}-${index}`}>
                    {entry.code && <span className="font-mono text-[11px] text-cyan-200/60">{entry.code}</span>} {entry.label}
                    {index < cnaeEntries.length - 1 && <span className="ml-1 text-white/20">·</span>}
                  </span>
                ))}
              </>
            )}
          </div>
          {editable && (
            <div className="flex shrink-0 items-center gap-1.5">
              {editingContact && (
                <button type="button" disabled={busy} onClick={() => { setWorkspaceState((current) => ({ ...current, contact: lastSavedState.contact })); setTouchedContactKeys(new Set()); setEditingContact(false); }} className="h-8 rounded-lg border border-white/15 bg-white/[0.04] px-3 text-[12px] font-medium text-white/70 transition-colors hover:bg-white/[0.08] hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/40 disabled:opacity-40">Cancel</button>
              )}
              <button
                type="button"
                disabled={busy || Boolean(notesParseError) || hasValidationErrors}
                onClick={async () => {
                  if (editingContact) {
                    const diff = computeDiff(
                      { ...workspaceState, contact: workspaceState.contact },
                      lastSavedState,
                    );
                    const hasContactChanges = diff?.updated?.contact === true;
                    if (hasContactChanges) {
                      const changedFields = (diff?.changed_fields || [])
                        .filter((f) => f.startsWith('person.') || f.startsWith('company.'))
                        .map((f) => f.replace(/^(person|company)\./, '').replace(/_id$/, '').replace(/_/g, ' ')
                          .replace(/^role position$/, 'role'));
                      const noteBody = changedFields.length
                        ? `Contact details updated: ${changedFields.join(', ')}`
                        : 'Contact details updated';
                      const activityNote: ISRNote = {
                        id: globalThis.crypto?.randomUUID?.() || `isr-note-${Date.now()}`,
                        title: 'Contact updated',
                        body: noteBody,
                        icon: 'bx:edit-alt',
                        activity_type: 'note',
                        created_at: new Date().toISOString(),
                        created_by: currentUser.id || currentUser.email || undefined,
                        created_by_name: currentUser.name || currentUser.email || 'SDR',
                      };
                      const next = {
                        ...workspaceState,
                        contact: workspaceState.contact,
                        notes: {
                          schema_version: 1 as const,
                          notes: [...workspaceState.notes.notes, activityNote],
                        },
                      };
                      const saved = await persist(next, 'Contact & activity saved');
                      if (saved) setEditingContact(false);
                    } else {
                      // No actual changes — save the snapshot as-is without a note.
                      const next = {
                        ...workspaceState,
                        contact: workspaceState.contact,
                      };
                      const saved = await persist(next, 'Contact saved');
                      if (saved) setEditingContact(false);
                    }
                  } else {
                    const code = leadDialingCode(lead);
                    setWorkspaceState((current) => {
                      const padScope = (scope: 'person' | 'company'): ContactDraft['person'] | ContactDraft['company'] => {
                        const contact = current.contact[scope] as unknown as Record<string, unknown>;
                        if (!code) return contact as unknown as ContactDraft['person'] | ContactDraft['company'];
                        const seeded: Record<string, unknown> = { ...contact };
                        (['phone_id', 'mobile_id', 'whatsapp_id'] as const).forEach((key) => {
                          const raw = String(seeded[key] || '').trim();
                          if (raw && !hasCountryCode(raw)) seeded[key] = `${code} ${raw}`;
                        });
                        return seeded as unknown as ContactDraft['person'] | ContactDraft['company'];
                      };
                      return {
                        ...current,
                        contact: {
                          person: padScope('person') as ContactDraft['person'],
                          company: padScope('company') as ContactDraft['company'],
                        } as ContactDraft,
                      };
                    });
                    setEditingContact(true);
                  }
                }}
                className="inline-flex h-7 items-center gap-1.5 rounded-md bg-white/[0.055] px-2.5 text-[11px] font-medium text-white/65 transition-colors hover:bg-white/[0.1] hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/40 disabled:opacity-40"
              >
                <Icon icon={editingContact ? 'bx:check' : 'bx:edit-alt'} className="h-3.5 w-3.5" aria-hidden="true" />
                {editingContact ? 'Save' : 'Edit details'}
              </button>
            </div>
          )}
        </div>
      <div className="px-4 pb-6 sm:px-6">
        {editingContact && localError && (
          <p className="mb-2 flex items-center gap-1.5 rounded-lg border border-rose-400/15 bg-rose-400/5 px-3 py-2 text-[11px] leading-5 text-rose-300">
            <Icon icon="bx:error-circle" className="h-4 w-4 shrink-0" aria-hidden="true" />
            <span>{localError}</span>
          </p>
        )}
        <div className={`grid gap-2.5 ${expanded ? '2xl:grid-cols-2' : 'xl:grid-cols-2'}`}>
          <div className="min-w-0 rounded-lg bg-white/[0.025] px-3 py-2.5 ring-1 ring-inset ring-white/[0.055]">
            <FieldGroup title="Person" icon="bx:user" scope="person" lead={lead} contact={workspaceState.contact} editing={editingContact} editable={editable} errors={validationErrors} warnings={validationWarnings} leadId={lead.id} onChange={handleContactChange} />
          </div>
          <div className={`min-w-0 rounded-lg px-3 py-2.5 ring-1 ring-inset ${editingContact ? 'bg-amber-400/[0.07] ring-amber-400/15' : 'bg-white/[0.025] ring-white/[0.055]'}`}>
            <FieldGroup title="Company" icon="bx:buildings" scope="company" lead={lead} contact={workspaceState.contact} editing={editingContact} editable={editable} errors={validationErrors} warnings={validationWarnings} leadId={companyLvl1Id} onChange={handleContactChange} />
          </div>
        </div>
      </div>

      <nav aria-label="SDR workspace sections" className="shrink-0 border-b border-white/[0.07] px-2 sm:px-3" data-view-block>
        <div role="tablist" className="flex min-w-0 items-stretch">
          <div className="-mb-px grid min-w-0 flex-1 auto-cols-fr grid-flow-col">
              {TAB_GROUPS[0].tabs.map((tab) => {
                const active = tab.key === activeTab;
                return (
                  <button
                    key={tab.key}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    aria-controls={`isr-panel-${tab.key}`}
                    id={`isr-tab-${tab.key}`}
                    title={tab.label}
                    onClick={() => setActiveTab(tab.key)}
                    className={`flex h-12 min-w-0 items-center justify-center gap-1.5 overflow-hidden border-b-2 px-0.5 text-[10px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-cyan-300/40 sm:text-[11px] sm:px-1 ${active ? 'border-cyan-300 bg-cyan-300/[0.035] text-white' : 'border-transparent text-white/45 hover:bg-white/[0.025] hover:text-white/80'}`}
                  >
                    <Icon icon={tab.icon} className={`h-4 w-4 shrink-0 ${active ? 'text-cyan-300' : ''}`} aria-hidden="true" />
                    <span className="block max-w-full truncate">{tab.label}</span>
                  </button>
                );
              })}
          </div>

          <div className="relative z-10 flex shrink-0 items-center border-l border-white/[0.1] bg-[#0b0e13] pl-1.5 shadow-[-10px_0_18px_rgba(5,7,11,0.72)] sm:pl-2">
            {TAB_GROUPS[1].tabs.map((tab) => {
              const active = tab.key === activeTab;
              const isSequence = tab.key === 'sequence';
              return (
                <button
                  key={tab.key}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  aria-controls={`isr-panel-${tab.key}`}
                  id={`isr-tab-${tab.key}`}
                  onClick={() => setActiveTab(tab.key)}
                  className={`inline-flex h-9 shrink-0 items-center gap-1.5 rounded-lg border px-1.5 text-[11px] font-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7f88ff]/60 sm:px-2 ${
                    isSequence
                      ? active
                        ? 'border-cyan-300/55 bg-cyan-300/15 text-cyan-100 shadow-[0_6px_18px_rgba(34,211,238,0.22)]'
                        : 'border-cyan-300/15 bg-cyan-300/[0.04] text-cyan-200/65 hover:border-cyan-300/35 hover:bg-cyan-300/[0.08] hover:text-cyan-100'
                      : active
                        ? 'border-[#7f88ff]/55 bg-[#5260FF] text-white shadow-[0_6px_18px_rgba(82,96,255,0.28)]'
                        : 'border-[#7f88ff]/20 bg-[#5260FF]/10 text-[#aeb4ff] hover:border-[#7f88ff]/40 hover:bg-[#5260FF]/16 hover:text-white'
                  }`}
                >
                  <Icon icon={tab.icon} className="h-4 w-4" aria-hidden="true" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </nav>

        <main id={`isr-panel-${activeTab}`} role="tabpanel" aria-labelledby={`isr-tab-${activeTab}`} className="custom-scrollbar min-h-0 w-full min-w-0 flex-1 overflow-y-auto [overscroll-behavior:contain]" data-view-block>
          <div className="w-full p-5 sm:p-7 lg:p-8">
            {hydrating ? (
              <div className="divide-y divide-white/[0.06]" aria-busy="true" aria-label="Loading lead briefing">
                <SkeletonSection lines={4} />
                <SkeletonSection lines={3} />
                <SkeletonSection lines={4} />
                <SkeletonSection lines={3} />
              </div>
            ) : (
              <>
                {activeTab === 'briefing' && <ISRBriefingTab data={lead} campaign={campaign} />}
                {activeTab === 'guidance' && <ISRGuidanceTab data={lead} campaign={campaign} />}
                {activeTab === 'pains' && <ISRPainsTab lead={lead} />}
                {activeTab === 'sequence' && <SequencePanel lead={lead} />}
                {activeTab === 'psych' && <ISRPsychologyTab lead={lead} />}
                {activeTab === 'network' && <ISRTeamTab lead={lead} />}
                {activeTab === 'signals' && <ISRSignalsTab lead={lead} />}
                {activeTab === 'opportunities' && <ISROpportunitiesTab lead={lead} />}
                {activeTab === 'campaign' && <CampaignContextTabForLead lead={lead} campaign={campaign} />}
              </>
            )}
          </div>
        </main>
        </div>
        <aside className="custom-scrollbar hidden min-w-0 shrink-0 basis-[23.076923%] overflow-y-auto border-l border-white/[0.08] bg-[#101319] shadow-[-18px_0_36px_rgba(0,0,0,0.16)] [overscroll-behavior:contain] xl:block" aria-label="Notes and activity" data-view-block>
          <ActivityRail
            lead={lead}
            notes={workspaceState.notes}
            noteDraft={noteDraft}
            setNoteDraft={setNoteDraft}
            onAddNote={handleAddNote}
            onUpdateNote={handleUpdateNote}
            onDeleteNote={handleDeleteNote}
            disabled={!editable || busy}
            hydrating={hydrating}
            parseError={notesParseError}
          />
        </aside>
      </div>

      <footer className="shrink-0 border-t border-white/[0.07]">
        <div className="flex min-w-0">
          {/* Left side: status text + action buttons */}
          <div className="flex min-w-0 flex-1 items-center justify-between gap-3 px-4 py-3 sm:px-6">
            <div className="min-w-0" aria-live="polite">
              {displayError ? (
                <p className="flex items-center gap-1.5 text-[12px] text-rose-300"><Icon icon="bx:error-circle" className="h-4 w-4 shrink-0" aria-hidden="true" /><span className="truncate">{displayError}</span></p>
              ) : notesParseError ? (
                <p className="flex items-center gap-1.5 text-[12px] text-amber-300"><Icon icon="bx:lock-alt" className="h-4 w-4 shrink-0" aria-hidden="true" />Saving blocked until notes JSON is recovered</p>
              ) : savedMessage ? (
                savedMessageTone === 'approval' ? (
                  <p className="flex items-center gap-1.5 text-[12px] text-amber-300"><Icon icon="bx:time-five" className="h-4 w-4 shrink-0" aria-hidden="true" />{savedMessage}</p>
                ) : (
                  <p className="flex items-center gap-1.5 text-[12px] text-emerald-300"><Icon icon="bx:check-circle" className="h-4 w-4 shrink-0" aria-hidden="true" />{savedMessage}</p>
                )
              ) : (
                <p className="text-[12px] text-white/35">{isDirty ? 'Unsaved changes — the complete SDR snapshot will be saved' : 'Powered by Brainiacs™ AI Engine. All SDR information is up to date'}</p>
              )}
            </div>
            {/* Wider gap than the default: Re-enrich sits beside + Report, and a
                mis-click there spends credits and re-runs enrichment. The extra
                separation is the cheapest guard against that. */}
            <div className="flex items-center gap-3 shrink-0">
              {isDirty && !editingContact && <button type="button" disabled={busy} onClick={() => { setWorkspaceState(lastSavedState); setTouchedContactKeys(new Set()); setEditingContact(false); }} className="h-9 rounded-lg border border-white/15 bg-white/[0.04] px-3.5 text-[12px] font-medium text-white/70 transition-colors hover:bg-white/[0.08] hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/40 disabled:opacity-40">Discard</button>}
              {!editingContact && (
              <>
              <button
                type="button"
                onClick={() => setActiveTab('campaign')}
                className={'inline-flex h-8 items-center gap-1.5 rounded-lg border px-3 text-[11px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7f88ff]/60 ' + (activeTab === 'campaign'
                  ? 'border-[#7f88ff]/55 bg-[#5260FF] text-white shadow-[0_6px_18px_rgba(82,96,255,0.28)]'
                  : 'border-white/12 bg-white/[0.03] text-white/45 hover:border-white/25 hover:bg-white/[0.07] hover:text-white/80')}
              >
                <Icon icon="bx:flag" className="h-3.5 w-3.5" aria-hidden="true" />
                Campaign
              </button>
              <button
                type="button"
                disabled={reEnriching}
                onClick={handleReEnrich}
                className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-white/12 bg-white/[0.03] px-3 text-[11px] font-medium text-white/45 transition-colors hover:border-white/25 hover:bg-white/[0.07] hover:text-white/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/40 disabled:opacity-40"
              >
                <Icon icon={reEnriching ? 'bx:loader-alt' : 'bx:refresh'} className={`h-3.5 w-3.5 ${reEnriching ? 'animate-spin motion-reduce:animate-none' : ''}`} aria-hidden="true" />
                {reEnriching ? 'Refreshing…' : 'Re-enrich'}
              </button>
              <div className="inline-flex h-8 items-stretch" role="group" aria-label="Lead reports">
                <button
                  type="button"
                  disabled={!lead.campaignId || !lead.id}
                  onClick={() => onGenerateReport?.(String(lead.id).replace(/[\"' ]/g,''), String(lead.campaignId).replace(/[\"' ]/g,''))}
                  className="inline-flex items-center gap-1.5 rounded-l-lg bg-white px-3 text-[11px] font-semibold text-[#0B0B0D] transition-colors hover:bg-[#e9ecff] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/40 disabled:opacity-40"
                  title="Generate the MQL or SQL report for this lead"
                >
                  <Icon icon="solar:document-add-bold" className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                  + Report
                </button>
                {activityReportUrl && (
                  <a
                    href={activityReportUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-r-lg border-l border-[#0B0B0D]/15 bg-white px-3 text-[11px] font-semibold text-[#0B0B0D] transition-colors hover:bg-[#e9ecff] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/40"
                    title="Download the previously generated Activity Report (SQL) for this lead"
                  >
                    <Icon icon="solar:download-minimalistic-bold" className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                    Activity Report
                  </a>
                )}
              </div>
              </>
              )}
            </div>
          </div>
          {/* Right side: Write Summary button — aligned under the Activity rail */}
          <div className="hidden shrink-0 basis-[23.076923%] items-center border-l border-white/[0.08] bg-[#101319] px-3 py-3 xl:flex">
            <button
              type="button"
              onClick={() => {
                setSummaryDraft(workspaceState.notes.notes.filter((n) => n.activity_type === 'interaction_summary').pop()?.body || '');
                setSummaryModalOpen(true);
              }}
              disabled={!editable || busy || Boolean(notesParseError)}
              className="inline-flex h-9 w-full items-center justify-center gap-1.5 rounded-lg border border-cyan-300/25 bg-cyan-400/10 px-3 text-[12px] font-semibold text-cyan-200 transition-colors hover:border-cyan-300/45 hover:bg-cyan-400/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/50 disabled:cursor-not-allowed disabled:opacity-45"
            >
              <Icon icon="bx:conversation" className="h-4 w-4" aria-hidden="true" />
              Generate Final Summary
            </button>
          </div>
        </div>
      </footer>
      {/* Stage 2: Interaction Summary modal */}
      {summaryModalOpen && (
        <div className="fixed inset-0 z-[20000] flex items-center justify-center">
          <button type="button" aria-label="Close" onClick={() => setSummaryModalOpen(false)} className="absolute inset-0 cursor-default bg-[#020306]/85 backdrop-blur-sm" />
          <div role="dialog" aria-modal="true" aria-label="Interaction Summary" className="relative flex w-full max-w-2xl flex-col rounded-2xl border border-white/10 bg-[#0e111a] shadow-[0_24px_80px_rgba(0,0,0,0.5)] outline-none">
            <div className="flex items-center justify-between border-b border-white/[0.08] px-5 py-4">
              <div className="flex items-center gap-2.5">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#5260FF]/15 text-[#8a93ff]">
                  <Icon icon="bx:conversation" className="h-4.5 w-4.5" />
                </span>
                <div>
                  <h2 className="text-[14px] font-semibold text-white">Interaction Summary</h2>
                  <p className="text-[11px] text-white/45">Your reflection after interacting with this lead</p>
                </div>
              </div>
              <button type="button" onClick={() => setSummaryModalOpen(false)} aria-label="Close" className="flex h-8 w-8 items-center justify-center rounded-lg text-white/40 hover:bg-white/[0.08] hover:text-white">
                <Icon icon="bx:x" className="h-5 w-5" />
              </button>
            </div>
            <div className="px-5 py-4">
              <label>
                <span className="sr-only">Summary text</span>
                <textarea
                  value={summaryDraft}
                  onChange={(event) => setSummaryDraft(event.target.value)}
                  rows={10}
                  autoFocus
                  placeholder="Describe your interaction with this lead — what worked, what you learned, next steps…"
                  className="w-full resize-y rounded-xl border border-white/10 bg-black/25 px-3.5 py-3 text-[13px] leading-6 text-white outline-none placeholder:text-white/22 focus-visible:border-cyan-300/50"
                />
              </label>
            </div>
            <div className="flex items-center justify-between gap-3 border-t border-white/[0.08] px-5 py-3.5">
              <div />
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => { setSummaryModalOpen(false); setSummaryDraft(''); }} className="h-9 rounded-lg px-3 text-[12px] text-white/45 hover:bg-white/[0.06] hover:text-white/80">Cancel</button>
                <button
                  type="button"
                  onClick={handleSaveSummary}
                  disabled={!summaryDraft.trim() || summarySaving}
                  className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-white px-3.5 text-[12px] font-semibold text-[#0a0b0e] transition-colors hover:bg-white/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/60 disabled:cursor-not-allowed disabled:bg-white/10 disabled:text-white/35"
                >
                  <Icon icon={summarySaving ? 'bx:loader-alt' : 'bx:check'} className={`h-4 w-4 ${summarySaving ? 'animate-spin' : ''}`} />
                  {summarySaving ? 'Saving…' : 'Save Summary'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Sales Intelligence activation request — opened when the Start
          Outreach button is locked (mod_sales not active on the team). */}
      <SupportRequestModal
        isOpen={showUnlockOutreachModal}
        onClose={() => setShowUnlockOutreachModal(false)}
        userEmail={effectiveUserEmail}
        userName={currentUser.name}
        impersonatedEmail={impersonatedEmail || undefined}
        mode="unlock_outreach"
      />
    </div>
  );
};



/** Convenience composition used by list and kanban entry points. */
export const ISRWorkspaceDrawer: React.FC<ISRWorkspaceDrawerProps> = ({
  open,
  onClose,
  confirmDiscard,
  onPreviousLead,
  onNextLead,
  hasPreviousLead = false,
  hasNextLead = false,
  leadPosition,
  totalLeads,
  ...workspaceProps
}: ISRWorkspaceDrawerProps) => {
  const [dirty, setDirty] = useState(false);

  // Quick actions mirror the workspace's contact derivation so the identity
  // panel always has the person's current best number/email/WhatsApp.
  const contact = workspaceProps.lead
    ? deriveContact(workspaceProps.lead)
    : null;
  const quickPhone = contact ? normalizePhone(contact.person.mobile_id || contact.person.phone_id) : '';
  const quickWhatsapp = contact ? normalizePhone(contact.person.whatsapp_id || contact.person.mobile_id) : '';
  const quickEmail = contact ? valueOf(contact.person.email_id) : '';

  return (
    <ISRWorkspaceDrawerShell
      open={open}
      onClose={onClose}
      onPreviousLead={onPreviousLead}
      onNextLead={onNextLead}
      hasPreviousLead={hasPreviousLead}
      hasNextLead={hasNextLead}
      leadPosition={leadPosition}
      totalLeads={totalLeads}
      hasUnsavedChanges={dirty}
      confirmDiscard={confirmDiscard}
      contextPanel={(
        <LeadIdentityPanel
          lead={workspaceProps.lead}
          campaign={workspaceProps.campaign}
          quickPhone={quickPhone}
          quickEmail={quickEmail}
          quickWhatsapp={quickWhatsapp}
          hydrating={workspaceProps.hydrating}
          onOpenFullLead={workspaceProps.onOpenFullLead}
        />
      )}
      contextFooter={
        <ContextBottomStrip lead={workspaceProps.lead} hydrating={workspaceProps.hydrating} />
      }
    >
      {({ requestClose }) => (
        <ISRWorkspace
          {...workspaceProps}
          onClose={requestClose}
          onDirtyChange={setDirty}
          expanded={false}
          showCloseButton={false}
          onPreviousLead={onPreviousLead}
          onNextLead={onNextLead}
          hasPreviousLead={hasPreviousLead}
          hasNextLead={hasNextLead}
          leadPosition={leadPosition}
          totalLeads={totalLeads}
        />
      )}
    </ISRWorkspaceDrawerShell>
  );
};

export default ISRWorkspace;
